import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from 'react';
import {
    Building2, Users, Target, BarChart3, ChevronRight, Search, Plus,
    Filter, MoreHorizontal, Download, LayoutDashboard, Calendar,
    MessageSquare, Bell, Settings, LogOut, Shield, ChevronDown,
    CheckCircle2, AlertCircle, Clock, Trash2, Edit, Save, X, Menu,
    Zap, BookOpen, Sparkles, Send, MoreVertical, Edit3, Database,
    FileText, TrendingUp, Calculator, CheckSquare, ChevronLeft, Check, LayoutGrid, RotateCcw,
    Briefcase, FolderOpen, ArrowRight, ArrowLeft, PieChart as PieChartIcon,
    LogOut as LogOutIcon, MoreHorizontal as MoreHorizontalIcon, ShieldCheck,
    Copy, Activity, GripVertical, History, HelpCircle, Eye, Info, TrendingDown, User, Lightbulb,
    UserCheck, Layers, Trello, Book, Layout as LayoutIcon, PlusSquare, RefreshCw, AlertTriangle, File, Bot, Crown, Award, Globe, ShieldAlert, ShieldX
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    AreaChart,
    Area,
    LineChart,
    Line,
    ComposedChart
} from 'recharts';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { useLocation, useNavigate, Routes, Route, Link, useSearchParams, Navigate } from 'react-router-dom';
import * as db from '../../lib/supabase-service';
import { recordAuditEvent } from '../../lib/audit-logger';
import { canEditAuthorizedModule as canEditModule } from '../../lib/module-authorization';
import { askAI } from '../../lib/ai-service';
import { cn, getColWidthOption, cycleColWidth, GenericTableEditor, SWOTEditor } from '../SharedComponents';
import { 
    calculateProratedCompliance, calculateVPPerformance, SystemSettings, SettingsContext, useRole,
    ALL_MODULES, getUserModules, canAccessModule, getModulePermission, sanitizeAccessibleModules, parseModulePermission, getModulesForRole,
    countActiveYTD, getIndicatorMetrics, parseNumberSafe, getComplianceVisualState,
    getElapsedCompletedMonths, MONTHS_LABELS, APP_VERSION as CENTRAL_VERSION
} from '../../lib/shared-logic';
// Aislamiento: planning-logic removido por regla forense v18.10.10
import { jsPDF } from 'jspdf';
import NationalDashboardPanel from '../NationalDashboardPanel';
import { resolveCanonicalPlanProgressState } from '../../lib/instance-plan-progress-state';
import { buildDashboardObligations, buildDashboardCaptureCounter, classifyDashboardPeriod, evaluateDashboardObligation, hasDashboardCapture, DASHBOARD_CYCLE_YEAR, storedProtocolType, storedProtocolActions, hasProtocolContent, protocolSaveType, protocolTypes } from '../../lib/instance-dashboard-obligations';
import InstanceExecutiveAlertsPanel from './InstanceExecutiveAlertsPanel';
import PriorPeriodCaptureNotice from './PriorPeriodCaptureNotice';
import { ExecutiveDashboardSummary, DashboardActionBar, CompactKpiCard } from './ExecutiveDashboardPresentation';
import { buildPriorPeriodCaptureSummary, parseCaptureOperationalMonth } from '../../lib/prior-period-capture';
import { getCaptureRegimeActivation } from '../../lib/prior-period-capture-config';
import { getOperationalStartPeriodConfig, parseSelectedMonthIndex } from '../../lib/operational-control/operational-start-period-config';
import { detectInstanceOperationalActivation, resolveOperationalStartPeriod } from '../../lib/operational-control/operational-start-period-engine';

const normalize = (s) => {
    if (s === null || s === undefined) return "";
    return String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();
};

function canonicalInstanceType(type: any): 'COMISION' | 'COMITE' | 'CONSEJERIA' | string {
    const t = normalize(type || '').replace(/[\s_]/g, '');
    if (t === 'COMISION' || t === 'COMISIONESTRATEGICA' || t === 'COMISIONES') return 'COMISION';
    if (t === 'COMITE' || t === 'COMITES') return 'COMITE';
    if (t === 'CONSEJERIA' || t === 'CONSEJERIAS') return 'CONSEJERIA';
    return t;
}

function normalizeOwnerType(type: any): 'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA' | string {
    const t = normalize(type || '').replace(/[\s_]/g, '');
    if (t === 'TODOS') return 'TODOS';
    if (t === 'COMISIONTYPE') return 'COMISION';
    return canonicalInstanceType(type);
}

function getOwnerTypeLabel(type: any): string {
    const t = normalizeOwnerType(type);
    if (t === 'COMISION') return 'Comisión';
    if (t === 'COMITE') return 'Comité';
    if (t === 'CONSEJERIA') return 'Consejería';
    if (t === 'TODOS') return 'Ver Todos';
    return String(type || '');
}

const getHierarchyInstances = (hierarchy: any): any[] => {
    if (!hierarchy) return [];

    const topLevel = [
        ...(hierarchy.commissions || []),
        ...(hierarchy.committees || []),
        ...(hierarchy.councils || []),
        ...(hierarchy.independents || [])
    ];

    const nested = (hierarchy.vicepresidencies || []).flatMap((vp: any) => [
        ...(vp?.comisiones || []),
        ...(vp?.comites || []),
        ...(vp?.consejerias || [])
    ]);

    const seen = new Set<string>();
    return [...topLevel, ...nested].filter((inst: any) => {
        const id = inst?.id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};

const getVPId = (u) => u?.vicepresidency_id || u?.instance_id || null;

export const MODULE_VERSION = CENTRAL_VERSION;
const APP_VERSION = "v18.27.71-MODULE-EDITOR-PERMISSION-PARITY";
const APP_BUILD = "2026.07.17.RC1";

const COMISION_TYPE = 'COMISION_Estratégica';
const INSTANCE_TYPE_LABELS: Record<string, string> = { 
    [COMISION_TYPE]: "Comisiones", 
    "COMITE": "Comités", 
    "CONSEJERIA": "Consejerías" 
};
const INSTANCE_TYPES = [COMISION_TYPE, 'COMITE', 'CONSEJERIA'];

const MONTHS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const MONTHS_LONG = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const REPORTS_TABLE = 'cpx_monthly_reports';
const monthToNum = (m: string) => MONTHS.indexOf(m) + 1;
const sanitizeFileSegment = (value: any, fallback: string) => {
    const raw = String(value || fallback)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "_");
    return raw || fallback;
};

// Legacy html2pdf and print window routines removed

const getCurrentPeriod = (frec: string) => {
    const now = new Date();
    if (frec === 'Mensual') {
        return MONTHS[now.getMonth()];
    } else {
        const start = new Date(now.getFullYear(), 0, 1);
        const diff = now.getTime() - start.getTime();
        const oneDay = 1000 * 60 * 60 * 24;
        const day = Math.floor(diff / oneDay);
        return `SEM ${Math.ceil(day / 7)}`;
    }
};

const getVpAnalysisMonthIdx = (selectedMonth: string): number => {
    const targetIdx = MONTHS.indexOf(selectedMonth);
    if (targetIdx < 0) return -1;
    return Math.max(0, getElapsedCompletedMonths(targetIdx));
};

const getVpAnalysisMonthLabel = (selectedMonth: string): string => {
    const analysisIdx = getVpAnalysisMonthIdx(selectedMonth);
    return analysisIdx >= 0 ? MONTHS[analysisIdx] : selectedMonth;
};

const isVpPlanEvaluable = (inst: any): boolean => {
    return parseNumberSafe(inst?.planGoal, 0) > 0;
};

const isVpKpiEvaluable = (inst: any): boolean => {
    return parseNumberSafe(inst?.kpiMeta, 0) > 0 && !!(inst?.kpiEligible || inst?.hasActiveKpis);
};

const buildVpPerformanceSettings = (instances: any[], settings: any) => {
    const activeInstances = (instances || []).filter((inst: any) => {
        const status = normalize(inst?.status || inst?.estado || '').replace(/[\s_]/g, '');
        return inst?.active !== false
            && inst?.is_active !== false
            && status !== 'INACTIVO'
            && status !== 'INACTIVE'
            && status !== 'ARCHIVED'
            && status !== 'ELIMINADO';
    });

    const counts = activeInstances.reduce((acc: Record<string, number>, inst: any) => {
        const type = canonicalInstanceType(inst?.type || inst?.commission_type);
        if (type === 'COMISION' || type === 'COMITE' || type === 'CONSEJERIA') {
            acc[type] = (acc[type] || 0) + 1;
        }
        return acc;
    }, { COMISION: 0, COMITE: 0, CONSEJERIA: 0 });

    return {
        ...settings,
        weight_comisiones: parseNumberSafe(settings?.weight_comisiones, 50) * Math.max(counts.COMISION, 1),
        weight_comites: parseNumberSafe(settings?.weight_comites, 30) * Math.max(counts.COMITE, 1),
        weight_consejerias: parseNumberSafe(settings?.weight_consejerias, 20) * Math.max(counts.CONSEJERIA, 1)
    };
};

const getVpInstanceWeightLabel = (instances: any[], type: any, settings: any): string => {
    const activeInstances = (instances || []).filter((inst: any) => {
        const status = normalize(inst?.status || inst?.estado || '').replace(/[\s_]/g, '');
        return inst?.active !== false
            && inst?.is_active !== false
            && status !== 'INACTIVO'
            && status !== 'INACTIVE'
            && status !== 'ARCHIVED'
            && status !== 'ELIMINADO';
    });

    const totalWeight = activeInstances.reduce((sum: number, inst: any) => {
        const instType = canonicalInstanceType(inst?.type || inst?.commission_type);
        if (instType === 'COMISION') return sum + parseNumberSafe(settings?.weight_comisiones, 50);
        if (instType === 'COMITE') return sum + parseNumberSafe(settings?.weight_comites, 30);
        if (instType === 'CONSEJERIA') return sum + parseNumberSafe(settings?.weight_consejerias, 20);
        return sum;
    }, 0);

    const instanceWeight = canonicalInstanceType(type) === 'COMISION'
        ? parseNumberSafe(settings?.weight_comisiones, 50)
        : canonicalInstanceType(type) === 'COMITE'
            ? parseNumberSafe(settings?.weight_comites, 30)
            : parseNumberSafe(settings?.weight_consejerias, 20);

    return totalWeight > 0 ? `${((instanceWeight / totalWeight) * 100).toFixed(1)}%` : '0.0%';
};

const formatVpPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
    return `${Math.round(value)}%`;
};

const ensureHistoryConsistency = (ind: any) => {
    const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const currentHistory = Array.isArray(ind.history) ? ind.history : [];
    
    // REGLA FORENSE v18.9.124: Garantizar siempre 12 slots para evitar desincronización de índices
    return months.map((m, idx) => {
        const existing = currentHistory.find((h: any) => h && h.period?.toUpperCase() === m);
        if (existing) return existing;
        
        // Si no existe, crear slot vacío pero consistente
        return { period: m, meta: 0, real: 0, note: '', elements: [], activities: [] };
    });
};

const calculateStats = (ind: any, selectedMonth?: string, currentMonth?: string) => {
    const history = ind.history || [];
    const targetMonth = selectedMonth || currentMonth || MONTHS[new Date().getMonth()];
    const targetIdx = MONTHS.indexOf(targetMonth);
    
    const metrics = getIndicatorMetrics(ind, getElapsedCompletedMonths(targetIdx), false);
    
    return {
        totalMeta: metrics.meta,
        totalReal: metrics.real,
        pct: metrics.pct,
        currentMeta: history[targetIdx]?.meta || 0,
        currentReal: history[targetIdx]?.real || 0
    };
};

export 
const STATUS_LABELS = { EN_ELABORACION: "En Elaboración", REVISADO: "Revisado", APROBADO: "Aprobado", SIN_PLAN: "Sin Plan" };
const hasChecklistProgress = (items: any) => {
    if (!Array.isArray(items)) return false;
    return items.some((item: any) =>
        item?.completed === true ||
        Number(item?.realizado || 0) > 0 ||
        Number(item?.real || 0) > 0 ||
        Number(item?.value || 0) > 0
    );
};

const hasRealCaptureEvidence = (entry: any) => {
    if (!entry) return false;
    if (hasDashboardCapture(entry)) return true;
    if (Number(entry.real || 0) > 0) return true;
    if (String(entry.note || '').trim()) return true;
    if (hasChecklistProgress(entry.checklist)) return true;
    if (hasChecklistProgress(entry.elements)) return true;
    if (hasChecklistProgress(entry.activities)) return true;
    return false;
};

const MONTH_INDEX_BY_TOKEN: Record<string, number> = {
    ENE: 0, ENERO: 0,
    FEB: 1, FEBRERO: 1,
    MAR: 2, MARZO: 2,
    ABR: 3, ABRIL: 3,
    MAY: 4, MAYO: 4,
    JUN: 5, JUNIO: 5,
    JUL: 6, JULIO: 6,
    AGO: 7, AGOSTO: 7,
    SEP: 8, SEPT: 8, SEPTIEMBRE: 8,
    OCT: 9, OCTUBRE: 9,
    NOV: 10, NOVIEMBRE: 10,
    DIC: 11, DICIEMBRE: 11
};

const getMonthIndexFromValue = (value: any): number | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
        if (value >= 1 && value <= 12) return value - 1;
        if (value >= 0 && value <= 11) return value;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    const normalized = normalize(raw).replace(/[.,]/g, ' ');
    const tokens = normalized.split(/\s+/).filter(Boolean);
    for (const token of tokens) {
        if (MONTH_INDEX_BY_TOKEN[token] !== undefined) {
            return MONTH_INDEX_BY_TOKEN[token];
        }
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.getMonth();
    }

    const compactMonth = raw.match(/\b(0?[1-9]|1[0-2])\b/);
    if (compactMonth) {
        return Number(compactMonth[1]) - 1;
    }

    return null;
};

const isTruthyPlanMarker = (value: any) => {
    if (value === true) return true;
    const normalized = normalize(value);
    return ['1', 'TRUE', 'SI', 'SÍ', 'YES', 'X', 'OK'].includes(normalized);
};

const sectionHasClosedPlanObligation = (node: any, lastClosedMonthIdx: number): boolean => {
    if (lastClosedMonthIdx < 0 || node === null || node === undefined) return false;

    if (Array.isArray(node)) {
        return node.some((item) => sectionHasClosedPlanObligation(item, lastClosedMonthIdx));
    }

    if (typeof node !== 'object') return false;

    return Object.entries(node).some(([rawKey, rawValue]) => {
        const key = normalize(rawKey).replace(/\s+/g, '');
        const quarterMatch = key.match(/^[QT]([1-4])$/);
        if (quarterMatch && isTruthyPlanMarker(rawValue)) {
            const quarterEndMonthIdx = Number(quarterMatch[1]) * 3 - 1;
            return quarterEndMonthIdx <= lastClosedMonthIdx;
        }

        if (typeof rawValue === 'string' || typeof rawValue === 'number') {
            const keySuggestsSchedule = key.includes('MES') || key.includes('FECHA') || key.includes('VIGENCIA');
            if (keySuggestsSchedule) {
                const monthIdx = getMonthIndexFromValue(rawValue);
                if (monthIdx !== null && monthIdx <= lastClosedMonthIdx) {
                    return true;
                }
            }
        }

        if (typeof rawValue === 'object') {
            return sectionHasClosedPlanObligation(rawValue, lastClosedMonthIdx);
        }

        return false;
    });
};

const hasExplicitClosedPlanObligation = (sections: any[], lastClosedMonthIdx: number) => {
    if (!Array.isArray(sections) || lastClosedMonthIdx < 0) return false;
    return sections.some((section: any) => {
        const goal = parseNumberSafe(section?.goal ?? section?.meta_anual, 0);
        return goal > 0 && sectionHasClosedPlanObligation(section?.content, lastClosedMonthIdx);
    });
};

const buildClosedKpiReason = (indicators: any[], lastClosedMonthIdx: number) => {
    if (!Array.isArray(indicators) || lastClosedMonthIdx < 0) return null;

    let criticalIndicator: any = null;

    indicators.forEach((indicator: any) => {
        const metrics = getIndicatorMetrics(indicator, lastClosedMonthIdx, false);
        if (metrics.meta > 0 && metrics.real === 0) {
            if (!criticalIndicator || metrics.meta > criticalIndicator.meta) {
                criticalIndicator = {
                    name: indicator?.nombre || indicator?.name || 'Indicador',
                    meta: metrics.meta,
                    real: metrics.real
                };
            }
        }
    });

    if (!criticalIndicator) return null;

    return `${criticalIndicator.name}: meta acumulada ${criticalIndicator.meta}, resultado ${criticalIndicator.real}, exigible al cierre de ${MONTHS_LONG[lastClosedMonthIdx]}.`;
};

const TrackingModule = ({ 
    user, 
    selectedMonth, 
    setSelectedMonth,
    selectedInstancia,
    setSelectedInstancia,
    selectedVPId,
    setSelectedVPId
}: { 
    user: any, 
    selectedMonth: string, 
    setSelectedMonth: (m: string) => void,
    selectedInstancia: string | null,
    setSelectedInstancia: (id: string | null) => void,
    selectedVPId: string | null,
    setSelectedVPId: (id: string | null) => void
}) => {
    const settings = useContext(SettingsContext);
    const isSelectedPeriodOpen = selectedMonth === getCurrentPeriod('Mensual') && new Date().getFullYear() === 2026;
    const navigate = useNavigate();
    const { isVP, isND, isPresident, isSA, isInstance, isGlobal } = useRole(user);
    const isSAOrAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR' || user?.role === 'DIRECTOR_NACIONAL';
    const isVPScoped = !isSAOrAdmin && (user?.org_scope === 'VP' || !!user?.vicepresidency_id);
    const isInstanceScoped = !isSAOrAdmin && (user?.org_scope === 'INSTANCE' || !!user?.commission_id || !!user?.instance_id);

    const [indicadores, setindicadores] = useState<any[]>([]);
    const pendingDeletedIndicatorsRef = useRef<any[]>([]);
    const [hierarchy, setHierarchy] = useState<any>(null);
    const [livePlanTotals, setLivePlanTotals] = useState<any[]>([]);

    // Sincronización de Contexto inicial v18.21.15
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const instId = params.get('instId') || params.get('instanciaId');
        const vpId = params.get('vpId') || params.get('vpFilterId');
        const userInstId = user?.instance_id || user?.commission_id;

        // Solo asignamos en el montaje inicial si no hay un estado ya seleccionado
        if (!selectedInstancia) {
            if (instId) {
                console.log("[SYNC] URL Mount Match:", instId);
                setSelectedInstancia(instId);
            } else if (userInstId && user?.org_scope === 'INSTANCE') {
                console.log("[SYNC] User Scope Mount Match:", userInstId);
                setSelectedInstancia(userInstId);
            }
        }

        if (!selectedVPId && vpId) {
            setSelectedVPId(vpId);
        }
    }, [user]); // Solo corre en la carga inicial del usuario o cambios del perfil del usuario

    // RBAC VISUAL ENFORCEMENT EFFECT
    useEffect(() => {
        if (!isSAOrAdmin) {
            if (isVPScoped && user?.vicepresidency_id) {
                if (selectedVPId !== user.vicepresidency_id) {
                    setSelectedVPId(user.vicepresidency_id);
                }
            } else if (isInstanceScoped) {
                const targetInst = user?.commission_id || user?.instance_id;
                if (targetInst && selectedInstancia !== targetInst) {
                    setSelectedInstancia(targetInst);
                }
                if (selectedVPId !== null) {
                    setSelectedVPId(null);
                }
            }
        }
    }, [user, isSAOrAdmin, isVPScoped, isInstanceScoped, selectedVPId, setSelectedVPId, selectedInstancia, setSelectedInstancia]);

    const [activeTab, setActiveTab] = useState<'TABLERO' | 'indicadores' | 'CAPTURA' | 'PONDERACIONES' | 'INFORME' | 'MINUTA' | 'TRIMESTRAL' | 'VP_DASHBOARD'>('TABLERO');
    const [loading, setLoading] = useState(true);
    const [scopedReadError, setScopedReadError] = useState<string | null>(null);
    const [activeIndicatorId, setActiveIndicatorId] = useState<string | null>(null);
    const [activeIndicatorPeriod, setActiveIndicatorPeriod] = useState<string | null>(null);
    const [instancesDashboardData, setInstancesDashboardData] = useState<any[]>([]);
    const [checklistOpen, setChecklistOpen] = useState<{ indId: string; periodIdx: number } | null>(null);
    const [showUnconfiguredList, setShowUnconfiguredList] = useState(false);
    const [showNoObligationList, setShowNoObligationList] = useState(false);

    const [reportData, setReportData] = useState<any>(null);
    const [instancePlanSections, setInstancePlanSections] = useState<any[]>([]);
    const [instancePlanTotals, setInstancePlanTotals] = useState<any>({ total_goal: 0, total_progress: 0, planPct: 0 });

    // [TRACKING_INSTANCE_RENDER_CHECK] Diagnóstico de renderizado (v18.9.115)
    // Movido aquí para cumplir con las reglas de Hooks de React
    useEffect(() => {
        // console.log('[TRACKING_INSTANCE_RENDER_CHECK] State:', {
        //     renderMode: selectedInstancia ? 'INSTANCIA' : 'VP',
        //     selectedVPId,
        //     selectedInstancia,
        //     activeTab,
        //     hasHierarchy: !!hierarchy,
        //     indicatorsCount: indicadores.length,
        //     currentInstanceFound: !!currentInstance
        // });
    }, [selectedInstancia, selectedVPId, activeTab, !!hierarchy, indicadores.length]);

    const [extraTables, setExtraTables] = useState<any[]>([]);
    const [extraColumns, setExtraColumns] = useState<any[]>([
        { key: 'col1', label: 'Descripción' },
        { key: 'col2', label: 'Dato / Valor' },
        { key: 'col3', label: 'Estado / Avance' }
    ]);
    const [observations, setObservations] = useState("");
    const [execSummary, setExecSummary] = useState("");
    const [reportLoading, setReportLoading] = useState(false);
    const [isEditingReport, setIsEditingReport] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const currentReportFormat = activeTab === 'MINUTA' ? 'Minuta' : activeTab === 'TRIMESTRAL' ? 'Trimestral' : 'Mensual';

    const refreshIndicators = useCallback(async (exactInstanceOnly = false) => {
        if (!selectedInstancia) return;
        setLoading(true);
        setScopedReadError(null);
        try {
            let targetIds = [selectedInstancia];
            if (!exactInstanceOnly && hierarchy) {
                const allInstances = getHierarchyInstances(hierarchy);
                const current = allInstances.find((c: any) => c.id === selectedInstancia);
                if (current && current.name) {
                    const cName = normalize(current.name);
                    targetIds = allInstances
                        .filter((c: any) => normalize(c.name) === cName)
                        .map((c: any) => c.id);
                }
            }

            const data = await db.getIndicators(targetIds);
            const normalized = (data || []).map((ind: any) => ({
                ...ind,
                history: ensureHistoryConsistency(ind)
            }));
            setindicadores(normalized);
        } catch (err: unknown) {
            console.error(err);
            setScopedReadError(db.getScopedReadErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [selectedInstancia, hierarchy]);

    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA'>('TODOS');
    const [replicaSearch, setReplicaSearch] = useState('');
    const [replicaTypeFilter, setReplicaTypeFilter] = useState<'TODOS' | 'COMISION' | 'COMITE' | 'CONSEJERIA'>('TODOS');
    const [activeType, setActiveType] = useState<'COMISION' | 'COMITE' | 'CONSEJERIA'>('COMISION');
    const [showReplicaModal, setShowReplicaModal] = useState(false);
    const [replicaTargets, setReplicaTargets] = useState<string[]>([]);
    const [indicatorSyncMode, setIndicatorSyncMode] = useState<db.IndicatorSyncSafeMode>('CATALOG_AND_ORDER_KEEP_DESTINATION_DATA');
    const [isBulkCapture, setIsBulkCapture] = useState(false);
    const [draggedIndicatorId, setDraggedIndicatorId] = useState<string | null>(null);
    const [hasPendingOrderChanges, setHasPendingOrderChanges] = useState(false);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const isSavingIndicatorsRef = useRef(false);

    const setIndicatorOrderLocally = (newIndicators: any[]) => {
        const ordered = newIndicators.map((ind: any, index: number) => ({
            ...ind,
            orden: index
        }));
        setindicadores(ordered);
        setHasPendingOrderChanges(true);
    };

    const allInstances = getHierarchyInstances(hierarchy);
    const filteredReplicaComms = allInstances.filter((c: any) => {
        const s = replicaSearch.toLowerCase();
        const query = s.trim();
        if (!query && replicaTypeFilter === 'TODOS') return true;
        const matchesSearch = !query || c.name.toLowerCase().includes(query) || c.type?.toLowerCase().includes(query);
        const matchesType = normalizeOwnerType(replicaTypeFilter) === 'TODOS' || normalizeOwnerType(c.type) === normalizeOwnerType(replicaTypeFilter);
        return matchesSearch && matchesType;
    });

    const isInstanceScopeUser = user?.org_scope === 'INSTANCE' || (user?.role === 'COMMISSION_PRESIDENT' && user?.commission_id);
    const [showEcosistema, setShowEcosistema] = useState(!(user?.role === 'COMMISSION_PRESIDENT' || !!user?.commission_id));
    const [isVPsExpanded, setIsVPsExpanded] = useState(true);
    const types = INSTANCE_TYPES;

    const getCurrentIdx = (ind: any) => {
        const label = getCurrentPeriod(ind.frec);
        const idx = ind.history.findIndex((h: any) => h.period === label);
        return idx !== -1 ? idx : ind.history.length - 1;
    };

    useEffect(() => {
        if (selectedInstancia) {
            setLoading(true);
            // v18.9.113: Limpieza selectiva. NO tocamos hierarchy ni dashboardData global.
            setindicadores([]);
            setReportData(null);

            // UX DEFENSIVO HOTFIX: Limpiar estados de edición y cerrar modales abiertos
            setChecklistOpen(null);
            setIsBulkCapture(false);
            setActiveIndicatorId(null);
            setActiveIndicatorPeriod(null);

            refreshIndicators();
        }
    }, [selectedInstancia]);

    const activeIndicator = indicadores.find(i => i.id === activeIndicatorId);

    useEffect(() => {
        db.getDashboardByCommission(user).then(data => {
            console.log("[AUDIT] Dashboard: Loaded instance data", data?.length || 0);
            setInstancesDashboardData(data || []);
        });
    }, [user, selectedMonth]); // Sincronizado con el mes Seleccionado

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setScopedReadError(null);
                const h = await db.getHierarchy(user);
                setHierarchy(h);
                
                // Cargar totales de Plan de Trabajo en Vivo usando la misma jerarquía normalizada
                const hierarchyInstances = getHierarchyInstances(h);
                if (hierarchyInstances.length > 0) {
                    const ids = hierarchyInstances.map((c: any) => c.id);
                    const planTotals = await db.getWorkPlanLiveTotalsByCommissionIds(ids);
                    setLivePlanTotals(planTotals);
                } else {
                    setLivePlanTotals([]);
                }
            } catch (err: unknown) {
                console.error("[TRACKING_INIT_ERROR]", err);
                setScopedReadError(db.getScopedReadErrorMessage(err));
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [user]);

    useEffect(() => {
        if (selectedInstancia) {
            refreshIndicators();
        }
    }, [selectedInstancia, refreshIndicators]);

    useEffect(() => {

        if (selectedInstancia && (activeTab === 'INFORME' || activeTab === 'MINUTA' || activeTab === 'TRIMESTRAL')) {
            const fetchReport = async () => {
                setReportLoading(true);
                try {
                    const monthNum = monthToNum(selectedMonth);
                    const data = await db.getTrackingReportScoped({
                        instanceId: selectedInstancia,
                        month: monthNum,
                        year: 2026,
                        format: currentReportFormat
                    });

                    if (data) {
                        setReportData(data);
                        setObservations(data.observations || "");
                        setExecSummary(data.summary || "");
                        setExtraTables(data.extra_tables || []);
                        setExtraColumns(data.extra_columns || [
                            { key: 'col1', label: 'Descripción' },
                            { key: 'col2', label: 'Dato / Valor' },
                            { key: 'col3', label: 'Estado / Avance' }
                        ]);
                    } else {
                        setReportData({
                            commission_id: selectedInstancia,
                            month: monthNum,
                            year: 2026,
                            format: currentReportFormat,
                            status: 'BORRADOR'
                        });
                        setObservations("");
                        setExecSummary("");
                        setExtraTables([]);
                    }
                } catch (err) {
                    console.error("Error fetching report", err);
                } finally {
                    setReportLoading(false);
                }
            };
            fetchReport();
        }
    }, [selectedInstancia, activeTab, selectedMonth, currentReportFormat]);

    useEffect(() => {
        const loadInstancePlanContext = async () => {
            if (!selectedInstancia) {
                setInstancePlanSections([]);
                setInstancePlanTotals({ total_goal: 0, total_progress: 0, planPct: 0 });
                return;
            }

            try {
                const [sections, totals] = await Promise.all([
                    db.getInstancePlan(selectedInstancia, 2026),
                    db.getWorkPlanLiveTotalsByCommissionIds([selectedInstancia])
                ]);
                setInstancePlanSections(Array.isArray(sections) ? sections : []);
                setInstancePlanTotals(totals?.[0] || { total_goal: 0, total_progress: 0, planPct: 0 });
            } catch (err: unknown) {
                console.error('[TRACKING_PLAN_CONTEXT_ERROR]', err);
                setScopedReadError(db.getScopedReadErrorMessage(err));
            }
        };

        loadInstancePlanContext();
    }, [selectedInstancia]);

    const handleSaveReport = async (updates: any) => {
        if (!selectedInstancia) return;
        try {
            const monthNum = monthToNum(selectedMonth);
            const payload = {
                ...reportData,
                ...updates,
                commission_id: selectedInstancia,
                month: monthNum,
                year: 2026,
                format: currentReportFormat,
                updated_at: new Date().toISOString()
            };

            await db.saveTrackingReportScoped(selectedInstancia, payload);
            alert('Reporte guardado exitosamente');
            setIsEditingReport(false);
        } catch (err: any) {
            alert('Error al guardar reporte: ' + err.message);
        }
    };


    const handleAIAnalysis = async () => {
        if (!indicadores || indicadores.length === 0) return;
        setReportLoading(true);
        try {
            const compactData = indicadores
                .map((i: any) => {
                    const stats = calculateStats(i, selectedMonth, selectedMonth);
                    return `- ${i.nombre}: Real/Meta Mes: ${stats.totalReal}/${stats.totalMeta} | Cump: ${stats.pct}%`;
                })
                .join('\n');

            const reportPrompt = `Eres el Analista de Inteligencia Articulada de COPARMEX. 
Analiza el desempeño de "${currentInstance?.name || 'la instancia'}" para el periodo de "${selectedMonth} 2026".

FORMATO OBLIGATORIO (Usa Markdown):
        { id: "resumen", label: "📝 RESUMEN EJECUTIVO: [Breve síntesis del estado general]" },
        { id: "logros", label: "🏆 LOGROS DESTACADOS: [indicadores con buen cumplimiento]" },
        { id: "riesgos", label: "⚠️ ÁREAS DE RIESGO: [indicadores con desviaciones]" },
        { id: "acciones", label: "📌 ACCIONES PRIORITARIAS: [2-3 recomendaciones clave]" }

Datos de cumplimiento (Actual/Meta):
${compactData}`;

            const text = await askAI(reportPrompt, "");

            // Intentamos separar el resumen del cuerpo si el formato es correcto
            const parts = text.split('\n\n');
            if (parts.length > 1) {
                setExecSummary(parts[0]);
                setObservations(parts.slice(1).join('\n\n'));
            } else {
                setExecSummary(text.slice(0, 500));
                setObservations(text);
            }

            alert('Análisis de IA generado exitosamente');
        } catch (err: any) {
            console.error('[AI_REPORT_ERROR]', err);
            alert("Error al generar análisis de IA: " + err.message);
        } finally {
            setReportLoading(false);
        }
    };

    // v18.27.5-TRACKING-REPORT-PDF-SEMAPHORE-COMPLIANCE-FIX
    const handleExportReportPDFSafe = async () => {
        if (isExportingPDF || !currentInstance) return;

        setIsExportingPDF(true);

        try {
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                format: 'a4',
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 48;
            let y = 56;

            const safeInstanceName = currentInstance?.name || 'Instancia';
            const periodLabel = `${selectedMonth} 2026`;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.setTextColor(0, 166, 118);
            pdf.text('COPARMEX 2026 / GOBERNANZA INSTITUCIONAL', margin, y);

            y += 34;

            pdf.setFontSize(24);
            pdf.setTextColor(2, 16, 43);
            pdf.text('INFORME ESTRATÉGICO MENSUAL', margin, y);

            y += 24;

            pdf.setFontSize(14);
            pdf.setTextColor(0, 145, 100);
            pdf.text(safeInstanceName.toUpperCase(), margin, y);

            y += 18;

            pdf.setFontSize(10);
            pdf.setTextColor(90, 103, 125);
            pdf.text(`PERIODO REPORTADO: ${periodLabel}`, margin, y);

            y += 28;

            pdf.setDrawColor(0, 166, 118);
            pdf.setLineWidth(2);
            pdf.line(margin, y, pageWidth - margin, y);

            y += 36;
            
            // Función auxiliar
            const ensureSpace = (required: number) => {
                if (y + required > pageHeight - margin) {
                    pdf.addPage();
                    y = margin;
                }
            };

            const safeText = (value: unknown, fallback = 'Sin información capturada') => {
                const text = typeof value === 'string' ? value.trim() : '';
                return text.length > 0 ? text : fallback;
            };

            const getCompliancePdfColor = (value: unknown): [number, number, number] => {
                const numericValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.replace('%', '').trim()) : NaN;
                if (!Number.isFinite(numericValue)) return [100, 116, 139];
                if (numericValue >= 85) return [16, 185, 129];
                if (numericValue >= 60) return [245, 158, 11];
                return [220, 38, 38];
            };

            const normalizeComplianceLabel = (value: unknown): string => {
                const numericValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.replace('%', '').trim()) : NaN;
                if (!Number.isFinite(numericValue)) return 'N/A';
                return `${Math.round(numericValue)}%`;
            };

            // Resumen Ejecutivo
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.setTextColor(2, 16, 43);
            ensureSpace(20);
            pdf.text('RESUMEN EJECUTIVO', margin, y);
            y += 20;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(51, 65, 85);
            const summaryText = safeText(execSummary);
            const summaryLines = pdf.splitTextToSize(summaryText, pageWidth - margin * 2);
            
            for (const line of summaryLines) {
                ensureSpace(14);
                pdf.text(line, margin, y);
                y += 14;
            }

            y += 24;

            // Observaciones
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.setTextColor(2, 16, 43);
            ensureSpace(20);
            pdf.text('OBSERVACIONES DEL MES', margin, y);
            y += 20;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(51, 65, 85);
            const obsText = safeText(observations);
            const obsLines = pdf.splitTextToSize(obsText, pageWidth - margin * 2);
            
            for (const line of obsLines) {
                ensureSpace(14);
                pdf.text(line, margin, y);
                y += 14;
            }

            y += 36;

            // DESEMPEÑO DE INDICADORES (KPIs)
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.setTextColor(2, 16, 43);
            ensureSpace(20);
            pdf.text('DESEMPEÑO DE INDICADORES (KPIs)', margin, y);
            y += 20;

            if (!indicadores || indicadores.length === 0) {
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(10);
                pdf.setTextColor(51, 65, 85);
                pdf.text('Sin indicadores capturados para el periodo.', margin, y);
                y += 20;
            } else {
                pdf.setFontSize(9);
                pdf.setTextColor(100, 116, 139);
                ensureSpace(14);
                pdf.text('INDICADOR', margin, y);
                pdf.text('META', margin + 280, y);
                pdf.text('REAL', margin + 350, y);
                pdf.text('CUMPLIMIENTO', margin + 420, y);
                y += 10;
                pdf.setDrawColor(226, 232, 240);
                pdf.setLineWidth(1);
                pdf.line(margin, y, pageWidth - margin, y);
                y += 14;

                pdf.setTextColor(51, 65, 85);
                for (const ind of indicadores) {
                    const monthIdx = MONTHS.indexOf(selectedMonth);
                    const metrics = getIndicatorMetrics(ind, getElapsedCompletedMonths(monthIdx), false);
                    const canonicalReal = metrics.real;
                    const canonicalMeta = metrics.meta;
                    const periodPct = metrics.pct;

                    ensureSpace(14);
                    const indNameLines = pdf.splitTextToSize(ind.nombre || '— ', 260);
                    
                    let rowY = y;
                    for (let i = 0; i < indNameLines.length; i++) {
                        ensureSpace(14);
                        pdf.text(indNameLines[i], margin, rowY);
                        if (i === 0) {
                            pdf.text(String(canonicalMeta ?? '— '), margin + 280, rowY);
                            pdf.text(String(canonicalReal ?? '— '), margin + 350, rowY);
                            
                            let complianceLabel = '';
                            let r = 100, g = 116, b = 139; // Neutral gray
                            if (canonicalMeta === 0 && canonicalReal === 0) {
                                complianceLabel = 'SIN OBLIGACIÓN';
                            } else if (canonicalMeta > 0 && canonicalReal === 0 && !isSelectedPeriodOpen) {
                                complianceLabel = '0%';
                                r = 220; g = 38; b = 38; // Red
                            } else if (canonicalMeta > 0 && isSelectedPeriodOpen) {
                                if (canonicalReal === 0) {
                                    complianceLabel = 'PENDIENTE';
                                    r = 245; g = 158; b = 11; // Amber
                                } else {
                                    const pctVal = Math.min(100, Math.round((canonicalReal / canonicalMeta) * 100));
                                    complianceLabel = `${pctVal}% (PROV)`;
                                    r = 245; g = 158; b = 11; // Amber
                                }
                            } else {
                                complianceLabel = normalizeComplianceLabel(periodPct);
                                const [cr, cg, cb] = getCompliancePdfColor(periodPct);
                                r = cr; g = cg; b = cb;
                            }

                            pdf.setTextColor(r, g, b);
                            pdf.text(complianceLabel, margin + 420, rowY);
                            pdf.setTextColor(51, 65, 85);
                        }
                        rowY += 14;
                    }
                    y = rowY + 6;
                    ensureSpace(2);
                    pdf.setDrawColor(241, 245, 249);
                    pdf.line(margin, y, pageWidth - margin, y);
                    y += 10;
                }
            }

            y += 20;

            // Acciones prioritarias / plan
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.setTextColor(2, 16, 43);
            ensureSpace(20);
            pdf.text('ACCIONES PRIORITARIAS / PLAN', margin, y);
            y += 20;

            if (!instancePlanSections || instancePlanSections.length === 0) {
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(10);
                pdf.setTextColor(51, 65, 85);
                pdf.text('Sin acciones prioritarias capturadas.', margin, y);
                y += 20;
            } else {
                pdf.setFontSize(9);
                pdf.setTextColor(100, 116, 139);
                ensureSpace(14);
                pdf.text('SECCIÓN', margin, y);
                pdf.text('META', margin + 280, y);
                pdf.text('AVANCE', margin + 350, y);
                pdf.text('CUMPLIMIENTO', margin + 420, y);
                y += 10;
                pdf.setDrawColor(226, 232, 240);
                pdf.setLineWidth(1);
                pdf.line(margin, y, pageWidth - margin, y);
                y += 14;
                for (const section of instancePlanSections) {
                    const secMeta = parseNumberSafe(section.goal ?? section.meta_anual, 0);
                    const secAvance = parseNumberSafe(section.progress, 0);
                    const secPct = secMeta > 0 ? Math.min(100, Math.round((secAvance / secMeta) * 100)) : 0;

                    ensureSpace(14);
                    const secNameLines = pdf.splitTextToSize(section.name || section.title || '— ', 260);
                    
                    let rowY = y;
                    for (let i = 0; i < secNameLines.length; i++) {
                        ensureSpace(14);
                        pdf.text(secNameLines[i], margin, rowY);
                        if (i === 0) {
                            pdf.text(String(secMeta), margin + 280, rowY);
                            pdf.text(String(secAvance), margin + 350, rowY);

                            let complianceLabel = '';
                            let r = 100, g = 116, b = 139; // Neutral gray
                            if (secMeta === 0 && secAvance === 0) {
                                complianceLabel = 'SIN OBLIGACIÓN';
                            } else if (secMeta > 0 && secAvance === 0 && !isSelectedPeriodOpen) {
                                complianceLabel = '0%';
                                r = 220; g = 38; b = 38; // Red
                            } else if (secMeta > 0 && isSelectedPeriodOpen) {
                                if (secAvance === 0) {
                                    complianceLabel = 'PENDIENTE';
                                    r = 245; g = 158; b = 11; // Amber
                                } else {
                                    const pctVal = Math.min(100, Math.round((secAvance / secMeta) * 100));
                                    complianceLabel = `${pctVal}% (PROV)`;
                                    r = 245; g = 158; b = 11; // Amber
                                }
                            } else {
                                complianceLabel = normalizeComplianceLabel(secPct);
                                const [cr, cg, cb] = getCompliancePdfColor(secPct);
                                r = cr; g = cg; b = cb;
                            }

                            pdf.setTextColor(r, g, b);
                            pdf.text(complianceLabel, margin + 420, rowY);
                            pdf.setTextColor(51, 65, 85);
                        }
                        rowY += 14;
                    }
                    y = rowY + 6;
                    ensureSpace(2);
                    pdf.setDrawColor(241, 245, 249);
                    pdf.line(margin, y, pageWidth - margin, y);
                    y += 10;
                }
            }

            // Pie
            const pageCount = (pdf as any).internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                pdf.setPage(i);
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(148, 163, 184);
                pdf.text(`COPARMEX 2026 · Gobernanza Institucional · Desarrollado por IAPRIORI · Fecha de generación: ${new Date().toLocaleDateString()}`, margin, pageHeight - 30);
                pdf.text(`Página ${i} de ${pageCount}`, pageWidth - margin - 40, pageHeight - 30);
            }

            const fileSafeName = safeInstanceName
                .replace(/[^\w\s-]/g, '')
                .replace(/\s+/g, '_');

            pdf.save(`Informe_${fileSafeName}_${selectedMonth}_2026.pdf`);
        } catch (error) {
            console.error('[TRACKING_REPORT_PROGRAMMATIC_PDF_ERROR]', error);
            alert('No fue posible generar el PDF. Intenta de nuevo.');
        } finally {
            setIsExportingPDF(false);
        }
    };

    const currentSaveKeyRef = useRef<string | null>(null);

    const handleSaveIndicators = async (overrideIndicators?: any[], silent = false, exactRefresh = false) => {
        if (isSavingIndicatorsRef.current) return false;
        // 1. Guard defensivo: si no hay currentInstance válido, bloquear guardado
        if (!selectedInstancia || !currentInstance) {
            const errorMsg = "Guardado bloqueado: No se ha seleccionado una instancia activa válida.";
            console.error(errorMsg);
            alert(errorMsg);
            return false;
        }

        // 2. Guard defensivo: si currentInstance.id no coincide con selectedInstancia real, bloquear guardado y mostrar alerta
        if (currentInstance.id !== selectedInstancia) {
            const errorMsg = `Guardado bloqueado: Discrepancia detectada entre la instancia seleccionada (${selectedInstancia}) y la cargada (${currentInstance.id}). Por favor, recargue el módulo.`;
            console.error(errorMsg);
            alert(errorMsg);
            return false;
        }

        isSavingIndicatorsRef.current = true;
        const idempotencyKey = currentSaveKeyRef.current || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `save-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
        currentSaveKeyRef.current = idempotencyKey;

        try {
            const source = overrideIndicators || indicadores;

            // 3. Guard defensivo: si algún indicador no pertenece a la instancia activa, bloquear guardado
            const mismatchIndicator = source.find((ind: any) => {
                const targetId = ind.instance_id || ind.commission_id;
                return targetId && targetId !== selectedInstancia;
            });
            if (mismatchIndicator) {
                const errorMsg = `Guardado bloqueado: El indicador "${mismatchIndicator.nombre || mismatchIndicator.name}" pertenece a la instancia con ID ${mismatchIndicator.instance_id || mismatchIndicator.commission_id}, pero se intentó guardar en la instancia activa con ID ${selectedInstancia}.`;
                console.error(errorMsg);
                alert(errorMsg);
                return false;
            }
            const processedIndicators = source.map((ind: any) => {
                const history = ind.history || [];
                const isAvg = ind.calculo === 'Promedio';
                const eligibleHistory = history.filter((h: any) => (parseFloat(h.meta) || 0) > 0);
                
                const rawMeta = history.reduce((acc: number, h: any) => acc + (parseFloat(h.meta) || 0), 0);
                const metaAnual = (isAvg && eligibleHistory.length > 0) 
                    ? (rawMeta / eligibleHistory.length).toFixed(1) 
                    : rawMeta;

                return {
                    ...ind,
                    metaAnual
                };
            });
            const persistedIndicatorIds = new Set(processedIndicators.map((ind: any) => ind.id));
            const deletedIndicators = pendingDeletedIndicatorsRef.current.filter((deleted: any) =>
                deleted?.id && !persistedIndicatorIds.has(deleted.id)
            );

            await db.saveIndicators(selectedInstancia, processedIndicators, { idempotencyKey });
            setindicadores(processedIndicators);

            // CARDEX v18.20.9: Evento genérico de guardado de historial/captura —  no-bloqueante
            try {
                await recordAuditEvent({
                    module: 'SEGUIMIENTO',
                    entity_type: 'TRACKING_HISTORY',
                    entity_id: selectedInstancia,
                    action: 'UPDATE',
                    source: 'UI',
                    instance_id: selectedInstancia,
                    instance_name: currentInstance?.name,
                    user_id: user?.id,
                    user_name: user?.name,
                    user_email: user?.email,
                    user_role: user?.role,
                    metadata: {
                        trigger: 'tracking_ui',
                        month: selectedMonth,
                        indicatorCount: processedIndicators.length
                    }
                });
            } catch (_auditErr) {
                // Fallo silencioso —  no bloquea el guardado
            }

            // CARDEX v18.21.1: DELETE seguro solo despues de persistencia exitosa.
            for (const deletedIndicator of deletedIndicators) {
                try {
                    const history = Array.isArray(deletedIndicator.history) ? deletedIndicator.history : [];
                    const hasHistory = history.some((h: any) => {
                        const realVal = Number(h?.real ?? h?.valor ?? h?.value ?? 0);
                        const noteVal = typeof (h?.note ?? h?.comentarios ?? h?.notes ?? h?.comment) === 'string'
                            ? (h?.note ?? h?.comentarios ?? h?.notes ?? h?.comment).trim()
                            : '';
                        return realVal > 0 || noteVal.length > 0;
                    });

                    await recordAuditEvent({
                        module: 'SEGUIMIENTO',
                        entity_type: 'INDICATOR',
                        entity_id: deletedIndicator.id,
                        action: 'DELETE',
                        source: 'UI',
                        instance_id: selectedInstancia,
                        instance_name: currentInstance?.name || selectedInstancia,
                        user_id: user?.id,
                        user_name: user?.name,
                        user_email: user?.email,
                        user_role: user?.role,
                        old_value: {
                            id: deletedIndicator.id,
                            nombre: deletedIndicator.nombre || deletedIndicator.name,
                            peso: deletedIndicator.peso ?? deletedIndicator.weight,
                            hasHistory
                        },
                        new_value: null,
                        metadata: {
                            trigger: 'tracking_indicator_delete_after_save',
                            indicatorName: deletedIndicator.nombre || deletedIndicator.name,
                            persistence_result: 'deleted_or_archived',
                            note: 'El indicador fue removido de la lista visible y persistido mediante saveIndicators'
                        }
                    });
                } catch (_auditErr) {
                    // Fallo silencioso: no bloquea el guardado principal.
                }
            }
            if (deletedIndicators.length > 0) {
                const persistedDeletedIds = new Set(deletedIndicators.map((deleted: any) => deleted.id));
                pendingDeletedIndicatorsRef.current = pendingDeletedIndicatorsRef.current.filter((deleted: any) =>
                    !persistedDeletedIds.has(deleted?.id)
                );
            }

            if (!silent) {
                alert(`Guardado correctamente en "${currentInstance?.name || 'la instancia activa'}"`);
            }
            await refreshIndicators(exactRefresh);
            return true;
        } catch (error: unknown) {
            console.error('[TRACKING] handleSaveIndicators error:', error);
            alert(db.getMutationErrorMessage(error));
            await refreshIndicators(exactRefresh);
            return false;
        } finally {
            isSavingIndicatorsRef.current = false;
            currentSaveKeyRef.current = null;
        }
    };

    const updateIndicatorHistory = (id: string, periodIdx: number, field: string, value: any, explicitCapture = false) => {
        setindicadores(prev => prev.map(ind => {
            if (ind.id !== id) return ind;
            const newHistory = [...(ind.history || [])];
            if (newHistory[periodIdx]) {
                const nextEntry = { ...newHistory[periodIdx], [field]: value };
                const meaningfulNote = field === 'note' && typeof value === 'string' && value.trim().length > 0;
                if (explicitCapture && (field === 'real' || meaningfulNote)) nextEntry.captureConfirmedAt = new Date().toISOString();
                newHistory[periodIdx] = nextEntry;
            }
            return { ...ind, history: newHistory };
        }));
    };

    const updateIndicatorProtocol = (id: string, label: string, protocolActions: any[]) => {
        return indicadores.map(ind => {
            if (ind.id === id) {
                return {
                    ...ind,
                    protocolLabel: label,
                    protocolType: label,
                    protocol_type: label,
                    protocolActions,
                    protocol_actions: protocolActions,
                    logic_config: {
                        ...(ind.logic_config || {}),
                        protocol_type: label,
                        protocol_actions: protocolActions
                    }
                };
            }
            return ind;
        });
    };

    const handleDeleteIndicator = async (id: string) => {
        if (!window.confirm('¿Está seguro de eliminar este indicador?')) return;
        try {
            await db.deleteIndicator(id);
            setindicadores(prev => prev.filter(ind => ind.id !== id));
            await refreshIndicators();
            alert('Indicador eliminado exitosamente.');
        } catch (error: any) {
            console.error('[DELETE_INDICATOR_ERROR]', error);
            alert('Error al eliminar el indicador: ' + (error.message || 'Error desconocido'));
        }
    };

    const handleReplicateIndicators = async () => {
        if (!selectedInstancia || !indicadores.length) return;
        if (replicaTargets.length === 0) {
            alert('Por favor Selecciona al menos una instancia destino.');
            return;
        }

        if (indicatorSyncMode === 'FULL_STRUCTURE_REPLACE_EXPLICIT') {
            alert('Reemplazo estructural completo no esta disponible en esta fase.');
            return;
        }

        const modeLabelMap: Record<string, string> = {
            CATALOG_ONLY_KEEP_DESTINATION_DATA: 'Sincronizar indicadores sin tocar datos',
            CATALOG_AND_ORDER_KEEP_DESTINATION_DATA: 'Sincronizar indicadores y orden conservando datos',
            CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS: 'Sincronizar indicadores, orden y metas conservando avances'
        };

        const confirmRep = confirm(`Desea ejecutar "${modeLabelMap[indicatorSyncMode]}" en ${replicaTargets.length} instancia(s)? No se borraran indicadores ni se limpiaran avances, notas o checklists.`);
        if (!confirmRep) return;

        try {
            setLoading(true);
            const result = await db.syncIndicatorsSafe(selectedInstancia, replicaTargets, indicatorSyncMode);
            const created = result.results.reduce((acc: number, row: any) => acc + (row.created || 0), 0);
            const orderChanges = result.results.reduce((acc: number, row: any) => acc + (row.orderChanges || 0), 0);
            const targetCopies = result.results.reduce((acc: number, row: any) => acc + (row.targetsCopied || 0), 0);
            alert(`Sincronizacion segura completada. Creados: ${created}. Orden: ${orderChanges}. Metas copiadas: ${targetCopies}.`);
            setShowReplicaModal(false);
            setReplicaTargets([]);
            setLoading(false);
        } catch (error) {
            console.error(error);
            alert('Error en la replica masiva.');
            setLoading(false);
        }
    };

    const toggleReplicaTarget = (id: string) => {
        setReplicaTargets(prev =>
            prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
        );
    };

    const selectGroup = (ids: string[]) => {
        const allPresent = ids.every(id => replicaTargets.includes(id));
        if (allPresent) {
            setReplicaTargets(prev => prev.filter(t => !ids.includes(t)));
        } else {
            setReplicaTargets(prev => [...new Set([...prev, ...ids])]);
        }
    };

    const handleExportExcel = () => {
        if (!indicadores.length || !currentInstance) {
            alert('No hay indicadores para exportar.');
            return;
        }
        // Columnas: Indicador | Objetivo | Algoritmo | Frecuencia | Peso | Meta Anual | periodos...
        const periods = indicadores[0]?.history?.map((h: any) => h.period) || [];
        const headers = ['Indicador', 'Objetivo', 'Algoritmo', 'Frecuencia', 'Peso %', 'Meta Anual', ...periods.map((p: string) => `Real ${p}`), ...periods.map((p: string) => `Meta ${p}`)];

        const rows = indicadores.map(ind => {
            const realValues = (ind.history || []).map((h: any) => h.real ?? 0);
            const metaValues = (ind.history || []).map((h: any) => h.meta ?? 0);
            return [
                ind.nombre,
                ind.tipo,
                ind.calculo,
                ind.frec,
                ind.peso,
                ind.metaAnual,
                ...realValues,
                ...metaValues,
            ];
        });

        // CSV con BOM para UTF-8 en Excel
        const BOM = '\uFEFF';
        const csvContent = BOM + [
            headers.join(','),
            ...rows.map(row => row.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const instanceName = currentInstance?.name?.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ _-]/g, '') || 'indicadores';
        link.href = url;
        link.download = `${instanceName}_KPIs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const canManageTracking = canEditModule(user, 'Seguimiento');
    const isAdmin = (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR') && canManageTracking;
    const canCapture = canManageTracking;
    const canEditReport = canManageTracking;

    const currentInstance = useMemo(() => {
        if (!selectedInstancia || !hierarchy) return null;
        return allInstances.find((c: any) => c.id === selectedInstancia) || null;
    }, [allInstances, hierarchy, selectedInstancia]);

    // Separate informational read model; never enters executive alerts or scores.
    const priorPeriodCaptureSummary = useMemo(() => {
        const empty = { evaluable: false, pending: [], indeterminate: [] };
        if (!currentInstance || loading || scopedReadError || !MONTHS.includes(selectedMonth)) return empty;
        const currentMonthIndex = parseCaptureOperationalMonth((settings as SystemSettings & { current_month?: unknown }).current_month);
        if (currentMonthIndex === null) return empty;
        const selectedMonthIndex = parseSelectedMonthIndex(selectedMonth);
        const instanceId = String(currentInstance.id);
        const ownIndicators = indicadores.filter((indicator) => String(indicator.instance_id || indicator.unified_instance_id || indicator.commission_id || '').trim() === instanceId);
        const config = getOperationalStartPeriodConfig(2026);
        const validMonths = [...new Set<number>(ownIndicators.flatMap((indicator) => (indicator.history || []).flatMap((entry: any, index: number) =>
            Number.isFinite(Number(entry?.meta)) && Number(entry.meta) > 0 ? [index] : [])))];
        const lifecycle = detectInstanceOperationalActivation({
            instance: currentInstance, indicators: ownIndicators, reports: [],
            workPlan: null, planTotal: null, year: 2026, selectedMonthIndex, cycleConfig: config,
        });
        const operationalWindow = resolveOperationalStartPeriod({
            year: 2026, instanceId, vpId: currentInstance.vicepresidency_id || null,
            selectedMonthIndex, currentMonthIndex, lifecycle, config, validMonths,
        });
        return buildPriorPeriodCaptureSummary({
            instanceId, indicators: ownIndicators, cycleYear: 2026, selectedMonthIndex,
            currentMonthIndex, lastCompletedMonthIndex: getElapsedCompletedMonths(), operationalWindow,
            regimeActivation: getCaptureRegimeActivation(import.meta.env.DEV),
        });
    }, [currentInstance, indicadores, selectedMonth, settings, scopedReadError, loading]);

    const instanceExecutiveSummary = useMemo(() => {
        const monthIdx = MONTHS.indexOf(selectedMonth);
        const lastClosedMonthIdx = getElapsedCompletedMonths(monthIdx);
        const kpiRows = indicadores.map((ind) => {
            const metrics = getIndicatorMetrics(ind, getElapsedCompletedMonths(monthIdx), false);
            const monthEntry = ind.history?.find((h: any) => h.period === selectedMonth) || {};
            const hasCapture = hasRealCaptureEvidence(monthEntry);
            const hasTarget = Number(monthEntry.meta || 0) > 0 || Number(metrics.meta || 0) > 0;
            return { metrics, hasCapture, hasTarget };
        });

        const planGoal = parseNumberSafe(instancePlanTotals?.total_goal, 0);
        const planProgressValue = parseNumberSafe(instancePlanTotals?.total_progress, 0);
        const sectionsCount = instancePlanSections.length;
        const activePlan = planGoal > 0 || sectionsCount > 0;

        const hasClosedPlanObligation = hasExplicitClosedPlanObligation(instancePlanSections || [], lastClosedMonthIdx);
        const rawPlanPct = planGoal > 0
            ? Math.min(100, Math.round((planProgressValue / planGoal) * 100))
            : (sectionsCount > 0 ? parseNumberSafe(instancePlanTotals?.planPct, 0) : null);
        const planProgressState = resolveCanonicalPlanProgressState({
            readError: scopedReadError,
            totalGoal: planGoal,
            canonicalPercentage: rawPlanPct
        });
        const planPct = planProgressState.state === 'VALUE' ? planProgressState.value : null;
        const planEligibleForPeriod = planProgressState.state === 'VALUE';

        const activeKpis = kpiRows.filter((row) => row.hasTarget).length;
        const kpisCaptured = kpiRows.filter((row) => row.hasCapture).length;
        const kpisPending = kpiRows.filter((row) => row.hasTarget && !row.hasCapture).length;
        
        const kpiComplianceValue = calculateProratedCompliance(indicadores, getElapsedCompletedMonths(monthIdx), false, true).cumplimiento;
        const kpiCompliance = activeKpis > 0 ? (kpiComplianceValue ?? 0) : null;
        const closedKpiReason = buildClosedKpiReason(indicadores, lastClosedMonthIdx);

        const sectionsWithGoalAndZero = (instancePlanSections || []).filter((section: any) => parseNumberSafe(section.goal ?? section.meta_anual, 0) > 0 && parseNumberSafe(section.progress, 0) <= 0).length;
        const sectionsActive = (instancePlanSections || []).filter((section: any) => parseNumberSafe(section.goal ?? section.meta_anual, 0) > 0).length;

        const planSemaphore = !planEligibleForPeriod ? 'NEUTRAL' 
            : planPct! >= (settings?.threshold_green ?? 90) ? 'ÓPTIMO' 
            : planPct! >= (settings?.threshold_amber ?? 75) ? 'ATENCIÓN' : 'CRÍTICO';

        const kpiSemaphore = activeKpis === 0 ? 'NEUTRAL'
            : kpiCompliance! >= (settings?.threshold_green ?? 90) ? 'ÓPTIMO'
            : kpiCompliance! >= (settings?.threshold_amber ?? 75) ? 'ATENCIÓN' : 'CRÍTICO';

        const alerts = buildDashboardObligations(indicadores, monthIdx, DASHBOARD_CYCLE_YEAR);
        const captureCounter = buildDashboardCaptureCounter(indicadores, monthIdx);
        return {
            planGoal,
            planProgressValue,
            planPct,
            planProgressState: planProgressState.state,
            sectionsCount,
            sectionsActive,
            sectionsPending: Math.max(0, sectionsCount - sectionsActive),
            kpisConfigured: indicadores.length,
            kpisCaptured,
            kpisPending,
            activeKpis,
            kpiCompliance,
            planEligibleForPeriod,
            hasClosedPlanObligation,
            planSemaphore,
            kpiSemaphore,
            alerts,
            captureCounter,
            lastUpdate: currentInstance?.updated_at || null
        };
    }, [indicadores, selectedMonth, instancePlanTotals, instancePlanSections, settings, execSummary, observations, currentInstance, scopedReadError]);

    const instanceWeightedPerformance = useMemo(() => {
        if (!currentInstance || !instanceExecutiveSummary) return null;
        
        const instanceForCalc = {
            ...currentInstance,
            active: true, // Forzar inclusión en el cálculo
            type: currentInstance?.type || 'COMISION',
            kpiCompliance: instanceExecutiveSummary.kpiCompliance ?? 0,
            planProgress: instanceExecutiveSummary.planPct ?? 0,
            hasActiveKpis: instanceExecutiveSummary.activeKpis > 0,
            hasActivePlan: instanceExecutiveSummary.planEligibleForPeriod,
            kpiEligible: instanceExecutiveSummary.activeKpis > 0,
            planEligible: instanceExecutiveSummary.planEligibleForPeriod
        };
        
        return calculateVPPerformance([instanceForCalc], settings);
    }, [currentInstance, instanceExecutiveSummary, settings]);

    const filteredInstances = useMemo(() => {
        let all = [...allInstances];

        // RBAC VISUAL HARDLOCK
        if (!isSAOrAdmin) {
            if (isInstanceScoped) {
                const targetInst = user?.commission_id || user?.instance_id;
                all = all.filter((c: any) => c.id === targetInst);
            } else if (isVPScoped && user?.vicepresidency_id) {
                all = all.filter((c: any) => c.vicepresidency_id === user.vicepresidency_id);
            }
        }

        // Filtrar por VP Seleccionada si hay una activa
        if (selectedVPId) {
            all = all.filter((c: any) => c.vicepresidency_id === selectedVPId);
        }
        if (typeFilter !== 'TODOS') {
            all = all.filter((c: any) => canonicalInstanceType(c.type) === canonicalInstanceType(typeFilter));
        }
        if (search) {
            const s = search.toLowerCase();
            all = all.filter((c: any) => 
                c.name.toLowerCase().includes(s) || 
                (c.president_name && c.president_name.toLowerCase().includes(s))
            );
        }
        return all.sort((a, b) => a.name.localeCompare(b.name));
    }, [allInstances, search, typeFilter, selectedVPId, isSAOrAdmin, isInstanceScoped, isVPScoped, user]);


    const totalPeso = indicadores.reduce((acc, curr) => acc + curr.peso, 0);

    const handleAddIndicator = async () => {
        if (!isAdmin) {
            alert('Acceso denegado: Solo el Administrador puede agregar indicadores estratégicos.');
            return;
        }
        const newInd = {
            id: crypto.randomUUID(),
            nombre: 'Nuevo Indicador',
            unidad: '#',
            tipo: 'Maximizar',
            calculo: 'Sumar',
            frec: 'Mensual',
            metaAnual: 0,
            peso: 0,
            history: MONTHS.map(m => ({ period: m, meta: 0, real: 0, note: '' }))
        };
        const nextInds = [...indicadores, newInd];
        setindicadores(nextInds);
        await handleSaveIndicators(nextInds);

        // CARDEX v18.20.9: Evento CREATE de indicador — no-bloqueante
        try {
            await recordAuditEvent({
                module: 'SEGUIMIENTO',
                entity_type: 'INDICATOR',
                entity_id: newInd.id,
                action: 'CREATE',
                source: 'UI',
                instance_id: selectedInstancia ?? undefined,
                instance_name: currentInstance?.name,
                user_id: user?.id,
                user_name: user?.name,
                user_email: user?.email,
                user_role: user?.role,
                new_value: { nombre: newInd.nombre, peso: newInd.peso },
                metadata: {
                    trigger: 'tracking_ui',
                    indicatorName: newInd.nombre
                }
            });
        } catch (_auditErr) {
            // Fallo silencioso — no bloquea el alta
        }
    };

    const handleDragStart = (id: string) => {
        setDraggedIndicatorId(id);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (targetId: string) => {
        if (!draggedIndicatorId || draggedIndicatorId === targetId) return;

        const oldIndex = indicadores.findIndex(i => i.id === draggedIndicatorId);
        const newIndex = indicadores.findIndex(i => i.id === targetId);

        if (oldIndex === -1 || newIndex === -1) return;

        const newIndicators = [...indicadores];
        const [moved] = newIndicators.splice(oldIndex, 1);
        newIndicators.splice(newIndex, 0, moved);

        // Actualizar el orden interno de cada indicador basándose en su nueva posicion
        const finalIndicators = newIndicators.map((ind, index) => ({
            ...ind,
            orden: index
        }));

        setindicadores(finalIndicators);
        setHasPendingOrderChanges(true);
        setDraggedIndicatorId(null);
    };

    const handleSaveIndicatorOrder = async () => {
        if (!selectedInstancia || indicadores.length === 0) return;
        try {
            setIsSavingOrder(true);
            const orderedIndicators = indicadores.map((ind: any, index: number) => ({
                ...ind,
                orden: index
            }));

            // Persistencia quirurgica: solo id, orden y updated_at via saveIndicatorOrder.
            await db.saveIndicatorOrder(orderedIndicators);
            setindicadores(orderedIndicators);
            setHasPendingOrderChanges(false);
            alert('Orden de indicadores guardado exitosamente.');
        } catch (error: any) {
            console.error('[INDICATOR_ORDER_SAVE_ERROR]', error);
            alert('Error al guardar orden: ' + (error.message || 'Error desconocido'));
        } finally {
            setIsSavingOrder(false);
        }
    };

    const handleSaveIndicatorProperties = async () => {
        if (!selectedInstancia || indicadores.length === 0) return;
        try {
            await db.saveIndicatorProperties(indicadores);
            alert('Propiedades de indicadores guardadas correctamente.');
            refreshIndicators();
        } catch (error: any) {
            console.error('[INDICATOR_PROPERTIES_SAVE_ERROR]', error);
            alert('Error al guardar propiedades: ' + (error.message || 'Error desconocido'));
        }
    };


    // --- Protocolo de Accion Condicional (PAI / Mantenimiento / Mitigacion) ---
    const IndicatorProtocol = ({ ind, semaforo, prevPeriod, prevPct, openPeriod }: {
        ind: any; semaforo: 'red' | 'yellow' | 'green'; prevPeriod: any; prevPct: number; openPeriod: boolean;
    }) => {
        const [expanded, setExpanded] = useState(false);
        // Inicializar desde Supabase si ya hay datos guardados
        const [actions, setActions] = useState<Array<{ accion: string; fecha: string; notas: string; impacto: string }>>(
            (storedProtocolActions(ind).length > 0)
                ? storedProtocolActions(ind)
                : [{ accion: '', fecha: '', notas: '', impacto: 'Nulo' }]
        );

        const existingActions = hasProtocolContent(storedProtocolActions(ind));
        const persistedType = storedProtocolType(ind);
        const suggestedType = semaforo === 'red' ? 'PAI' : semaforo === 'yellow' ? 'MITIGACIÓN' : 'MANTENIMIENTO';
        const [selectedType, setSelectedType] = useState(persistedType || (openPeriod ? '' : suggestedType));
        const effectiveType = existingActions ? persistedType : selectedType;
        const isRed = effectiveType === 'PAI';
        const isYellow = effectiveType === 'MITIGACIÓN';
        const isGreen = effectiveType === 'MANTENIMIENTO';
        const protocolLabel = effectiveType;
        const hasActions = existingActions;
        const isVisor = !canEditModule(user, 'Seguimiento') || user?.role === 'VISOR';

        const config = !protocolLabel || !protocolTypes.includes(protocolLabel as any)
            ? { label: protocolLabel ? 'PROTOCOLO EXISTENTE · ' + protocolLabel : 'ACCIONES DEL INDICADOR', subtitle: 'Selecciona un tipo explícito antes de guardar. El resultado provisional no exige un PAI.', bg: 'bg-slate-950/40', border: 'border-slate-700', icon: '📋', btnLabel: hasActions ? 'Gestionar acciones' : 'Definir acciones', btnClass: 'bg-sky-700 hover:bg-sky-600 text-white', textColor: 'text-sky-300' }
            : isRed
            ? { label: "PROTOCOLO DE ACCIÓN INMEDIATA (PAI)", subtitle: "", bg: "bg-red-950/40", border: "border-red-900/60", icon: "🚨", btnLabel: hasActions ? "Gestionar Plan de Acción" : "Iniciar Plan de Acción", btnClass: "bg-red-600 hover:bg-red-500 text-white", textColor: "text-red-400" }
            : isYellow
            ? { label: "PLAN DE MITIGACIÓN", subtitle: "Define las acciones para recuperar el indicador.", bg: "bg-amber-950/30", border: "border-amber-900/40", icon: "⚠️", btnLabel: hasActions ? "Editar Plan de Mitigación" : "Iniciar Plan de Mitigación", btnClass: "bg-amber-600 hover:bg-amber-500 text-white", textColor: "text-amber-400" }
                : { label: "ESTRATEGIA DE MANTENIMIENTO", subtitle: "Documenta las acciones que mantienen el indicador en exito.", bg: "bg-emerald-950/20", border: "border-emerald-900/30", icon: "✅", btnLabel: hasActions ? "Editar Estrategia" : "Iniciar Estrategia", btnClass: "bg-emerald-600 hover:bg-emerald-500 text-white", textColor: "text-emerald-400" };

        const IMPACT_OPTIONS = ['Nulo', 'Bajo', 'Medio', 'Alto', 'Positivo'];
        const impactColors: Record<string, string> = { Nulo: 'text-white/60', Bajo: 'text-red-400', Medio: 'text-amber-400', Alto: 'text-orange-400', Positivo: 'text-emerald-400' };
        const getDateInputValue = (rawDate: string) => {
            if (!rawDate) return '';
            const trimmed = String(rawDate).trim();
            return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : '';
        };
        const getLegacyDateLabel = (rawDate: string) => {
            if (!rawDate) return '';
            const trimmed = String(rawDate).trim();
            return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? '' : trimmed;
        };

        const handleSaveProtocol = async () => {
            const cleanActions = actions.filter(a => a.accion?.trim() || a.notas?.trim());
            const typeToSave = protocolSaveType(ind, selectedType);
            if (!typeToSave || isVisor) return;
            const updatedArray = updateIndicatorProtocol(ind.id, typeToSave, cleanActions);
            const saved = await handleSaveIndicators(updatedArray, false, true);
            if (saved) {
                setExpanded(false);
            }
        };

        return (
            <div className={`rounded-[1.5rem] border ${config.bg} ${config.border} overflow-hidden transition-all`}>
                <div className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-xl">{config.icon}</span>
                        <div>
                            <p className={`text-[10px] font-black uppercase tracking-widest ${config.textColor}`}>{config.label}</p>
                            {config.subtitle && <p className="text-[9px] text-white/70 mt-0.5">{config.subtitle}</p>}
                            {!expanded && hasActions && (
                                <p className="text-[9px] text-white/70 mt-0.5">
                                    {actions.filter(a => a.accion?.trim() || a.notas?.trim()).length} acción(es) documentada(s)
                                </p>
                            )}
                            {isRed && !expanded && !hasActions && (
                                <p className="text-[9px] text-slate-600 italic mt-0.5">-- Protocolo PAI pendiente de definición --</p>
                            )}
                        </div>
                    </div>
                    {!expanded && !isVisor && (
                        <button onClick={() => setExpanded(true)}
                            className={`px-5 py-2 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all ${config.btnClass}`}>
                            {config.btnLabel}
                        </button>
                    )}
                    {!expanded && isVisor && hasActions && (
                        <button onClick={() => setExpanded(true)}
                            className="px-5 py-2 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all bg-slate-800 text-slate-300 hover:text-white">
                            Ver {config.label}
                        </button>
                    )}
                </div>
                {openPeriod && <p className="px-6 pb-3 text-xs text-slate-300">Gestión por indicador · el resultado provisional no cambia el tipo registrado.</p>}
                {expanded && (
                    <div className="px-6 pb-6 border-t border-slate-800/60">
                        {!isVisor && !persistedType && <label className="mt-3 block text-xs text-slate-300">Tipo de protocolo<select aria-label="Tipo de protocolo" value={selectedType} onChange={event => setSelectedType(event.target.value)} className="ml-3 rounded border border-slate-700 bg-slate-950 p-2"><option value="">Seleccionar tipo</option>{protocolTypes.map(type => <option key={type} value={type}>{type}</option>)}</select></label>}
                        <div className="mt-4 space-y-3">
                            <div className="grid grid-cols-12 gap-2 text-[8px] text-slate-600 font-black uppercase px-1">
                                <span className="col-span-4">Acción Táctica</span>
                                <span className="col-span-2">Fecha</span>
                                <span className="col-span-4">Resultados / Notas</span>
                                <span className="col-span-1">Impacto</span>
                                <span className="col-span-1"></span>
                            </div>
                            {actions.map((action, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                                    <textarea
                                        className="col-span-4 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 outline-none focus:border-slate-600 resize-none disabled:opacity-50"
                                        placeholder="Describir acción..."
                                        rows={2}
                                        value={action.accion}
                                        onChange={e => { const a = [...actions]; a[idx].accion = e.target.value; setActions(a); }}
                                        disabled={isVisor}
                                    />
                                    <div className="col-span-2 flex flex-col gap-1">
                                        <input type="date"
                                            className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 outline-none focus:border-slate-600 disabled:opacity-50"
                                            value={getDateInputValue(action.fecha)}
                                            onChange={e => { const a = [...actions]; a[idx].fecha = e.target.value; setActions(a); }}
                                            disabled={isVisor}
                                        />
                                        {getLegacyDateLabel(action.fecha) && (
                                            <span className="text-[9px] text-amber-400/80 px-1">
                                                Fecha legacy: {getLegacyDateLabel(action.fecha)}
                                            </span>
                                        )}
                                    </div>
                                    <textarea
                                        className="col-span-4 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 outline-none focus:border-slate-600 resize-none disabled:opacity-50"
                                        placeholder="Logros o notas..."
                                        rows={2}
                                        value={action.notas}
                                        onChange={e => { const a = [...actions]; a[idx].notas = e.target.value; setActions(a); }}
                                        disabled={isVisor}
                                    />
                                    <div className="col-span-1">
                                        <select
                                            className={`w-full bg-slate-950 border border-slate-800 rounded-xl px-2 py-2 text-[10px] font-black outline-none focus:border-slate-600 ${impactColors[action.impacto]} disabled:opacity-50`}
                                            value={action.impacto}
                                            onChange={e => { const a = [...actions]; a[idx].impacto = e.target.value; setActions(a); }}
                                            disabled={isVisor}
                                        >
                                            {IMPACT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    {!isVisor && (
                                        <button onClick={() => setActions(a => a.filter((_, i) => i !== idx))}
                                            className="col-span-1 flex items-center justify-center h-8 w-8 rounded-xl bg-slate-800 text-white/70 hover:text-red-400 transition-all">
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between mt-4">
                            {!isVisor ? (
                                <button onClick={() => setActions(a => [...a, { accion: '', fecha: '', notas: '', impacto: 'Nulo' }])}
                                    className="text-[9px] text-white/60 hover:text-white font-black uppercase tracking-widest flex items-center gap-1 transition-all">
                                    <Plus size={12} /> Agregar acción
                                </button>
                            ) : <div />}
                            <div className="flex gap-3">
                                <button onClick={() => setExpanded(false)}
                                    className="px-5 py-2 bg-slate-800 text-white/60 rounded-xl font-black text-[9px] uppercase tracking-widest hover:text-white transition-all">
                                    {!isVisor ? 'Cancelar' : 'Cerrar'}
                                </button>
                                {!isVisor && (
                                    <button disabled={!protocolSaveType(ind, selectedType)} onClick={handleSaveProtocol}
                                        className={`px-7 py-2 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all flex items-center gap-2 ${config.btnClass}`}>
                                        <Save size={12} /> Guardar Itinerario
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };


    // --- ChecklistManagerView: Gestión de Elementos FULL-YEAR GRID (Modo Checklist) ---
    const ChecklistManagerView = ({ ind, periodIdx: initialPeriodIdx, onClose, onNavigate }: { key?: any; ind: any; periodIdx: number; onClose: () => void; onNavigate?: (newIdx: number) => void }) => {
        const progressEditedPeriods = useRef(new Set<number>());
        // All-periods state: store elements for EVERY period
        const [allElements, setAllElements] = useState<Record<number, Array<{ id: string; nombre: string; meta: number; realizado: number }>>>(() => {
            const init: Record<number, Array<{ id: string; nombre: string; meta: number; realizado: number }>> = {};
            ind.history.forEach((h: any, i: number) => {
                // Unificación: si hay elementos/activities se usan. Si no, pero hay meta/real, se sintetizan para evitar el 0/0.
                if (h?.elements && h.elements.length > 0) {
                    init[i] = [...h.elements];
                } else if (h?.activities && h.activities.length > 0) {
                    init[i] = [...h.activities];
                } else if (h?.meta > 0) {
                    // Síntesis controlada para mantener integridad de datos
                    init[i] = Array.from({ length: Math.ceil(h.meta) }, (_, idx) => ({
                        id: crypto.randomUUID(),
                        nombre: `Elemento ${idx + 1}`,
                        meta: 1,
                        realizado: idx < h.real ? 1 : 0
                    }));
                } else {
                    init[i] = [];
                }
            });
            return init;
        });
        const [expandedPeriod, setExpandedPeriod] = useState<number | null>(initialPeriodIdx);
        const [showMasiva, setShowMasiva] = useState(false);
        const [masivaText, setMasivaText] = useState('');
        const [search, setSearch] = useState('');

        // Compute totals across expired periods (up to last closed period)
        const lastClosedIdx = getElapsedCompletedMonths();

        // Convert object to array, filter by period index <= lastClosedIdx, then sum
        const expiredElements = Object.entries(allElements)
            .filter(([idx, _]) => parseInt(idx) <= lastClosedIdx)
            .map(([_, acts]) => acts) as any[];

        const globalTotalMeta: number = expiredElements.reduce((s: number, acts: any) => s + acts.reduce((ss: number, a: any) => ss + (a.meta || 0), 0), 0);
        const globalTotalReal: number = expiredElements.reduce((s: number, acts: any) => s + acts.reduce((ss: number, a: any) => ss + (a.realizado || 0), 0), 0);
        const globalPct = globalTotalMeta > 0 ? Math.round((globalTotalReal / globalTotalMeta) * 100) : 0;
        const isVisor = !canEditModule(user, 'Seguimiento') || user?.role === 'VISOR';

        // Period helpers
        const getPerLabel = (idx: number) => ind.history[idx]?.period?.toUpperCase() || `P${idx + 1}`;
        const getPerElements = (idx: number) => allElements[idx] || [];
        const getPerMeta = (idx: number) => getPerElements(idx).reduce((s, a) => s + (a.meta || 0), 0);
        const getPerReal = (idx: number) => getPerElements(idx).reduce((s, a) => s + (a.realizado || 0), 0);
        const getPerPct = (idx: number) => { const m = getPerMeta(idx); return m > 0 ? Math.round((getPerReal(idx) / m) * 100) : 0; };

        // Element CRUD for a specific period
        const addElement = (periodI: number, nombre = '') => {
            setAllElements(prev => ({
                ...prev,
                [periodI]: [...(prev[periodI] || []), { id: crypto.randomUUID(), nombre, meta: 1, realizado: 0 }]
            }));
        };
        const removeElement = (periodI: number, id: string) => {
            setAllElements(prev => ({
                ...prev,
                [periodI]: (prev[periodI] || []).filter(a => a.id !== id)
            }));
        };
        const setElementField = (periodI: number, id: string, field: 'meta' | 'realizado' | 'nombre', val: any) => {
            if (field === 'realizado' && Number(val) > 0) progressEditedPeriods.current.add(periodI);
            setAllElements(prev => ({
                ...prev,
                [periodI]: (prev[periodI] || []).map(a => a.id === id ? { ...a, [field]: field === 'nombre' ? val : Math.max(0, val) } : a)
            }));
        };
        const updateElement = (periodI: number, id: string, field: 'meta' | 'realizado', delta: number) => {
            if (field === 'realizado') {
                const element = (allElements[periodI] || []).find((item) => item.id === id);
                if (element && Math.max(0, (element.realizado || 0) + delta) > 0) progressEditedPeriods.current.add(periodI);
            }
            setAllElements(prev => ({
                ...prev,
                [periodI]: (prev[periodI] || []).map(a => a.id === id ? { ...a, [field]: Math.max(0, (a[field] || 0) + delta) } : a)
            }));
        };

        const handleMasiva = () => {
            if (expandedPeriod === null) return;
            const lines = masivaText.split('\n').map(l => l.trim()).filter(Boolean);
            setAllElements(prev => ({
                ...prev,
                [expandedPeriod]: [...(prev[expandedPeriod] || []), ...lines.map(line => ({ id: crypto.randomUUID(), nombre: line, meta: 1, realizado: 0 }))]
            }));
            setMasivaText('');
            setShowMasiva(false);
        };

        // Copy elements from one period to all others
        const copyToAllPeriods = (sourcePeriodIdx: number) => {
            const sourceElements = allElements[sourcePeriodIdx] || [];
            if (sourceElements.length === 0) return;
            if (!confirm(`¿Copiar ${sourceElements.length} elementos de ${getPerLabel(sourcePeriodIdx)} a todos los periodos? Los elementos existentes se conservarán.`)) return;
            setAllElements(prev => {
                const next = { ...prev };
                ind.history.forEach((_: any, i: number) => {
                    if (i !== sourcePeriodIdx) {
                        const existingNames = new Set((next[i] || []).map(a => a.nombre.toLowerCase()));
                        const newElements = sourceElements.filter(a => !existingNames.has(a.nombre.toLowerCase())).map(a => ({ ...a, id: crypto.randomUUID(), realizado: 0 }));
                        next[i] = [...(next[i] || []), ...newElements];
                    }
                });
                return next;
            });
        };

        const doSaveAll = () => {
            const updatedInds = indicadores.map(i => {
                if (i.id === ind.id) {
                    const newHistory = i.history.map((h: any, idx: number) => {
                        const acts = allElements[idx] || [];
                        const newReal = acts.reduce((s, a) => s + (a.realizado || 0), 0);
                        const newMeta = acts.reduce((s, a) => s + (a.meta || 0), 0);
                        // Guardamos como both elements y activities para compatibilidad. Meta siempre es dinámica en checklist.
                        const next = { ...h, elements: acts, activities: acts, real: newReal, meta: newMeta };
                        if (progressEditedPeriods.current.has(idx) && newReal > 0) next.captureConfirmedAt = new Date().toISOString();
                        return next;
                    });
                    return { ...i, history: newHistory };
                }
                return i;
            });
            setindicadores(updatedInds);
            return updatedInds;
        };

        const handleSave = () => {
            const updatedInds = doSaveAll();
            handleSaveIndicators(updatedInds);
            onClose();
        };

        const currentPeriodLabel = getCurrentPeriod(ind.frec);

        return (
            <div className="bg-[#080C14] border border-slate-800 rounded-[2rem] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="px-6 md:px-8 py-6 border-b border-slate-800/60 flex flex-wrap gap-4 items-start justify-between">
                    <div className="flex flex-col gap-2">
                        <p className="text-[10px] text-violet-400 font-black uppercase tracking-[0.3em]">
                            Editando "{ind.nombre}" de "{currentInstance?.name || 'la instancia activa'}"
                        </p>
                        <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-tighter leading-tight break-words max-w-xl">
                            {ind.nombre}
                        </h2>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Global Impact */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl px-5 py-3 text-center">
                            <p className="text-[8px] text-white/70 font-black uppercase tracking-widest mb-1">IMPACTO GLOBAL</p>
                            <p className="text-xl font-black text-white leading-none">{globalTotalReal} / {globalTotalMeta}</p>
                            <div className="w-full bg-slate-800 rounded-full h-1.5 mt-2">
                                <div className={`h-full rounded-full transition-all ${globalPct >= (settings?.threshold_green || 90) ? 'bg-emerald-500' : globalPct >= (settings?.threshold_amber || 75) ? 'bg-amber-500' : 'bg-red-500'}`}
                                    style={{ width: `${Math.min(globalPct, 100)}%` }} />
                            </div>
                        </div>
                        {!isVisor && (
                            <button onClick={handleSave}
                                className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all active:scale-95">
                                <Save size={14} /> GUARDAR TODO
                            </button>
                        )}
                        <button onClick={onClose}
                            className="p-3 bg-slate-800 hover:bg-slate-700 text-white/60 hover:text-white rounded-xl transition-all">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Grid of ALL periods */}
                <div className="p-4 md:p-6 max-h-[75vh] overflow-y-auto scrollbar-hide">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                        {ind.history.map((h: any, idx: number) => {
                            const acts = getPerElements(idx);
                            const pMeta = getPerMeta(idx);
                            const pReal = getPerReal(idx);
                            const pp = pMeta > 0 ? Math.round((pReal / pMeta) * 100) : 0;
                            const isCurrent = h.period?.toUpperCase() === currentPeriodLabel?.toUpperCase();
                            const isExpanded = expandedPeriod === idx;
                            const barColor = pp >= (settings?.threshold_green || 90) ? 'bg-emerald-500' : pp >= (settings?.threshold_amber || 75) ? 'bg-amber-500' : pp > 0 ? 'bg-red-500' : 'bg-slate-700';
                            const borderColor = isExpanded ? 'border-violet-500 shadow-lg shadow-violet-500/10' : isCurrent ? 'border-emerald-500/40' : 'border-slate-800';

                            return (
                                <button
                                    key={idx}
                                    onClick={() => setExpandedPeriod(isExpanded ? null : idx)}
                                    className={cn(
                                        "relative bg-slate-900/80 border rounded-2xl p-4 text-left transition-all hover:border-violet-500/50 group",
                                        borderColor
                                    )}
                                >
                                    {isCurrent && <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                                    <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-2 group-hover:text-violet-400 transition-colors">{h.period?.toUpperCase()}</p>
                                    <p className="text-lg font-black text-white leading-none">{pReal}<span className="text-slate-600 text-sm">/{pMeta}</span></p>
                                    <div className="w-full bg-slate-800 rounded-full h-1.5 mb-2">
                                        <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${Math.min(pp, 100)}%` }} />
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[8px] text-slate-600 font-bold">{acts.length} elm.</span>
                                        <span className={cn("text-[9px] font-black", pp >= (settings?.threshold_green || 90) ? 'text-emerald-400' : pp >= (settings?.threshold_amber || 75) ? 'text-amber-400' : pp > 0 ? 'text-red-400' : 'text-slate-600')}>{pp}%</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Expanded period detail */}
                    {expandedPeriod !== null && (() => {
                        const acts = getPerElements(expandedPeriod);
                        const filtered = search ? acts.filter(a => a.nombre.toLowerCase().includes(search.toLowerCase())) : acts;
                        const pMeta = getPerMeta(expandedPeriod);
                        const pReal = getPerReal(expandedPeriod);
                        const pp = pMeta > 0 ? Math.round((pReal / pMeta) * 100) : 0;
                        const h = ind.history[expandedPeriod];

                        return (
                            <div className="bg-slate-900/70 border border-violet-500/30 rounded-2xl overflow-hidden">
                                {/* Period Header */}
                                <div className="px-6 py-4 border-b border-slate-800/60 flex flex-wrap gap-4 items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => setExpandedPeriod(expandedPeriod > 0 ? expandedPeriod - 1 : null)}
                                            disabled={expandedPeriod === 0}
                                            className="p-1.5 border border-slate-800 rounded-lg text-white/60 hover:text-white hover:bg-slate-800 disabled:opacity-30 transition-colors">
                                            <ChevronLeft size={16} />
                                        </button>
                                        <h3 className="text-lg font-black text-white uppercase tracking-tighter">
                                            {getPerLabel(expandedPeriod)} -  {filtered.length} Elementos
                                        </h3>
                                        <button onClick={() => setExpandedPeriod(expandedPeriod < ind.history.length - 1 ? expandedPeriod + 1 : null)}
                                            disabled={expandedPeriod >= ind.history.length - 1}
                                            className="p-1.5 border border-slate-800 rounded-lg text-white/60 hover:text-white hover:bg-slate-800 disabled:opacity-30 transition-colors">
                                            <ChevronRight size={16} />
                                        </button>
                                        <span className={cn("text-xs font-black ml-2", pp >= (settings?.threshold_green || 90) ? 'text-emerald-400' : pp >= (settings?.threshold_amber || 75) ? 'text-amber-400' : 'text-red-400')}>{pp}%</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" size={12} />
                                            <input
                                                type="text"
                                                placeholder="Buscar..."
                                                className="bg-slate-950 border border-slate-800 rounded-xl py-2 pl-8 pr-3 text-[10px] text-slate-300 outline-none focus:border-violet-500/50 w-40"
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                            />
                                        </div>
                                        {!isVisor && (
                                            <>
                                                <button onClick={() => setShowMasiva(!showMasiva)}
                                                    className="px-4 py-2 border border-violet-500/30 text-violet-400 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-violet-500/10 transition-all flex items-center gap-1.5">
                                                    <Zap size={11} /> MASIVA
                                                </button>
                                                <button onClick={() => copyToAllPeriods(expandedPeriod)}
                                                    className="px-4 py-2 border border-cyan-500/30 text-cyan-400 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-cyan-500/10 transition-all flex items-center gap-1.5"
                                                    title="Copiar elementos a todos los periodos">
                                                    <Database size={11} /> COPIAR A TODOS
                                                </button>
                                                <button onClick={() => addElement(expandedPeriod)}
                                                    className="px-4 py-2 bg-slate-800 text-slate-300 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-slate-700 transition-all flex items-center gap-1.5">
                                                    <Plus size={11} /> NUEVO
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Inconsistency warning */}
                                {h && h.real !== pReal && (
                                    <div className="mx-6 mt-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
                                        <AlertTriangle className="text-amber-500 shrink-0" size={18} />
                                        <p className="text-xs text-amber-200/90 font-medium">
                                            El total guardado no coincide con el checklist. Revise y guarde el checklist.
                                        </p>
                                    </div>
                                )}

                                {/* Masiva panel */}
                                {showMasiva && (
                                    <div className="mx-6 my-3 bg-slate-950 border border-violet-500/20 rounded-xl p-4">
                                        <p className="text-[9px] text-white/70 mb-2">Pega elementos (uno por línea). Se añadirán con meta = 1.</p>
                                        <textarea
                                            className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-300 outline-none focus:border-violet-500/50 resize-none font-mono"
                                            rows={4}
                                            placeholder={"Elemento 1\nElemento 2\n..."}
                                            value={masivaText}
                                            onChange={e => setMasivaText(e.target.value)}
                                        />
                                        <div className="flex justify-end gap-2 mt-2">
                                            <button onClick={() => setShowMasiva(false)} className="px-4 py-1.5 text-white/70 font-black text-[8px] uppercase tracking-widest">CANCELAR</button>
                                            <button onClick={handleMasiva} className="px-6 py-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-black text-[8px] uppercase tracking-widest">AÑADIR</button>
                                        </div>
                                    </div>
                                )}

                                {/* Activities list */}
                                <div className="px-4 md:px-6 pb-4 space-y-2 max-h-[40vh] overflow-y-auto scrollbar-hide">
                                    {filtered.length === 0 ? (
                                        <div className="text-center py-10 text-slate-600 font-black uppercase text-[10px] tracking-widest">
                                            {search ? 'Sin resultados' : 'Sin elementos -  Agrega con NUEVO o MASIVA'}
                                        </div>
                                    ) : filtered.map(act => {
                                        const aPct = act.meta > 0 ? Math.round((act.realizado / act.meta) * 100) : 0;
                                        return (
                                            <div key={act.id} className="bg-slate-950/60 border border-slate-800/80 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                                {/* Name */}
                                                <div className="flex-1 min-w-0">
                                                    <input
                                                        type="text"
                                                        className="w-full bg-transparent text-white text-xs font-bold outline-none placeholder:text-slate-600 disabled:opacity-50"
                                                        placeholder="Nombre del elemento..."
                                                        value={act.nombre}
                                                        onChange={e => setElementField(expandedPeriod, act.id, 'nombre', e.target.value)}
                                                        disabled={isVisor}
                                                    />
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <div className="flex-1 bg-slate-800 rounded-full h-1">
                                                            <div className={cn("h-full rounded-full transition-all", aPct >= (settings?.threshold_green || 90) ? 'bg-emerald-500' : aPct >= (settings?.threshold_amber || 75) ? 'bg-amber-500' : 'bg-red-500')}
                                                                style={{ width: `${Math.min(aPct, 100)}%` }} />
                                                        </div>
                                                        <span className="text-[8px] text-white/70 font-black w-10 text-right">{aPct}%</span>
                                                    </div>
                                                </div>
                                                {/* Meta */}
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[7px] text-white/70 font-black uppercase">META</span>
                                                    <div className="flex items-center bg-slate-900 border border-slate-700 rounded-lg px-1">
                                                        {!isVisor && <button onClick={() => updateElement(expandedPeriod, act.id, 'meta', -1)} className="w-6 h-6 flex items-center justify-center text-white/60 hover:text-white text-sm font-black">-</button>}
                                                        <input type="number" min="0" className="w-10 text-center bg-transparent text-white font-black text-xs outline-none disabled:opacity-50"
                                                            value={act.meta} onChange={e => setElementField(expandedPeriod, act.id, 'meta', parseInt(e.target.value) || 0)} disabled={isVisor} />
                                                        {!isVisor && <button onClick={() => updateElement(expandedPeriod, act.id, 'meta', 1)} className="w-6 h-6 flex items-center justify-center text-white/60 hover:text-white text-sm font-black">+</button>}
                                                    </div>
                                                </div>
                                                {/* Realizado */}
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[7px] text-violet-400 font-black uppercase">REAL</span>
                                                    <div className="flex items-center bg-violet-950/30 border border-violet-700/30 rounded-lg px-1">
                                                        {!isVisor && <button onClick={() => updateElement(expandedPeriod, act.id, 'realizado', -1)} className="w-6 h-6 flex items-center justify-center text-violet-400 hover:text-white text-sm font-black">-</button>}
                                                        <input type="number" min="0" className="w-10 text-center bg-transparent text-violet-300 font-black text-xs outline-none disabled:opacity-50"
                                                            value={act.realizado} onChange={e => setElementField(expandedPeriod, act.id, 'realizado', parseInt(e.target.value) || 0)} disabled={isVisor} />
                                                        {!isVisor && <button onClick={() => updateElement(expandedPeriod, act.id, 'realizado', 1)} className="w-6 h-6 flex items-center justify-center text-violet-400 hover:text-white text-sm font-black">+</button>}
                                                    </div>
                                                </div>
                                                {/* Delete */}
                                                {!isVisor && (
                                                    <button onClick={() => removeElement(expandedPeriod, act.id)}
                                                        className="p-1.5 text-slate-700 hover:text-red-400 transition-all rounded-lg hover:bg-red-950/20">
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        );
    };

    // --- Sub-View: Bulk Capture Management (Image 4 style - Cards Premium) ---
    const BulkCaptureView = ({ ind, onClose }: { ind: any, onClose: () => void }) => {
        const currentPeriodLabel = getCurrentPeriod(ind.frec);
        const getCardBorder = (h: any) => {
            if (h.meta === 0 && h.real === 0) return 'border-slate-800/60';
            const pct2 = h.meta > 0 ? (ind.tipo === 'Minimizar' ? Math.round((h.meta / (h.real || 1)) * 100) : Math.round((h.real / h.meta) * 100)) : 0;
            if (pct2 >= (settings?.threshold_green || 90)) return 'border-emerald-500/40';
            if (pct2 >= (settings?.threshold_amber || 75)) return 'border-amber-500/30';
            return 'border-red-500/20';
        };
        const getPct = (h: any) => {
            if (h.meta <= 0) return 0;
            return ind.tipo === 'Minimizar' ? Math.round((h.meta / (h.real || 1)) * 100) : Math.round((h.real / h.meta) * 100);
        };
        const getBarColor = (pct2: number) => pct2 >= (settings?.threshold_green || 90) ? 'bg-emerald-500' : pct2 >= (settings?.threshold_amber || 75) ? 'bg-amber-500' : 'bg-red-500';
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-6 bg-slate-950/95 backdrop-blur-xl">
                <div className="bg-[#080C14] border border-slate-800 w-full max-w-7xl max-h-[94vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden">
                    <header className="px-8 py-5 border-b border-slate-800 flex justify-between items-center shrink-0 bg-slate-900/40">
                        <div className="flex items-center gap-5">
                            <div className="w-11 h-11 bg-cyan-500/10 rounded-2xl flex items-center justify-center border border-cyan-500/20">
                                <BarChart3 size={20} className="text-cyan-400" />
                            </div>
                            <div>
                                <p className="text-[9px] text-cyan-400 font-black uppercase tracking-[0.25em] mb-0.5">CAPTURAR DATOS Y NOTAS</p>
                                <h2 className="text-lg font-black text-white uppercase tracking-tight leading-none">{ind.nombre}</h2>
                            </div>
                            <span className="px-3 py-1 bg-slate-800 text-white/60 border border-slate-700 rounded-full text-[9px] font-black uppercase tracking-widest">
                                AÑO {new Date().getFullYear()}
                            </span>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={onClose} className="px-5 py-2.5 bg-slate-800 text-white/60 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:text-white transition-all">Cancelar</button>
                            <button onClick={() => { handleSaveIndicators(); onClose(); }}
                                className="px-7 py-2.5 bg-emerald-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-500 shadow-xl shadow-emerald-500/20 transition-all flex items-center gap-2">
                                <Save size={14} /> Guardar
                            </button>
                        </div>
                    </header>
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                            {ind.history.map((h: any, idx: number) => {
                                const isCurrentPeriod = h.period === currentPeriodLabel;
                                const pctPeriod = getPct(h);
                                const hasData = h.meta > 0 || h.real > 0;
                                const hasChecklist = (h.elements && h.elements.length > 0) || (h.activities && h.activities.length > 0);
                                return (
                                    <div key={idx} className={cn(
                                        'bg-slate-900/70 border rounded-2xl p-4 transition-all hover:border-slate-600',
                                        getCardBorder(h),
                                        isCurrentPeriod && 'ring-2 ring-cyan-500/50 shadow-lg shadow-cyan-500/10'
                                    )}>
                                        <div className="flex justify-between items-start mb-3">
                                            <span className={cn('text-[10px] font-black uppercase tracking-wider', isCurrentPeriod ? 'text-cyan-400' : 'text-white/60')}>
                                                {h.period}
                                            </span>
                                            {isCurrentPeriod && (
                                                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" title="Foco actual" />
                                            )}
                                            {hasData && !isCurrentPeriod && (
                                                <span className={cn('text-[8px] font-black', pctPeriod >= (settings?.threshold_green || 90) ? 'text-emerald-400' : pctPeriod >= (settings?.threshold_amber || 75) ? 'text-amber-400' : 'text-red-400')}>
                                                    {pctPeriod}%
                                                </span>
                                            )}
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div>
                                                <label className="text-[7px] text-slate-600 font-black uppercase mb-1 block">META</label>
                                                <input type="number"
                                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-2 text-white text-xs font-black outline-none focus:border-cyan-500/50 transition-colors text-center"
                                                    value={h.meta}
                                                    onChange={e => updateIndicatorHistory(ind.id, idx, 'meta', parseFloat(e.target.value) || 0)}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[7px] text-cyan-500/70 font-black uppercase mb-1 block">REAL</label>
                                                {hasChecklist ? (
                                                    <div className="flex flex-col gap-1">
                                                        <div className="w-full bg-slate-950/40 border border-slate-800 rounded-xl px-2.5 py-2 text-slate-500 text-xs font-black text-center select-none">
                                                            {h.real}
                                                        </div>
                                                        <p className="text-[9px] text-violet-400 font-bold leading-tight mt-1 text-center">
                                                            El avance se calcula desde el checklist
                                                        </p>
                                                        <button 
                                                            type="button"
                                                            onClick={() => {
                                                                setChecklistOpen({ indId: ind.id, periodIdx: idx });
                                                                onClose();
                                                            }}
                                                            className="text-[9px] text-cyan-400 hover:text-cyan-300 underline font-black uppercase tracking-wider mt-1 text-center cursor-pointer transition-colors"
                                                        >
                                                            Editar checklist
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <input type="number"
                                                        className="w-full bg-cyan-500/5 border border-cyan-500/20 rounded-xl px-2.5 py-2 text-cyan-400 text-xs font-black outline-none focus:border-cyan-500/50 transition-colors text-center"
                                                        value={h.real}
                                                        onChange={e => updateIndicatorHistory(ind.id, idx, 'real', parseFloat(e.target.value) || 0, true)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        {hasData && (
                                            <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden mb-3">
                                                <div className={cn('h-full rounded-full transition-all', getBarColor(pctPeriod))}
                                                    style={{ width: `${Math.min(pctPeriod, 100)}%` }} />
                                            </div>
                                        )}
                                        <textarea
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-2 text-white/70 text-[9px] font-medium outline-none focus:border-slate-600 transition-colors resize-none"
                                            placeholder="Análisis..."
                                            rows={2}
                                            value={h.note || ''}
                                            onChange={e => updateIndicatorHistory(ind.id, idx, 'note', e.target.value, true)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };

    // --- Sub-View: Individual Indicator Detail (Premium Redesign) ---
    const IndicatorDetailView = ({ ind, initialPeriod }: { ind: any; initialPeriod?: string | null }) => {
        const [chartMode, setChartMode] = useState<'vencidos' | 'anual'>('vencidos');
        const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod || getCurrentPeriod(ind.frec));
        const isOpenCurrentPeriod = ind.frec === 'Mensual'
            && selectedPeriod === getCurrentPeriod(ind.frec)
            && new Date().getFullYear() === 2026;

        const currentIdx = ind.history.findIndex((h: any) => h.period === selectedPeriod) !== -1
            ? ind.history.findIndex((h: any) => h.period === selectedPeriod)
            : ind.history.length - 1;

        // Periodo anterior al Seleccionado
        const prevIdx = currentIdx > 0 ? currentIdx - 1 : null;
        const prevPeriod = prevIdx !== null ? ind.history[prevIdx] : null;

        const h = ind.history[currentIdx];
        const { totalMeta: canonicalTotalMeta, totalReal: canonicalTotalReal, pct } = calculateStats(ind, selectedPeriod, selectedPeriod);
        const hasChecklist = h && ((h.elements && h.elements.length > 0) || (h.activities && h.activities.length > 0));
        const hasSelectedTarget = parseNumberSafe(h?.meta, 0) > 0;
        const hasSelectedCapture = hasRealCaptureEvidence(h);


        const brechaValue = ind.tipo === 'Minimizar' ? (h.real - h.meta) : (h.meta - h.real);
        const isOverMeta = ind.tipo === 'Minimizar' ? h.real <= h.meta : h.real >= h.meta;
        const isOpenCurrentShortfall = isOpenCurrentPeriod && hasSelectedTarget && !isOverMeta;
        const openPeriodStatusLabel = hasSelectedCapture ? 'AVANCE PROVISIONAL' : 'SIN CAPTURA AÚN';
        const displayPct = isOpenCurrentShortfall ? null : pct;

        const getPeriodPct = (hp: any) => {
            if (hp.meta <= 0) return 0;
            return ind.tipo === 'Minimizar'
                ? Math.round((hp.meta / (hp.real || 1)) * 100)
                : Math.round((hp.real / hp.meta) * 100);
        };

        const prevPct = prevPeriod ? getPeriodPct(prevPeriod) : null;
        const semaforo = (() => {
            if (isOpenCurrentShortfall) return 'yellow';
            const state = getComplianceVisualState(pct, settings).semanticState;
            if (state === 'green') return 'green';
            if (state === 'amber') return 'yellow';
            return 'red';
        })();

        // Datos del gráfico: solo periodos transcurridos hasta el Seleccionado
        const chartData = ind.history.map((hp: any, i: number) => ({
            name: hp.period,
            meta: hp.meta,
            real: hp.real,
            isCurrent: i === currentIdx,
        })).filter((hp: any, i: number) => chartMode === 'anual' ? true : i < currentIdx);

        const semaforoColors: Record<string, string> = {
            green: '#10b981', yellow: '#f59e0b', red: '#ef4444', none: '#64748b'
        };

        return (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">

                {/* CABECERA */}
                <div className="bg-[#080C14] border border-slate-800 rounded-[2rem] p-6 shadow-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 to-transparent pointer-events-none" />
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-6 relative z-10">
                        <div className="flex items-center gap-4">
                            <button onClick={() => { setActiveIndicatorId(null); setActiveIndicatorPeriod(null); setIsBulkCapture(false); }}
                                className="p-3 bg-slate-800 text-white/60 hover:text-white rounded-2xl transition-all active:scale-95">
                                <ChevronLeft size={20} />
                            </button>
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <select
                                        className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full text-[9px] font-black uppercase tracking-widest px-3 py-1 outline-none cursor-pointer"
                                        value={selectedPeriod}
                                        onChange={(e) => setSelectedPeriod(e.target.value)}
                                    >
                                        {MONTHS.map(m => <option key={m} value={m} className="bg-slate-900 text-white">{m} 2026</option>)}
                                    </select>

                                    {prevPeriod && (
                                        <span className={cn('px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border',
                                            semaforo === 'red' ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                : semaforo === 'yellow' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20')}>
                                            TENDENCIA {prevPeriod.period}: {prevPct}%
                                        </span>
                                    )}
                                </div>
                                <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter leading-tight break-words">{ind.nombre}</h2>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            {ind.modo === 'checklist' && (
                                <span className="px-3 py-1 bg-violet-500/10 text-violet-400 border border-violet-500/20 rounded-full text-[9px] font-black uppercase tracking-widest">
                                    MODO CHECKLIST
                                </span>
                            )}
                            {getModulePermission(user, 'Seguimiento') && (ind.modo === 'checklist' ? (
                                <button onClick={() => setChecklistOpen({ indId: ind.id, periodIdx: currentIdx })}
                                    className="px-6 py-2.5 bg-violet-900/40 border border-violet-500/40 text-violet-300 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:border-violet-400 hover:text-white transition-all flex items-center gap-2">
                                    <CheckSquare size={14} /> {canEditModule(user, 'Seguimiento') ? 'Gestiónar Elementos' : 'Ver Elementos'}
                                </button>
                            ) : (
                                canEditModule(user, 'Seguimiento') && (
                                    <button onClick={() => setIsBulkCapture(true)}
                                        className="px-6 py-2.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:border-cyan-500/50 hover:text-white transition-all flex items-center gap-2">
                                        <BarChart3 size={14} /> Gestiónar Metas
                                    </button>
                                )
                            ))}
                        </div>
                    </div>

                    {/* GRID PRINCIPAL */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 relative z-10">

                        {/* COL 1: Captura del periodo */}
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[9px] text-white/70 font-black uppercase mb-1.5 block">META {ind.frec === 'Mensual' ? 'MES' : 'SEM'}</label>
                                    <div className="relative">
                                        <input type="number"
                                            className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-4 py-4 text-2xl font-black text-white outline-none focus:border-emerald-500/50 shadow-inner transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            value={h.meta}
                                            onChange={e => updateIndicatorHistory(ind.id, currentIdx, 'meta', parseFloat(e.target.value) || 0)}
                                            disabled={!canEditModule(user, 'Seguimiento')}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600 text-[10px] font-black">
                                            {ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[9px] text-cyan-400 font-black uppercase mb-1.5 block">REAL {ind.frec === 'Mensual' ? 'MES' : 'SEM'}</label>
                                    <div className="relative">
                                        <input type="number"
                                            className="w-full bg-cyan-500/5 border border-cyan-500/20 rounded-2xl px-4 py-4 text-2xl font-black text-cyan-300 outline-none focus:border-cyan-500/50 shadow-inner transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            value={h.real}
                                            onChange={e => updateIndicatorHistory(ind.id, currentIdx, 'real', parseFloat(e.target.value) || 0, true)}
                                            disabled={!canEditModule(user, 'Seguimiento') || hasChecklist}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-700 text-[10px] font-black">
                                            {ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}
                                        </span>
                                    </div>
                                    {hasChecklist && (
                                        <div className="mt-1.5 flex flex-col gap-1">
                                            <p className="text-[10px] text-violet-400 font-bold leading-tight">
                                                El avance se calcula desde el checklist
                                            </p>
                                            <button 
                                                type="button"
                                                onClick={() => setChecklistOpen({ indId: ind.id, periodIdx: currentIdx })}
                                                className="text-[10px] text-cyan-400 hover:text-cyan-300 underline font-black uppercase tracking-wider text-left cursor-pointer transition-colors"
                                            >
                                                Editar checklist
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-[9px] text-white/70 font-black uppercase mb-1.5 block">OBSERVACIONES DEL PERIODO</label>
                                <textarea
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-sm font-medium text-white/60 placeholder:text-slate-700 focus:outline-none focus:border-emerald-500/30 transition-all resize-none min-h-[90px] shadow-inner disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="Análisis de resultados..."
                                    value={h.note || ''}
                                    onChange={e => updateIndicatorHistory(ind.id, currentIdx, 'note', e.target.value, true)}
                                    disabled={!canEditModule(user, 'Seguimiento')}
                                />
                            </div>
                            {canEditModule(user, 'Seguimiento') ? (
                                <button onClick={() => handleSaveIndicators()}
                                    className="w-full py-4 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-widest hover:opacity-90 shadow-xl shadow-emerald-500/20 transition-all flex items-center justify-center gap-3 active:scale-[0.98]">
                                    <Save size={16} /> GUARDAR {ind.frec === 'Mensual' ? 'MES' : 'SEMANA'}
                                </button>
                            ) : (
                                <div className="w-full py-3 bg-slate-800/50 border border-slate-700/50 text-white/70 rounded-2xl font-black text-[10px] uppercase tracking-widest text-center flex items-center justify-center gap-2">
                                    <Eye size={14} /> MODO VISOR • SOLO LECTURA
                                </div>
                            )}
                        </div>

                        {/* COL 2: Gráfica HD */}
                        <div className="lg:col-span-2 bg-slate-950/60 rounded-2xl border border-slate-800/60 p-5">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex flex-col gap-1">
                                    <p className="text-[9px] text-white/70 font-black uppercase tracking-[0.2em]">TENDENCIA DE {chartMode === 'vencidos' ? 'PERIODOS CERRADOS' : 'VISTA ANUAL'}</p>
                                    <p className="text-[8px] text-slate-700 font-black uppercase tracking-widest">(línea punteada = meta)</p>
                                </div>
                                <div className="flex bg-slate-900 border border-slate-800 rounded-xl p-1">
                                    <button onClick={() => setChartMode('vencidos')} className={cn("px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all", chartMode === 'vencidos' ? "bg-cyan-500/20 text-cyan-400" : "text-white/70 hover:text-slate-300")}>Transcurridos</button>
                                    <button onClick={() => setChartMode('anual')} className={cn("px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all", chartMode === 'anual' ? "bg-cyan-500/20 text-cyan-400" : "text-white/70 hover:text-slate-300")}>Anual</button>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                                <ComposedChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                    <defs>
                                        <linearGradient id="gradRealHD" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.35} />
                                            <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="2 6" stroke="#1e293b" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 9, fontWeight: 800 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#475569', fontSize: 9 }} axisLine={false} tickLine={false} width={32} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0B1120', border: '1px solid #1e293b', borderRadius: '12px', fontSize: '11px', color: '#fff' }}
                                        labelStyle={{ color: '#94a3b8', fontWeight: 800, fontSize: '10px' }}
                                        formatter={(val: any, name: string) => [val, name === 'real' ? 'Real' : 'Meta']}
                                    />
                                    <Area type="monotone" dataKey="real" stroke="#06b6d4" strokeWidth={2.5} fill="url(#gradRealHD)" dot={false} activeDot={{ r: 4, fill: '#06b6d4' }} />
                                    <Line type="monotone" dataKey="meta" stroke="#94a3b8" strokeWidth={2.5} strokeDasharray="4 6" dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* FILA INFERIOR: Desglose periodos + Estadísticas */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                    {/* Desglose por periodo con semáforo */}
                    <div className="lg:col-span-2 bg-[#080C14] border border-slate-800 rounded-[2rem] p-6 shadow-xl">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-[10px] text-white/60 font-black uppercase tracking-[0.2em]">Desglose de Cumplimiento {ind.frec === 'Mensual' ? 'Mensual' : 'por Semana'}</h3>
                            <BarChart3 size={14} className="text-slate-600" />
                        </div>
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                            {ind.history.map((period: any, idx: number) => {
                                const pp = getPeriodPct(period);
                                const isExp = idx < currentIdx; // vencido
                                const isCur = idx === currentIdx;
                                const periodHasCapture = hasRealCaptureEvidence(period);
                                const periodIsOverMeta = ind.tipo === 'Minimizar' ? period.real <= period.meta : period.real >= period.meta;
                                const periodIsOpenCurrent = isCur && isOpenCurrentPeriod && parseNumberSafe(period.meta, 0) > 0;
                                const periodIsOpenShortfall = periodIsOpenCurrent && !periodIsOverMeta;
                                const visual = periodIsOpenShortfall ? null : getComplianceVisualState(pp, settings);
                                const barColor = periodIsOpenShortfall ? (periodHasCapture ? 'bg-amber-500/40' : 'bg-slate-700') : visual!.bgClass;
                                const textColor = periodIsOpenShortfall ? (periodHasCapture ? 'text-amber-400' : 'text-slate-400') : visual!.textSoftClass;
                                const currentPeriodLabel = periodHasCapture ? 'AVANCE PROVISIONAL' : 'SIN CAPTURA AÚN';
                                return (
                                    <div key={idx} className={cn('transition-all', !isExp && !isCur ? 'opacity-30' : '')}>
                                        <div className="flex justify-between items-center mb-1">
                                            <div className="flex items-center gap-2">
                                                <span className={cn('text-[10px] font-black uppercase', isCur ? (periodIsOpenShortfall ? (periodHasCapture ? 'text-amber-400' : 'text-slate-300') : 'text-emerald-400') : isExp ? textColor : 'text-white/70')}>
                                                    {period.period}
                                                </span>
                                                {isCur && <span className={cn("text-[8px] font-black", periodIsOpenShortfall ? (periodHasCapture ? "text-amber-400/70" : "text-slate-400") : "text-emerald-400/60")}>{currentPeriodLabel}</span>}
                                                {evaluateDashboardObligation({indicator: ind, entry: period, monthIndex: idx, year: DASHBOARD_CYCLE_YEAR}) && <span className="text-[9px] text-amber-300 font-semibold">Captura vencida</span>}
                                                {isExp && periodHasCapture && <span className="text-[9px] text-slate-300">Capturado</span>}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-[9px] text-slate-600">{(periodHasCapture || isExp) ? `${period.real} / ${period.meta}` : `— / ${period.meta}`}</span>
                                                {(isExp || isCur) && period.meta > 0 && (
                                                    <span className={cn('text-[10px] font-black', textColor)}>{periodIsOpenShortfall ? (periodHasCapture ? 'PROV.' : 'EN CURSO') : `${pp}%`}</span>
                                                )}
                                                {ind.modo === 'checklist' && (
                                                    <button
                                                        onClick={() => setChecklistOpen({ indId: ind.id, periodIdx: idx })}
                                                        className="bg-violet-900/50 hover:bg-violet-500 text-violet-300 hover:text-white p-1.5 rounded-lg transition-colors border border-violet-500/30 hover:border-violet-400"
                                                        title={`Gestiónar elementos de ${period.period}`}
                                                    >
                                                        <CheckSquare size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {(isExp || isCur) && period.meta > 0 && (
                                            <div className="h-2 w-full bg-slate-900 rounded-full overflow-hidden">
                                                <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${periodIsOpenShortfall ? Math.max(8, Math.min(pp, 100)) : Math.min(pp, 100)}%` }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Stats generales */}
                    <div className="bg-[#080C14] border border-slate-800 rounded-[2rem] p-6 shadow-xl space-y-4">
                        <h3 className="text-[10px] text-white/60 font-black uppercase tracking-[0.2em] mb-2">Resumen General · acumulado de periodos cerrados</h3>
                        <div className="flex items-center gap-4">
                            <div className="relative w-20 h-20">
                                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                    <circle cx="50" cy="50" r="40" strokeWidth="10" fill="none" stroke="#1e293b" />
                                    {pct !== null && (
                                        <circle cx="50" cy="50" r="40" strokeWidth="10" fill="none"
                                            stroke={getComplianceVisualState(pct, settings).semanticState === 'red' ? semaforoColors.red : getComplianceVisualState(pct, settings).semanticState === 'amber' ? semaforoColors.yellow : semaforoColors.green}
                                            strokeDasharray={251.2}
                                            strokeDashoffset={251.2 - (251.2 * Math.min(pct, 100)) / 100}
                                            strokeLinecap="round"
                                        />
                                    )}
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className={cn("text-xl font-black", getComplianceVisualState(pct, settings).textClass)}>
                                        {pct}%
                                    </span>
                                </div>
                            </div>
                            <div className="flex-1 space-y-2">
                                <div className="flex justify-between text-[9px]">
                                    <span className="text-white/70 font-black uppercase">Tipo de Meta:</span>
                                    <span className="text-white font-black">{ind.tipo}</span>
                                </div>
                                <div className="flex justify-between text-[9px]">
                                    <span className="text-white/70 font-black uppercase">Meta anual configurada:</span>
                                    <span className="text-white font-black">{ind.metaAnual} {ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}</span>
                                </div>
                                <div className="flex justify-between text-[9px]">
                                    <span className="text-white/70 font-black uppercase">Real acumulado cerrado:</span>
                                    <span className="text-white font-black">{canonicalTotalReal} {ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}</span>
                                </div>
                                <div className="flex justify-between text-[9px]">
                                    <span className="text-white/70 font-black uppercase">Cumplimiento acumulado:</span>
                                    <span className={cn('font-black', pct >= (settings?.threshold_green || 90) ? 'text-emerald-400' : pct >= (settings?.threshold_amber || 75) ? 'text-amber-400' : 'text-red-400')}>
                                        {pct}% · {canonicalTotalReal}/{canonicalTotalMeta}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* Brecha */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                            <p className="text-[8px] text-white/70 font-black uppercase mb-2">{isOpenCurrentShortfall ? (hasSelectedCapture ? 'BRECHA PROVISIONAL DEL PERIODO' : 'META DEL PERIODO EN CURSO') : 'BRECHA PERIODO ACTUAL'}</p>
                            <div className="flex items-baseline gap-2">
                                <span className={cn('text-3xl font-black', isOpenCurrentShortfall ? (hasSelectedCapture ? 'text-amber-400' : 'text-slate-300') : brechaValue <= 0 ? 'text-emerald-500' : 'text-red-500')}>
                                    {isOpenCurrentShortfall && !hasSelectedCapture ? '—' : (brechaValue > 0 ? `+${brechaValue}` : brechaValue)}
                                </span>
                                <span className="text-slate-600 text-[10px] font-black">
                                    {ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}
                                </span>
                            </div>
                            <p className="text-[8px] mt-1 font-black uppercase"
                                style={{ color: isOpenCurrentShortfall ? (hasSelectedCapture ? '#f59e0b' : '#94a3b8') : isOverMeta ? '#10b981' : '#ef4444' }}>
                                {isOpenCurrentShortfall ? `${openPeriodStatusLabel} · PERIODO ABIERTO` : isOverMeta ? "✅ CUMPLIENDO META" : "POR DEBAJO DE META"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* PROTOCOLO DE ACCIÓN */}
                <IndicatorProtocol ind={ind} semaforo={semaforo} prevPeriod={prevPeriod} prevPct={prevPct ?? 0} openPeriod={isOpenCurrentPeriod || !hasSelectedTarget} />

            </motion.div>
        );
    };
    return (
        <div className="flex flex-col lg:flex-row gap-4 pb-32 font-['Inter']">
            {loading ? (
                <div className="w-full flex items-center justify-center p-20 text-center text-emerald-500 font-black animate-pulse uppercase">
                    Cargando Seguimiento...
                </div>
            ) : scopedReadError ? (
                <div className="w-full flex items-center justify-center p-8">
                    <div className="max-w-xl w-full p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
                        <AlertTriangle className="mx-auto text-red-400" size={36} />
                        <h2 className="text-red-200 font-black uppercase tracking-widest">Fallo de lectura de Seguimiento</h2>
                        <p className="text-white/70 text-sm">{scopedReadError}</p>
                        <p className="text-white/40 text-xs">No se mostraran N/A, cero planes ni SIN INDICADORES como sustituto del error.</p>
                        {selectedInstancia && (
                            <button onClick={() => refreshIndicators()} className="px-5 py-2 rounded-xl bg-red-500 text-white text-xs font-black uppercase tracking-widest">Reintentar</button>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {/* Sidebar de Navegacion Lateral - Desplegable */}
                    {showEcosistema && !isInstanceScoped && (
                        <aside className="w-full lg:w-72 space-y-3 no-print shrink-0 transition-all">
                            <header className="bg-slate-900 border border-slate-800 p-4 rounded-xl shadow-xl">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-sm font-black text-white uppercase tracking-tighter flex items-center gap-2">
                                        <Building2 className="text-emerald-500" size={16} />
                                        Ecosistema
                            </h2>
                            <button onClick={() => setShowEcosistema(false)} className="text-white/70 hover:text-white transition-all p-1 rounded-lg hover:bg-slate-800">
                                <X size={14} />
                            </button>
                        </div>
                        <div className="space-y-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" size={12} />
                                <input
                                    type="text"
                                    placeholder="BUSCAR..."
                                    className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 pl-8 pr-3 text-white text-[9px] font-black uppercase tracking-widest focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {['TODOS', ...INSTANCE_TYPES].map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTypeFilter(t as any)}
                                        className={cn(
                                            "px-3 py-1.5 rounded-lg font-black text-[8px] uppercase tracking-widest transition-all border",
                                            typeFilter === t
                                                ? "bg-emerald-600 border-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                                                : "bg-slate-950 border-slate-800 text-white/70 hover:text-slate-300"
                                        )}
                                    >
                                        {t === 'TODOS' ? 'TODO' : t === COMISION_TYPE ? 'COM' : t === 'COMITE' ? 'CMT' : 'CNS'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </header>

                    <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-2 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-hide space-y-1.5 font-black uppercase tracking-tighter">
                        {/* SECCIÓN: TABLERO NACIONAL (DIRECTOR NACIONAL / SA) */}
                        {isSAOrAdmin && !isVPScoped && !isInstanceScoped && (
                            <div className="mb-4">
                                <h3 className="text-[8px] font-black text-slate-500 uppercase px-2 mb-2 tracking-widest flex items-center gap-2">
                                    <ShieldCheck size={10} className="text-emerald-500" />
                                    Estrategia Nacional
                                </h3>
                                <button
                                    onClick={() => {
                                        setSelectedVPId(null);
                                        setSelectedInstancia(null);
                                        setActiveTab('TABLERO');
                                    }}
                                    className={cn(
                                        "w-full p-2.5 rounded-lg border transition-all text-left flex items-center gap-2.5 group relative overflow-hidden",
                                        activeTab === 'TABLERO'
                                            ? "bg-emerald-600 border-emerald-500 shadow-lg"
                                            : "bg-slate-950/50 border-slate-800 hover:border-slate-700 hover:bg-slate-900"
                                    )}
                                >
                                    <div className={cn(
                                        "w-7 h-7 rounded-md flex items-center justify-center shrink-0",
                                        activeTab === 'TABLERO' ? "bg-white/20 text-white" : "bg-slate-800 text-emerald-500"
                                    )}>
                                        <Trello size={14} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={cn(
                                            "text-[9px] font-black uppercase tracking-tighter leading-tight",
                                            activeTab === 'TABLERO' ? "text-white" : "text-white/70"
                                        )}>
                                            TABLERO COPARMEX
                                        </p>
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* Sección: Vicepresidencias */}
                        {hierarchy?.vicepresidencies && hierarchy.vicepresidencies.length > 0 && (
                            <div className="mb-4">
                                <button
                                    onClick={() => setIsVPsExpanded(!isVPsExpanded)}
                                    className="w-full flex items-center justify-between text-[8px] font-black text-slate-500 uppercase px-2 mb-2 tracking-widest hover:text-emerald-500 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <ChevronRight size={10} className={cn("text-emerald-500 transition-transform", isVPsExpanded && "rotate-90")} />
                                        Vicepresidencias
                                    </div>
                                    <span className="bg-slate-800 text-white/50 px-1.5 py-0.5 rounded-sm">
                                        {hierarchy.vicepresidencies.filter((vp: any) => !isVPScoped || vp.id === user?.vicepresidency_id).length}
                                    </span>
                                </button>
                                {isVPsExpanded && (
                                    <div className="space-y-1">
                                        {hierarchy.vicepresidencies
                                            .filter((vp: any) => !isVPScoped || vp.id === user?.vicepresidency_id)
                                            .map((vp: any) => (
                                            <button
                                                key={vp.id}
                                                onClick={() => {
                                                    if (selectedVPId === vp.id) {
                                                        // Toggle: deSeleccionar VP
                                                        setSelectedVPId(null);
                                                        setSelectedInstancia(null);
                                                        setActiveTab('TABLERO');
                                                    } else {
                                                        setSelectedVPId(vp.id);
                                                        setSelectedInstancia(null);
                                                        setActiveTab('VP_DASHBOARD');
                                                    }
                                                }}
                                                className={cn(
                                                    "w-full p-2.5 rounded-lg border transition-all text-left flex items-center gap-2.5 group relative overflow-hidden",
                                                    selectedVPId === vp.id && activeTab === 'VP_DASHBOARD'
                                                        ? "bg-slate-800 border-emerald-500 shadow-lg"
                                                        : "bg-slate-950/50 border-slate-800 hover:border-slate-700 hover:bg-slate-900"
                                                )}
                                            >
                                                <div className={cn(
                                                    "w-7 h-7 rounded-md flex items-center justify-center shrink-0",
                                                    selectedVPId === vp.id && activeTab === 'VP_DASHBOARD' ? "bg-emerald-600 text-white" : "bg-slate-800 text-white/50 group-hover:text-emerald-500"
                                                )}>
                                                    <Crown size={14} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={cn(
                                                        "text-[9px] font-black uppercase tracking-tighter leading-tight",
                                                        selectedVPId === vp.id && activeTab === 'VP_DASHBOARD' ? "text-white" : "text-slate-400 group-hover:text-white"
                                                    )}>
                                                        {vp.name}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-center justify-between px-2 mb-2">
                            <h3 className="text-[8px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                <ChevronRight size={10} className="text-emerald-500" />
                                Instancias {selectedVPId && <span className="text-emerald-400">({filteredInstances.length})</span>}
                            </h3>
                            {selectedVPId && (
                                <button
                                    onClick={() => { setSelectedVPId(null); setActiveTab('TABLERO'); }}
                                    className="text-[7px] font-black text-emerald-500 uppercase tracking-widest hover:text-emerald-400 transition-colors"
                                >
                                    Ver Todas
                                </button>
                            )}
                        </div>
                        {filteredInstances.length > 0 ? filteredInstances.map((c: any) => (
                            <button
                                key={c.id}
                                onClick={() => {
                                    setSelectedInstancia(c.id);
                                    if (activeTab === 'VP_DASHBOARD') setActiveTab('TABLERO');
                                }}
                                className={cn(
                                    "w-full p-2.5 rounded-lg border transition-all text-left flex items-center gap-2.5 group relative overflow-hidden",
                                    selectedInstancia === c.id
                                        ? "bg-emerald-600 border-emerald-500 shadow-lg shadow-emerald-900/40"
                                        : "bg-slate-950 border-slate-800 hover:border-slate-700 hover:bg-slate-900"
                                )}
                            >
                                <div className={cn(
                                    "w-7 h-7 rounded-md flex items-center justify-center shrink-0",
                                    selectedInstancia === c.id ? "bg-white/20 text-white" : "bg-slate-900 text-white/70 group-hover:text-emerald-500"
                                )}>
                                    <Building2 size={14} />
                                </div>
                                <div className="relative z-10 flex-1 min-w-0">
                                    <p className={cn(
                                        "text-[9px] font-black uppercase tracking-tighter leading-tight item-name font-black tracking-tight",
                                        selectedInstancia === c.id ? "text-white" : "text-slate-300 group-hover:text-white"
                                    )}>
                                        {c.name}
                                    </p>
                                    <span className={cn(
                                        "text-[7px] font-bold uppercase",
                                        selectedInstancia === c.id ? "text-emerald-100/70" : "text-slate-600"
                                    )}>
                                        {INSTANCE_TYPE_LABELS[c.type] || c.type}
                                    </span>
                                </div>
                            </button>
                        )) : (
                            <div className="p-4 text-center text-slate-600 text-[9px] font-black uppercase tracking-widest italic opacity-50">
                                No se encontraron instancias
                            </div>
                        )}
                    </div>
                </aside>
            )}

            {/* Área de Detalle Principal */}
            <main className="flex-1 space-y-4">
                {/* Tablero Nacional COPARMEX (v9.1.1 -  PLATINUM (RESTORE)) */}
                {!selectedInstancia && !selectedVPId && activeTab === 'TABLERO' && (
                    <NationalDashboardPanel
                        hierarchy={hierarchy}
                        settings={settings}
                        dashboardData={instancesDashboardData}
                        livePlanTotals={livePlanTotals}
                        selectedMonth={selectedMonth}
                        onSelectVP={(vpId: string) => {
                            setSelectedVPId(vpId);
                            setActiveTab('VP_DASHBOARD');
                        }}
                    />
                )}

                {/* Dashboard de Vicepresidencia (v8.8.0 PLATINUM (RESTORE)) */}
                {!selectedInstancia && selectedVPId ? (
                    <>
                        <header className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 border border-slate-800 p-4 sm:p-5 rounded-2xl shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="bg-emerald-400/10 text-emerald-400 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border border-emerald-400/20 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                                        Monitor de Vicepresidencia
                                    </span>
                                    <button onClick={() => { setSelectedVPId(null); setActiveTab('TABLERO'); }} className="bg-slate-800/80 border border-slate-700 hover:bg-slate-700 text-white/50 hover:text-white px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest transition-all">
                                        Ver Todas las Instancias
                                    </button>
                                </div>
                                <h1 className="text-xl md:text-2xl font-black text-white uppercase tracking-tighter leading-none">
                                    {hierarchy?.vicepresidencies?.find((v: any) => v.id === selectedVPId)?.name}
                                </h1>
                                <p className="text-white/40 text-[9px] font-black uppercase tracking-[0.4em] mt-3">Consolidado de Gestión Estratégica Ciclo 2026</p>
                            </div>
                            <div className="flex items-center gap-4 mt-6 md:mt-0 relative z-10">
                                <div className="text-right">
                                    <p className="text-[9px] text-emerald-500 font-black uppercase tracking-widest leading-none mb-1">Mes de Análisis</p>
                                    <p className="text-xs text-white font-bold uppercase">{selectedMonth} 2026</p>
                                </div>
                            </div>
                        </header>

                        <VPDashboardPanel
                            vpId={selectedVPId}
                            hierarchy={hierarchy}
                            settings={settings}
                            selectedMonth={selectedMonth}
                            dashboardData={instancesDashboardData}
                            livePlanTotals={livePlanTotals}
                            user={user}
                            onSelectInstance={(id) => {
                                setSelectedInstancia(id);
                                if (activeTab === 'VP_DASHBOARD') setActiveTab('TABLERO');
                            }}
                        />
                    </>
                ) : !selectedInstancia ? (
                    /* ESTADO 2: Ninguna seleccion (Monitor en Espera) */
                    <div className="bg-slate-900/50 border-2 border-dashed border-slate-800 rounded-[3rem] p-24 text-center">
                        <div className="w-24 h-24 bg-slate-950 border border-slate-800 rounded-[2rem] mx-auto flex items-center justify-center text-slate-800 mb-10 shadow-2xl">
                            <BarChart3 size={48} />
                        </div>
                        <h3 className="text-white font-black text-2xl uppercase tracking-tighter mb-4">MONITOR EN ESPERA</h3>
                        <p className="text-slate-600 text-xs font-bold uppercase tracking-widest max-w-sm mx-auto leading-relaxed">Selecciona una instancia o vicepresidencia del ecosistema en el panel lateral para activar los tableros de control y gestión de KPIs.</p>
                        
                        {!showEcosistema && !isInstanceScoped && (
                            <button 
                                onClick={() => setShowEcosistema(true)}
                                className="mt-8 bg-emerald-600 px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white shadow-xl shadow-emerald-900/30 hover:bg-emerald-500 transition-all flex items-center gap-2 mx-auto"
                            >
                                <Building2 size={16} /> Mostrar Ecosistema
                            </button>
                        )}
                    </div>
                ) : (
                    /* ESTADO 3: Detalle de Instancia */
                    <>
                        <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-2 sm:py-2.5 rounded-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                            <div className="relative z-10 flex items-center gap-3">
                                {!showEcosistema && !isInstanceScoped && (
                                    <button onClick={() => setShowEcosistema(true)} className="bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)] text-slate-900 p-1.5 rounded-lg hover:bg-emerald-400 transition-all shrink-0" title="Mostrar Ecosistema">
                                        <Building2 size={16} />
                                    </button>
                                )}
                                <div>
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        {activeTab !== 'TABLERO' && <span className="bg-emerald-400/10 text-emerald-400 px-2 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest border border-emerald-400/20">Monitor Activo</span>}
                                        {activeTab !== 'TABLERO' && <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest border border-slate-700">Tipo: {currentInstance?.type || 'COMISIÓN'}</span>}
                                        {activeTab !== 'TABLERO' && <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest border border-slate-700">ID Soporte: {selectedInstancia?.slice(0, 8)}</span>}
                                        {!isInstanceScoped && (
                                            <button onClick={() => { setSelectedVPId(currentInstance?.vicepresidency_id || null); setActiveTab('VP_DASHBOARD'); }} className="bg-slate-800/80 border border-slate-700 hover:bg-emerald-500 hover:border-emerald-400 hover:text-white px-2.5 py-0.5 rounded-full text-[8.5px] font-black uppercase text-slate-300 tracking-widest transition-all flex items-center gap-1 shadow-lg">
                                                <Shield size={11} /> {hierarchy?.vicepresidencies?.find((v: any) => v.id === currentInstance?.vicepresidency_id)?.name || 'Vicepresidencia'}
                                            </button>
                                        )}
                                        {!isInstanceScoped && (
                                            <button onClick={() => setShowEcosistema(true)} className="bg-slate-800/20 border border-white/5 hover:border-emerald-500/50 px-2.5 py-0.5 rounded-full text-[8.5px] font-black uppercase text-slate-500 tracking-widest transition-all">
                                                Cambiar Instancia
                                            </button>
                                        )}
                                    </div>
                                    <h1 className="text-lg sm:text-xl font-black text-white uppercase tracking-tighter max-w-2xl leading-none flex items-center gap-2">
                                        {currentInstance?.name}
                                    </h1>
                                    <p className="text-slate-300 text-xs mt-0.5">Presidente: {currentInstance?.president_name || 'No Asignado'}</p>
                                    {activeTab === 'TABLERO' && <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-300">
                                        <span className="rounded-full bg-slate-800 px-2.5 py-0.5">{selectedMonth} · 2026</span>
                                        {selectedMonth !== getCurrentPeriod('Mensual') && <span className="rounded-full bg-sky-400/10 px-2.5 py-0.5 text-sky-200">Foco · {getCurrentPeriod('Mensual')}</span>}
                                        <span className={cn('rounded-full px-2.5 py-0.5', isSelectedPeriodOpen ? 'bg-amber-400/10 text-amber-200' : 'bg-slate-800 text-slate-300')}>{isSelectedPeriodOpen ? 'Abierto · provisional' : 'Consulta'}</span>
                                    </p>}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 mt-4 md:mt-0 relative z-10">
                                {/* Temporalmente oculto hasta estabilizar exportación
                                <button onClick={handleExportExcel} className="bg-slate-950 border border-slate-800 text-white/60 px-3 py-1.5 rounded-xl font-black text-[9px] uppercase tracking-widest hover:border-emerald-500/50 hover:text-white transition-all flex items-center gap-2">
                                    <Download size={13} /> Exportar Excel
                                </button>
                                */}
                            </div>
                        </header>


                        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
                            <div className="flex flex-wrap bg-slate-900/95 backdrop-blur-md border-b border-slate-800 sticky top-0 z-40">
                                {(['TABLERO', 'indicadores', 'PONDERACIONES', 'CAPTURA', 'INFORME', 'MINUTA', 'TRIMESTRAL'] as const).filter(t => {
                                    if (t === 'TABLERO') return true;
                                    if (t === 'CAPTURA') return canCapture;
                                    if (t === 'INFORME') return true;
                                    if (t === 'PONDERACIONES') return isAdmin;
                                    if (t === 'indicadores') return isAdmin;
                                    if (t === 'MINUTA') return isAdmin;
                                    if (t === 'TRIMESTRAL') return isAdmin;
                                    return true;
                                }).map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab as any)}
                                        className={cn(
                                            "min-h-11 px-3 py-2 text-xs font-semibold uppercase tracking-wide whitespace-nowrap transition-colors relative group focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-400",
                                            activeTab === tab ? "text-emerald-400" : "text-white/70 hover:text-slate-300"
                                        )}
                                    >
                                        {tab}
                                        {activeTab === tab && <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 w-full h-1 bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.7)]"></motion.div>}
                                    </button>
                                ))}
                            </div>

                            <div className={activeTab === 'TABLERO' ? 'p-2 sm:p-3' : 'p-3 sm:p-4'}>
                                {activeTab !== 'TABLERO' && (
                                <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-800/50">
                                    <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-3">
                                        <div className="w-1.5 h-6 bg-emerald-500 rounded-full"></div>
                                        {currentInstance?.name || 'Instancia Seleccionada'}
                                    </h2>
                                    <div className="flex gap-3">
                                        <div className="text-right">
                                            <p className="text-[9px] text-slate-600 font-black uppercase">Foco Actual</p>
                                            <p className="text-xs text-white font-black uppercase">{getCurrentPeriod('Mensual')} (2026)</p>
                                            {isSelectedPeriodOpen && (
                                                <span className="text-[7px] text-amber-500 font-black uppercase tracking-widest block mt-0.5">
                                                    MES ABIERTO
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                )}

                                {activeTab === 'TABLERO' && (
                                    activeIndicator ? (
                                        <IndicatorDetailView key={`${activeIndicator.id}-${activeIndicatorPeriod || 'current'}`} ind={activeIndicator} initialPeriod={activeIndicatorPeriod} />
                                    ) : (
                                        <div className="space-y-1.5 sm:space-y-2">
                                            <ExecutiveDashboardSummary metrics={[
                                                {
                                                    label: `Plan · ${instanceWeightedPerformance?.settings?.weightDimensionPlans || 30}%`, icon: 'plan',
                                                    value: scopedReadError ? 'Error' : instanceExecutiveSummary.planPct !== null ? `${instanceExecutiveSummary.planPct}%` : 'No aplica',
                                                    state: instanceExecutiveSummary.planSemaphore,
                                                    detail: scopedReadError ? 'Sin lectura' : `${instanceExecutiveSummary.planProgressValue}/${instanceExecutiveSummary.planGoal} · ${instanceExecutiveSummary.sectionsActive} secc. · Acumulado cerrado hasta ${MONTHS[getElapsedCompletedMonths(MONTHS.indexOf(selectedMonth))] || 'sin periodo'}`,
                                                    valueClass: scopedReadError ? 'text-red-300' : instanceExecutiveSummary.planPct !== null ? getComplianceVisualState(instanceExecutiveSummary.planPct, settings).textSoftClass : 'text-slate-400',
                                                },
                                                {
                                                    label: `KPI · ${instanceWeightedPerformance?.settings?.weightDimensionKpis || 70}%`, icon: 'kpi',
                                                    value: scopedReadError ? 'Error' : instanceExecutiveSummary.kpiCompliance !== null ? `${instanceExecutiveSummary.kpiCompliance}%` : 'No aplica',
                                                    state: instanceExecutiveSummary.kpiSemaphore,
                                                    detail: scopedReadError ? 'Sin lectura' : 'Acumulado cerrado hasta ' + (MONTHS[getElapsedCompletedMonths(MONTHS.indexOf(selectedMonth))] || 'sin periodo'),
                                                    valueClass: scopedReadError ? 'text-red-300' : instanceExecutiveSummary.kpiCompliance !== null ? getComplianceVisualState(instanceExecutiveSummary.kpiCompliance, settings).textSoftClass : 'text-slate-400',
                                                },
                                                {
                                                    label: 'Desempeño', icon: 'performance',
                                                    state: (!instanceExecutiveSummary.activeKpis && !instanceExecutiveSummary.planEligibleForPeriod) ? 'NEUTRAL' : getComplianceVisualState(instanceWeightedPerformance?.global || 0, settings).labelState,
                                                    value: (!instanceExecutiveSummary.activeKpis && !instanceExecutiveSummary.planEligibleForPeriod) ? 'N/A' : `${instanceWeightedPerformance?.global || 0}%`,
                                                    detail: `${instanceWeightedPerformance?.settings?.weightDimensionPlans || 30}/${instanceWeightedPerformance?.settings?.weightDimensionKpis || 70} · Plan/KPI · Acumulado cerrado hasta ${MONTHS[getElapsedCompletedMonths(MONTHS.indexOf(selectedMonth))] || 'sin periodo'}`,
                                                    valueClass: (!instanceExecutiveSummary.activeKpis && !instanceExecutiveSummary.planEligibleForPeriod) ? 'text-slate-400' : getComplianceVisualState(instanceWeightedPerformance?.global || 0, settings).textClass,
                                                },
                                                {
                                                    label: 'Captura del periodo', icon: 'pending',
                                                    value: instanceExecutiveSummary.kpisCaptured + '/' + instanceExecutiveSummary.activeKpis,
                                                    detail: isSelectedPeriodOpen ? 'Mes abierto · captura provisional' : 'Captura y meta se evalúan por separado',
                                                    valueClass: isSelectedPeriodOpen ? 'text-slate-200' : (instanceExecutiveSummary.kpisPending > 0 ? 'text-amber-300' : 'text-emerald-400'),
                                                },
                                            ]} />

                                            <InstanceExecutiveAlertsPanel
                                                key={`${currentInstance?.id || 'instance'}-${selectedMonth}`}
                                                alerts={instanceExecutiveSummary.alerts}
                                                operationalOnly
                                                onOpenAlert={(alert) => { if (!alert.indicatorId) { navigate("/planning?instanciaId=" + selectedInstancia); return; } setActiveIndicatorPeriod(MONTHS[alert.monthIndex]); setActiveIndicatorId(alert.indicatorId); }}
                                                compact
                                            />
                                            <PriorPeriodCaptureNotice summary={priorPeriodCaptureSummary} compact onOpenPeriod={(period) => {
                                                if (String(period.instanceId) !== String(selectedInstancia)) return;
                                                const periodMonth = MONTHS.find(month => month.slice(0, 3).toUpperCase() === period.month.toUpperCase());
                                                if (!periodMonth) return;
                                                setSelectedMonth(periodMonth);
                                                setActiveIndicatorPeriod(periodMonth);
                                                setActiveIndicatorId(period.indicatorId);
                                            }} />

                                            <DashboardActionBar>
                                                {(isAdmin || canEditModule(user, 'Seguimiento')) && (
                                                    <button onClick={() => setActiveTab('CAPTURA')} aria-label="Capturar" title="Capturar" className="rounded-lg border border-emerald-600/30 bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600 hover:text-white flex justify-center items-center gap-2"><Edit3 size={15} aria-hidden="true"/><span>Capturar</span></button>
                                                )}
                                                <button onClick={() => navigate(`/planning?instanciaId=${selectedInstancia}`)} aria-label="Abrir plan" title="Plan" className="rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 flex justify-center items-center gap-2"><Book size={15} aria-hidden="true"/><span>Plan</span></button>
                                                {(isAdmin || canEditModule(user, 'Seguimiento')) && (
                                                    <button onClick={() => setActiveTab('INFORME')} aria-label="Abrir informe" title="Informe" className="rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 flex justify-center items-center gap-2"><FileText size={15} aria-hidden="true"/><span>Informe</span></button>
                                                )}
                                                <button onClick={handleExportReportPDFSafe} disabled={isExportingPDF} aria-label="Exportar informe PDF" title="Exportar" className="rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed flex justify-center items-center gap-2"><Download size={15} aria-hidden="true"/><span>{isExportingPDF ? 'Exportando' : 'Exportar'}</span></button>
                                            </DashboardActionBar>

                                            <div>
                                                <h3 className="text-base font-semibold text-white mb-1.5">Indicadores <span className="ml-1 text-xs font-normal text-slate-400">{indicadores.length}</span></h3>
                                                {(() => {
                                                    if (indicadores.length === 0) {
                                                        return (
                                                            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center">
                                                                <BarChart3 size={32} className="text-slate-600 mx-auto mb-3" />
                                                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Sin indicadores configurados para el periodo</p>
                                                            </div>
                                                        );
                                                    }

                                                    const mIdx = MONTHS.indexOf(selectedMonth);
                                                    const elapsedMonthsIdx = getElapsedCompletedMonths(mIdx);
                                                    
                                                    const classifiedIndicators = indicadores.map(ind => {
                                                        const indMetricsAccum = getIndicatorMetrics(ind, elapsedMonthsIdx, false);
                                                        const monthEntry = ind.history?.find((h: any) => h.period === selectedMonth) || {};

                                                        const hasMonthlyTarget = parseNumberSafe(monthEntry.meta, 0) > 0;
                                                        const hasAccumulatedTarget = parseNumberSafe(indMetricsAccum.meta, 0) > 0;
                                                        const hasMonthlyCapture = hasRealCaptureEvidence(monthEntry);
                                                        
                                                        const hasAnyMetaInHistory = ind.history?.some((h: any) => parseNumberSafe(h.meta, 0) > 0);
                                                        const hasAnnualConfiguration = hasAnyMetaInHistory || parseNumberSafe(ind.meta_anual, 0) > 0;

                                                        const isUnconfigured = !hasAnnualConfiguration && !hasMonthlyTarget && !hasAccumulatedTarget;
                                                        const isActionable = hasMonthlyTarget || hasAccumulatedTarget || hasMonthlyCapture;

                                                        return { ind, isUnconfigured, isActionable };
                                                    });

                                                    const unconfiguredList = classifiedIndicators.filter(c => c.isUnconfigured).map(c => c.ind);
                                                    const actionableList = classifiedIndicators.filter(c => !c.isUnconfigured && c.isActionable).map(c => c.ind);
                                                    const noObligationList = classifiedIndicators.filter(c => !c.isUnconfigured && !c.isActionable).map(c => c.ind);

                                                    const renderIndicatorCard = (ind: any) => {
                                                        const indMetricsSingle = getIndicatorMetrics(ind, mIdx, true);
                                                        const indMetricsAccum = getIndicatorMetrics(ind, elapsedMonthsIdx, false);
                                                        const monthEntry = ind.history?.find((h: any) => h.period === selectedMonth) || {};

                                                        const hasMonthlyTarget = parseNumberSafe(monthEntry.meta, 0) > 0;
                                                        const hasAccumulatedTarget = parseNumberSafe(indMetricsAccum.meta, 0) > 0;
                                                        const hasMonthlyCapture = hasRealCaptureEvidence(monthEntry);

                                                        let stateName: 'monthly_complete' | 'pending_capture' | 'closed_missing_capture' | 'accumulated_available' | 'no_monthly_obligation' = 'no_monthly_obligation';

                                                        if (hasMonthlyTarget) {
                                                            if (hasMonthlyCapture) {
                                                                stateName = 'monthly_complete';
                                                            } else {
                                                                stateName = isSelectedPeriodOpen ? 'pending_capture' : 'closed_missing_capture';
                                                            }
                                                        } else if (hasAccumulatedTarget) {
                                                            stateName = 'accumulated_available';
                                                        }

                                                        let pct: number | null = null;
                                                        let badgeText = '';
                                                        let badgeClass = '';
                                                        let metaLabel = '';
                                                        let metaValue = '';
                                                        let realLabel = '';
                                                        let realValue = '';
                                                        let realClass = '';
                                                        let borderClass = 'border-slate-800 hover:border-slate-700';
                                                        let textClass = 'text-slate-300';

                                                        if (stateName === 'monthly_complete') {
                                                            pct = indMetricsSingle.pct;
                                                            const visual = getComplianceVisualState(pct, settings);
                                                            badgeText = visual.labelState;
                                                            badgeClass = `${visual.bgSoftClass} ${visual.textSoftClass} ${visual.borderClass}`;
                                                            metaLabel = `Meta ${selectedMonth.slice(0, 3).toUpperCase()}`;
                                                            metaValue = String(monthEntry.meta);
                                                            realLabel = isSelectedPeriodOpen ? "Real prov." : "Real";
                                                            realValue = String(monthEntry.real);
                                                            realClass = "text-white";
                                                            borderClass = `border-slate-800 hover:${visual.borderClass}`;
                                                            textClass = visual.textClass;
                                                        } else if (stateName === 'pending_capture') {
                                                            pct = null;
                                                            badgeText = 'EN CURSO';
                                                            badgeClass = 'bg-slate-800 text-slate-300 border-slate-700';
                                                            metaLabel = `Meta ${selectedMonth.slice(0, 3).toUpperCase()}`;
                                                            metaValue = String(monthEntry.meta);
                                                            realLabel = "Real prov.";
                                                            realValue = monthEntry.real !== null && monthEntry.real !== undefined && String(monthEntry.real).trim() !== '' ? String(monthEntry.real) : "—";
                                                            realClass = "text-slate-400";
                                                            borderClass = 'border-slate-800 hover:border-slate-700';
                                                            textClass = 'text-slate-400';
                                                        } else if (stateName === 'closed_missing_capture') {
                                                            pct = 0;
                                                            const visual = getComplianceVisualState(pct, settings);
                                                            badgeText = visual.labelState;
                                                            badgeClass = `${visual.bgSoftClass} ${visual.textSoftClass} ${visual.borderClass}`;
                                                            metaLabel = `Meta ${selectedMonth.slice(0, 3).toUpperCase()}`;
                                                            metaValue = String(monthEntry.meta);
                                                            realLabel = "Real";
                                                            realValue = monthEntry.real !== null && monthEntry.real !== undefined && String(monthEntry.real).trim() !== '' ? String(monthEntry.real) : "—";
                                                            realClass = visual.textClass;
                                                            borderClass = `border-slate-800 hover:${visual.borderClass}`;
                                                            textClass = visual.textClass;
                                                        } else if (stateName === 'accumulated_available') {
                                                            pct = indMetricsAccum.pct;
                                                            const visual = getComplianceVisualState(pct, settings);
                                                            badgeText = `Acum. ${MONTHS_LABELS[elapsedMonthsIdx]}`;
                                                            badgeClass = 'bg-blue-500/10 text-blue-400 border-blue-500/20';
                                                            metaLabel = "Meta Acum.";
                                                            metaValue = String(indMetricsAccum.meta);
                                                            realLabel = "Real Acum.";
                                                            realValue = String(indMetricsAccum.real);
                                                            realClass = "text-white";
                                                            borderClass = `border-slate-800 hover:${visual.borderClass}`;
                                                            textClass = visual.textClass;
                                                        } else {
                                                            badgeText = 'SIN OBLIGACIÓN';
                                                            badgeClass = 'bg-slate-800/50 text-slate-400 border-slate-700';
                                                            borderClass = 'border-slate-800 hover:border-slate-700';
                                                            textClass = 'text-slate-500';
                                                        }

                                                        return <CompactKpiCard
                                                            key={ind.id}
                                                            name={ind.nombre}
                                                            badgeText={badgeText}
                                                            badgeClass={badgeClass}
                                                            borderClass={borderClass}
                                                            metaLabel={metaLabel}
                                                            metaValue={metaValue}
                                                            realLabel={realLabel}
                                                            realValue={realValue}
                                                            realClass={realClass}
                                                            pct={pct}
                                                            complianceValue={stateName === 'pending_capture' ? 'En curso' : undefined}
                                                            statusMeaning={pct !== null ? getComplianceVisualState(pct, settings).labelState : undefined}
                                                            textClass={textClass}
                                                            complianceLabel={stateName === 'accumulated_available' ? 'Cumpl. acum.' : (isSelectedPeriodOpen ? 'Avance prov.' : 'Cumpl.')}
                                                            warning={stateName === 'closed_missing_capture' ? 'Sin evidencia al cierre' : undefined}
                                                            noObligation={stateName === 'no_monthly_obligation' ? `Sin meta · ${selectedMonth.slice(0, 3).toUpperCase()} · No aplica obligación` : undefined}
                                                            onOpen={() => setActiveIndicatorId(ind.id)}
                                                        />;
                                                    };

                                                    return (
                                                        <>
                                                            {unconfiguredList.length > 0 && (
                                                                <div className="mb-1.5 border-l-2 border-amber-500/60 pl-2.5">
                                                                    <button
                                                                        onClick={() => setShowUnconfiguredList(!showUnconfiguredList)}
                                                                        type="button"
                                                                        aria-expanded={showUnconfiguredList}
                                                                        aria-controls="dashboard-unconfigured-indicators"
                                                                        className="w-full min-h-9 flex justify-between items-center gap-2 py-0.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-400"
                                                                    >
                                                                        <div>
                                                                            <h4 className="text-xs sm:text-sm font-semibold text-amber-200 flex items-center gap-1.5">
                                                                                <Settings size={14} aria-hidden="true" />
                                                                                {unconfiguredList.length} sin configurar
                                                                            </h4>
                                                                        </div>
                                                                        <div className={cn("text-slate-400 p-0.5 hover:text-white transition-all transform duration-200", showUnconfiguredList && "rotate-180")}>
                                                                            <ChevronDown size={15} />
                                                                        </div>
                                                                    </button>

                                                                    {showUnconfiguredList && (
                                                                        <div id="dashboard-unconfigured-indicators" className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
                                                                            {unconfiguredList.map(ind => (
                                                                                <button type="button"
                                                                                    key={ind.id}
                                                                                    onClick={() => setActiveIndicatorId(ind.id)}
                                                                                    className="min-h-11 min-w-0 text-left bg-slate-900/50 border border-slate-800/40 p-3 rounded-lg text-slate-300 hover:text-white hover:border-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-400 flex justify-between items-center gap-3 text-sm"
                                                                                >
                                                                                    <span className="min-w-0 break-words">{ind.nombre}</span>
                                                                                    <span className="shrink-0 text-xs text-slate-400">Sin Meta</span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {actionableList.length === 0 ? (
                                                                <div className="bg-slate-900/40 border border-slate-800/80 rounded-xl p-5 text-center my-2.5">
                                                                    <BarChart3 size={24} className="text-slate-600 mx-auto mb-2" />
                                                                    <p className="text-xs font-medium text-slate-400">Todo al día</p>
                                                                </div>
                                                            ) : (
                                                                <Reorder.Group 
                                                                    axis="x" 
                                                                    values={actionableList} 
                                                                    onReorder={(newOrder) => setIndicatorOrderLocally([...newOrder, ...noObligationList, ...unconfiguredList])} 
                                                                    className="grid grid-cols-1 lg:grid-cols-2 gap-2"
                                                                >
                                                                    {actionableList.map(ind => (
                                                                        <Reorder.Item key={ind.id} value={ind} className="h-auto">
                                                                            {renderIndicatorCard(ind)}
                                                                        </Reorder.Item>
                                                                    ))}
                                                                </Reorder.Group>
                                                            )}

                                                            {noObligationList.length > 0 && (
                                                                <div className="mt-2 bg-slate-950/20 border border-slate-800/50 rounded-xl p-2.5 shadow-md">
                                                                    <button 
                                                                        onClick={() => setShowNoObligationList(!showNoObligationList)}
                                                                        className="w-full min-h-[44px] flex justify-between items-center text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-400 touch-target"
                                                                        aria-expanded={showNoObligationList}
                                                                        aria-label="Alternar indicadores sin actividad"
                                                                    >
                                                                        <div>
                                                                            <h4 className="text-xs font-semibold text-slate-300 flex items-center gap-2">
                                                                                <div className="w-1.5 h-1.5 rounded-full bg-slate-700"></div>
                                                                                Sin obligación
                                                                                <span className="ml-1.5 bg-slate-800 text-slate-400 px-1.5 py-0.2 rounded text-[9px] font-bold">
                                                                                    {noObligationList.length}
                                                                                </span>
                                                                            </h4>
                                                                            <p className="text-[10px] text-slate-500 mt-0.5">
                                                                                {showNoObligationList ? 'Ocultar' : 'Ver'}
                                                                            </p>
                                                                        </div>
                                                                        <div className={cn("text-slate-500 p-1 hover:text-white transition-all transform duration-200", showNoObligationList && "rotate-180")}>
                                                                            <ChevronDown size={14} />
                                                                        </div>
                                                                    </button>
                                                                    
                                                                    {showNoObligationList && (
                                                                        <div className="mt-3 pt-3 border-t border-slate-800/50 grid grid-cols-1 lg:grid-cols-2 gap-3 animate-in fade-in duration-200">
                                                                            {noObligationList.map(ind => renderIndicatorCard(ind))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )
                                )}

                                {activeTab === 'indicadores' && (
                                    <div className="space-y-8">
                                        <div className="flex justify-between items-center bg-slate-950 p-6 rounded-3xl border border-slate-800 shadow-xl">
                                            <div>
                                                <h3 className="text-white font-black uppercase text-sm tracking-tight text-emerald-500">Gestor de Indicadores Estratégicos</h3>
                                                <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest mt-1">Configura las metas y propiedades de tus KPIs</p>
                                            </div>
                                            <div className="flex gap-4">
                                                {isAdmin && canEditModule(user, 'Seguimiento') && (
                                                    <button onClick={() => setShowReplicaModal(true)} className="bg-purple-600/10 text-purple-400 border border-purple-500/20 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-600/20 transition-all flex items-center gap-2">
                                                        <Sparkles size={16} /> Sincronización multinivel
                                                    </button>
                                                )}
                                                {canEditModule(user, 'Seguimiento') && (
                                                    <>
                                                        <button
                                                            onClick={handleSaveIndicatorOrder}
                                                            disabled={!hasPendingOrderChanges || isSavingOrder}
                                                            className={cn(
                                                                "px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border active:scale-95",
                                                                hasPendingOrderChanges && !isSavingOrder
                                                                    ? "bg-cyan-600/15 text-cyan-300 border-cyan-500/30 hover:bg-cyan-600/25"
                                                                    : "bg-slate-900 text-white/30 border-slate-800 cursor-not-allowed"
                                                            )}
                                                        >
                                                            <GripVertical size={16} /> {isSavingOrder ? 'Guardando Orden' : 'Guardar Orden'}
                                                        </button>
                                                        <button onClick={handleSaveIndicatorProperties} className="bg-slate-800 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-700 transition-all flex items-center gap-2 border border-slate-700 active:scale-95">
                                                            <Save size={16} /> Guardar
                                                        </button>
                                                        <button onClick={handleAddIndicator} className="bg-emerald-600 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95">
                                                            <Plus size={16} /> Agregar Indicador
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Vista Desktop: Tabla Pro */}
                                        <div className="hidden lg:block relative pb-4">
                                            <div className="w-full text-left">
                                                <div className="flex text-[10px] text-white/70 uppercase font-black tracking-[0.2em] px-6 pb-3">
                                                    <div className="px-8 min-w-[300px] flex-1">Definicion del KPI</div>
                                                    <div className="px-8 w-40">Objetivo</div>
                                                    <div className="px-8 w-40 text-center">Peso (%)</div>
                                                    <div className="px-8 w-40">Algoritmo</div>
                                                    <div className="px-8 w-40">Periodo</div>
                                                    <div className="px-8 w-24 text-center"></div>
                                                </div>
                                                <Reorder.Group axis="y" values={indicadores} onReorder={setIndicatorOrderLocally} className="flex flex-col gap-3">
                                                    {indicadores.map((ind, i) => (
                                                        <Reorder.Item
                                                            value={ind}
                                                            key={ind.id}
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            whileDrag={{
                                                                scale: 1.01,
                                                                backgroundColor: "rgba(15, 23, 42, 0.9)",
                                                                boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
                                                                zIndex: 10
                                                            }}
                                                            className="flex bg-slate-950/50 group hover:bg-slate-900/80 transition-all border border-slate-800 shadow-sm relative rounded-2xl items-stretch"
                                                        >
                                                            <div className="px-4 py-3 flex-1 flex items-center gap-3">
                                                                {isAdmin && (
                                                                    <div className="cursor-grab active:cursor-grabbing p-1 text-slate-700 hover:text-emerald-500 transition-colors">
                                                                        <Menu size={14} />
                                                                    </div>
                                                                )}
                                                                <div className="flex-1">
                                                                    <input
                                                                        readOnly={!isAdmin}
                                                                        className={cn(
                                                                            "w-full bg-transparent text-white text-xs font-black uppercase outline-none transition-colors placeholder:text-slate-700",
                                                                            isAdmin ? "focus:text-emerald-400" : "cursor-default"
                                                                        )}
                                                                        placeholder="Nombre del indicador..."
                                                                        value={ind.nombre}
                                                                        onChange={e => {
                                                                            if (!isAdmin) return;
                                                                            const n = [...indicadores]; n[i].nombre = e.target.value; setindicadores(n);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="px-4 py-3 w-32 border-l border-slate-800/50 flex items-center">
                                                                <select
                                                                    disabled={!isAdmin}
                                                                    className="w-full bg-slate-900 border border-slate-800 px-2 py-1.5 rounded-lg text-slate-300 text-[9px] font-black uppercase outline-none appearance-none cursor-pointer focus:border-emerald-500/40 disabled:opacity-50 disabled:cursor-default"
                                                                    value={ind.tipo}
                                                                    onChange={e => {
                                                                        const n = [...indicadores]; n[i].tipo = e.target.value; setindicadores(n);
                                                                    }}
                                                                >
                                                                    <option value="Maximizar">Maximizar</option>
                                                                    <option value="Minimizar">Minimizar</option>
                                                                </select>
                                                            </div>
                                                            <div className="px-4 py-3 w-32 border-l border-slate-800/50 flex items-center bg-slate-950/30">
                                                                <div className="flex items-center gap-1.5 w-full">
                                                                    <input
                                                                        type="number"
                                                                        readOnly={!isAdmin}
                                                                        className={cn(
                                                                            "w-full bg-slate-900 border border-slate-800 px-2 py-1.5 rounded-lg text-emerald-400 text-center text-[11px] font-black outline-none focus:border-emerald-500/40",
                                                                            !isAdmin && "opacity-50 cursor-default"
                                                                        )}
                                                                        value={ind.peso || 0}
                                                                        onChange={e => {
                                                                            if (!isAdmin) return;
                                                                            const n = [...indicadores]; n[i].peso = parseFloat(e.target.value) || 0; setindicadores(n);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="px-4 py-3 w-32 border-l border-slate-800/50 flex items-center">
                                                                <select
                                                                    disabled={!isAdmin}
                                                                    className="w-full bg-slate-900 border border-slate-800 px-2 py-1.5 rounded-lg text-slate-300 text-[9px] font-black uppercase outline-none appearance-none cursor-pointer focus:border-emerald-500/40 disabled:opacity-50 disabled:cursor-default"
                                                                    value={ind.calculo}
                                                                    onChange={e => {
                                                                        const n = [...indicadores]; n[i].calculo = e.target.value; setindicadores(n);
                                                                    }}
                                                                >
                                                                    <option value="Sumar">Sumatoria</option>
                                                                    <option value="Promedio">Promedio</option>
                                                                </select>
                                                            </div>
                                                            <div className="px-4 py-3 w-32 border-l border-slate-800/50 flex items-center">
                                                                <select
                                                                    disabled={!isAdmin}
                                                                    className="w-full bg-slate-900 border border-slate-800 px-2 py-1.5 rounded-lg text-slate-300 text-[9px] font-black uppercase outline-none appearance-none cursor-pointer focus:border-emerald-500/40 disabled:opacity-50 disabled:cursor-default"
                                                                    value={ind.frec}
                                                                    onChange={e => {
                                                                        const n = [...indicadores]; n[i].frec = e.target.value; setindicadores(n);
                                                                    }}
                                                                >
                                                                    <option value="Semanal">Semanal</option>
                                                                    <option value="Mensual">Mensual</option>
                                                                </select>
                                                            </div>
                                                            {/* MODO: Normal o Checklist */}
                                                            <div className="px-4 py-3 w-32 border-l border-slate-800/50 flex items-center">
                                                                <select
                                                                    disabled={!isAdmin}
                                                                    className={`w-full px-2 py-1.5 rounded-lg text-[9px] font-black uppercase outline-none appearance-none cursor-pointer transition-all disabled:opacity-50 disabled:cursor-default border ${ind.modo === 'checklist'
                                                                        ? 'bg-violet-950/30 border-violet-500/40 text-violet-300'
                                                                        : 'bg-slate-900 border-slate-800 text-slate-300'
                                                                        }`}
                                                                    value={ind.modo || 'normal'}
                                                                    onChange={e => {
                                                                        const n = [...indicadores]; n[i].modo = e.target.value; setindicadores(n);
                                                                    }}
                                                                >
                                                                    <option value="normal">Normal</option>
                                                                    <option value="checklist">Checklist</option>
                                                                </select>
                                                            </div>
                                                            <div className="px-4 py-3 w-16 border-l border-slate-800/50 flex justify-center items-center">
                                                                {isAdmin && (
                                                                    <button onClick={() => handleDeleteIndicator(ind.id)} className="p-2 text-slate-600 hover:text-red-500 transition-colors bg-slate-900 rounded-lg hover:shadow-lg hover:shadow-red-500/10"><Trash2 size={14} /></button>
                                                                )}
                                                            </div>
                                                        </Reorder.Item>
                                                    ))}
                                                </Reorder.Group>
                                            </div>
                                        </div>

                                        {/* Vista Movil: Tarjetas */}
                                        <Reorder.Group axis="y" values={indicadores} onReorder={setIndicatorOrderLocally} className="lg:hidden space-y-3">
                                            {indicadores.map((ind, i) => (
                                                <Reorder.Item
                                                    value={ind}
                                                    key={ind.id}
                                                    whileDrag={{ scale: 1.02, rotate: 0, zIndex: 10 }}
                                                    className="bg-slate-950/80 border border-slate-800 p-4 rounded-[1.5rem] space-y-3 relative group hover:border-emerald-500/30 transition-all backdrop-blur-sm shadow-sm"
                                                >
                                                    <div className="flex justify-between items-center mb-1">
                                                        <div className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-slate-700 hover:text-emerald-500 transition-colors">
                                                            <Menu size={16} />
                                                        </div>
                                                        <span className="text-[9px] font-black text-white/70 uppercase tracking-widest bg-slate-900 px-2 py-0.5 rounded-md">#{i + 1}</span>
                                                    </div>
                                                    <div>
                                                        <input className="w-full bg-slate-900 border border-slate-800 p-2.5 rounded-xl text-white text-[11px] font-black uppercase outline-none focus:border-emerald-500/40" placeholder="Nombre..." value={ind.nombre} onChange={e => {
                                                            const n = [...indicadores]; n[i].nombre = e.target.value; setindicadores(n);
                                                        }} />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="bg-slate-900 p-2 rounded-xl border border-slate-800">
                                                            <label className="text-[7px] text-slate-500 font-black uppercase mb-1 block">Peso (%)</label>
                                                            <input
                                                                readOnly={!isAdmin}
                                                                type="number"
                                                                className="w-full bg-transparent text-emerald-400 text-[11px] font-black outline-none"
                                                                value={ind.peso || 0}
                                                                onChange={e => {
                                                                    if (!isAdmin) return;
                                                                    const n = [...indicadores]; n[i].peso = parseFloat(e.target.value) || 0; setindicadores(n);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="bg-slate-900 p-2 rounded-xl border border-slate-800">
                                                            <label className="text-[7px] text-slate-500 font-black uppercase mb-1 block">Frecuencia</label>
                                                            <select
                                                                disabled={!isAdmin}
                                                                className="w-full bg-transparent text-white text-[9px] font-black outline-none uppercase"
                                                                value={ind.frec}
                                                                onChange={e => {
                                                                    const n = [...indicadores]; n[i].frec = e.target.value; setindicadores(n);
                                                                }}
                                                            >
                                                                <option value="Semanal">Semanal</option>
                                                                <option value="Mensual">Mensual</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="bg-slate-900 p-2 rounded-xl border border-slate-800">
                                                            <label className="text-[7px] text-slate-500 font-black uppercase mb-1 block">Tipo</label>
                                                            <select className="w-full bg-transparent text-white text-[9px] font-black outline-none uppercase" value={ind.tipo} onChange={e => {
                                                                const n = [...indicadores]; n[i].tipo = e.target.value; setindicadores(n);
                                                            }}>
                                                                <option value="Maximizar">Maximizar</option>
                                                                <option value="Minimizar">Minimizar</option>
                                                            </select>
                                                        </div>
                                                        <div className="bg-slate-900 p-2 rounded-xl border border-slate-800">
                                                            <label className="text-[7px] text-slate-500 font-black uppercase mb-1 block">Cálculo</label>
                                                            <select className="w-full bg-transparent text-white text-[9px] font-black outline-none uppercase" value={ind.calculo} onChange={e => {
                                                                const n = [...indicadores]; n[i].calculo = e.target.value; setindicadores(n);
                                                            }}>
                                                                <option value="Sumar">Sumatoria</option>
                                                                <option value="Promedio">Promedio</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end">
                                                        {isAdmin && (
                                                            <button onClick={() => handleDeleteIndicator(ind.id)} className="p-2.5 text-red-500/50 hover:text-red-500 transition-colors bg-red-500/5 rounded-xl border border-red-500/10"><Trash2 size={14} /></button>
                                                        )}
                                                    </div>
                                                </Reorder.Item>
                                            ))}
                                        </Reorder.Group>
                                    </div>
                                )}

                                {activeTab === 'PONDERACIONES' && (
                                    <div className="flex justify-center py-6">
                                        <div className="bg-slate-950/80 p-8 rounded-[2rem] border border-slate-800 w-full max-w-4xl shadow-2xl relative overflow-hidden flex flex-col h-[calc(100vh-250px)]">
                                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/[0.03] rounded-full -mr-32 -mt-32 blur-3xl point-events-none"></div>
                                            <header className="mb-6 shrink-0 flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-white font-black uppercase tracking-tight text-lg mb-1">Distribución de Pesos Estratégicos</h3>
                                                    <p className="text-white/70 text-[9px] font-black uppercase tracking-[0.2em]">El ecosistema requiere suma exacta de 100%</p>
                                                </div>
                                                <div className={cn(
                                                    "px-4 py-2 rounded-xl flex items-center gap-2 border transition-all",
                                                    totalPeso === 100 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-red-500/10 text-red-400 border-red-500/30"
                                                )}>
                                                    <span className="text-[9px] font-black uppercase tracking-widest hidden sm:inline">Total:</span>
                                                    <span className="text-lg font-black font-mono">{totalPeso}%</span>
                                                    {totalPeso === 100 && <CheckCircle2 size={16} />}
                                                </div>
                                            </header>

                                            {/* Lista de indicadores (Scrollable) */}
                                            <div className="space-y-2 flex-1 overflow-y-auto pr-2 scrollbar-hide py-2">
                                                {indicadores.map((ind, i) => (
                                                    <div key={ind.id} className="flex items-center gap-4 bg-slate-900/60 px-4 py-3 rounded-2xl border border-slate-800 hover:border-emerald-500/30 transition-all group">
                                                        <span className="text-[10px] font-medium text-white/70 w-6 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                                                        <span className="flex-1 text-xs text-slate-300 font-bold uppercase tracking-tight group-hover:text-white transition-colors item-name font-black tracking-tight" title={ind.nombre}>{ind.nombre}</span>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <div className="relative">
                                                                <input
                                                                    type="number"
                                                                    readOnly={!isAdmin}
                                                                    className={cn(
                                                                        "w-20 bg-slate-950 border border-slate-700 px-3 py-1.5 rounded-lg text-white text-sm font-black outline-none font-mono text-center transition-all",
                                                                        isAdmin ? "focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50" : "opacity-50 cursor-default"
                                                                    )}
                                                                    value={ind.peso}
                                                                    onChange={e => {
                                                                        if (!isAdmin) return;
                                                                        const val = parseInt(e.target.value) || 0;
                                                                        const n = [...indicadores]; n[i].peso = Math.max(0, val); setindicadores(n);
                                                                    }}
                                                                />
                                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 font-black text-[10px]">%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Barra inferior fija y conectada */}
                                            <footer className="mt-6 pt-4 border-t border-slate-800 shrink-0 flex flex-col sm:flex-row justify-end items-center gap-3">
                                                {isAdmin && (
                                                    <button onClick={() => setShowReplicaModal(true)} className="w-full sm:w-auto px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 flex justify-center items-center gap-2">
                                                        <Sparkles size={14} /> Sincronización multinivel
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleSaveIndicators()}
                                                    disabled={totalPeso !== 100}
                                                    className={cn(
                                                        "w-full sm:w-auto px-8 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-lg flex justify-center items-center gap-2",
                                                        totalPeso === 100
                                                            ? "bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-600/20 active:scale-95 cursor-pointer"
                                                            : "bg-slate-800 text-white/70 cursor-not-allowed border border-slate-700"
                                                    )}
                                                >
                                                    Guardar Pesos <Save size={14} />
                                                </button>
                                            </footer>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'CAPTURA' && (
                                    <div className="space-y-10">
                                        <div className="bg-slate-950/50 p-8 rounded-[2rem] border border-slate-800 flex items-center gap-6">
                                            <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-500 border border-emerald-500/20"><Calculator size={32} /></div>
                                            <div>
                                                <h3 className="text-white font-black uppercase text-base">Consola de Captura Directa</h3>
                                                <p className="text-[10px] text-slate-600 font-black uppercase tracking-[0.2em] mt-1">Ingresa el avance real obtenido para el periodo en curso</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                            {indicadores.map(ind => {
                                                const currentIdx = getCurrentIdx(ind);
                                                const progress = ind.history[currentIdx].real;
                                                const meta = ind.history[currentIdx].meta;
                                                const pct = meta > 0 ? Math.round((progress / meta) * 100) : 0;

                                                return (
                                                    <div
                                                        key={ind.id}
                                                        draggable={isAdmin}
                                                        onDragStart={() => handleDragStart(ind.id)}
                                                        onDragOver={handleDragOver}
                                                        onDrop={() => handleDrop(ind.id)}
                                                        onClick={() => setActiveIndicatorId(ind.id)}
                                                        className={cn(
                                                            "bg-slate-950 border border-slate-800 p-4 rounded-3xl group hover:border-emerald-500/50 transition-all cursor-pointer relative overflow-hidden shadow-lg flex flex-col justify-between",
                                                            draggedIndicatorId === ind.id && "opacity-40 scale-95",
                                                            draggedIndicatorId && draggedIndicatorId !== ind.id && "hover:border-indigo-500"
                                                        )}
                                                    >
                                                        <div className="flex justify-between items-start mb-4">
                                                            <h4 className="text-white font-black uppercase text-[10px] line-clamp-2 leading-tight pr-6">{ind.nombre}</h4>
                                                            <div className={cn(
                                                                "text-[8px] font-black px-1.5 py-0.5 rounded border uppercase tracking-tighter",
                                                                getComplianceVisualState(pct, settings).bgSoftClass,
                                                                getComplianceVisualState(pct, settings).textSoftClass,
                                                                getComplianceVisualState(pct, settings).borderClass
                                                            )}>
                                                                {pct}%
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                                            <div className="bg-slate-900/50 rounded-2xl p-2 border border-slate-800/50">
                                                                <label className="text-[6px] text-white/70 font-black uppercase tracking-[0.2em] mb-1 block">META {MONTHS[new Date().getMonth()]}</label>
                                                                <div className="flex items-center gap-1">
                                                                    <input
                                                                        type="number"
                                                                        readOnly={!isAdmin}
                                                                        className={cn(
                                                                            "w-full bg-slate-950 border border-slate-800/50 rounded-lg px-2 py-1.5 text-xs font-black outline-none transition-all",
                                                                            isAdmin ? "text-white focus:border-emerald-500" : "text-slate-600 cursor-default"
                                                                        )}
                                                                        value={meta}
                                                                        onClick={e => e.stopPropagation()}
                                                                        onChange={e => {
                                                                            if (!isAdmin) return;
                                                                            updateIndicatorHistory(ind.id, currentIdx, 'meta', parseFloat(e.target.value) || 0);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="bg-emerald-500/5 rounded-2xl p-2 border border-emerald-500/20">
                                                                <label className="text-[6px] text-emerald-500 font-black uppercase tracking-[0.2em] mb-1 block">REAL {MONTHS[new Date().getMonth()]}</label>
                                                                <div className="flex items-center gap-1">
                                                                    <input
                                                                        type="number"
                                                                        readOnly={!canCapture}
                                                                        className={cn(
                                                                            "w-full bg-emerald-950/20 border border-emerald-500/30 rounded-lg px-2 py-1.5 text-xs font-black text-emerald-400 outline-none transition-all",
                                                                            canCapture ? "focus:border-emerald-500" : "cursor-default opacity-60"
                                                                        )}
                                                                        value={progress}
                                                                        onClick={e => e.stopPropagation()}
                                                                        onChange={e => {
                                                                            if (!canCapture) return;
                                                                            updateIndicatorHistory(ind.id, currentIdx, 'real', parseFloat(e.target.value) || 0, true);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="flex justify-between items-center gap-2">
                                                            <div className="text-[8px] text-white/70 font-black uppercase tracking-widest bg-slate-900 px-2 py-1 rounded">
                                                                Meta Mes: <span className="text-slate-300">{meta}{ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}</span>
                                                            </div>
                                                            <div className="text-[8px] text-white/70 font-black uppercase tracking-widest bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">
                                                                Anual: <span className="text-emerald-400">{calculateStats(ind, selectedMonth, selectedMonth).totalMeta}{ind.unidad === '%' ? (ind.modo === 'checklist' ? '' : '%') : ind.unidad}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        {canCapture && (
                                            <div className="flex justify-center pt-6">
                                                <button
                                                    onClick={() => handleSaveIndicators()}
                                                    className="bg-white text-black px-12 py-5 rounded-[1.5rem] text-[11px] font-black uppercase tracking-[0.3em] hover:bg-emerald-500 hover:text-white shadow-2xl transition-all flex items-center gap-3 active:scale-95"
                                                >
                                                    <CheckCircle2 size={18} /> Consolidar Cambios
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'INFORME' && (
                                    <div id="tracking-report-pdf-container" className="bg-white p-8 md:p-12 rounded-[2rem] text-slate-900 shadow-2xl mx-auto print:shadow-none print:m-0 print:p-0">
                                        <div className="flex justify-between items-center mb-8 border-b-2 border-slate-100 pb-6 print:hidden" data-pdf-exclude="true">
                                            <div className="flex gap-2">
                                                {canEditReport && (
                                                    <button
                                                        onClick={() => setIsEditingReport(!isEditingReport)}
                                                        className={cn(
                                                            "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                                            isEditingReport ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                        )}
                                                    >
                                                        {isEditingReport ? 'Finalizar Edicion' : 'Editar Informe'}
                                                    </button>
                                                )}
                                                {isEditingReport && (
                                                    <button
                                                        onClick={() => handleSaveReport({ observations, summary: execSummary, extra_tables: extraTables, extra_columns: extraColumns })}
                                                        className="bg-emerald-500 text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-emerald-600"
                                                    >
                                                        GUARDAR INFORME
                                                    </button>
                                                )}
                                            </div>
                                            <button onClick={handleExportReportPDFSafe} disabled={isExportingPDF} className={cn("text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg transition-all", isExportingPDF ? "bg-slate-700 cursor-not-allowed opacity-70" : "bg-slate-900 hover:bg-slate-800")}>
                                                {isExportingPDF ? 'GENERANDO PDF...' : 'GENERAR PDF'}
                                            </button>
                                        </div>

                                        <div className="border-b-4 border-emerald-600 pb-8 mb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-500 mb-3">COPARMEX 2026 / Gobernanza Institucional</p>
                                                <h1 className="text-4xl font-black uppercase text-slate-900 tracking-tighter leading-none mb-2">INFORME ESTRATÉGICO MENSUAL</h1>
                                                <h2 className="text-xl font-bold text-emerald-700 uppercase">{currentInstance?.name}</h2>
                                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-2">
                                                    Presidente: {currentInstance?.president_name || 'No Asignado'}
                                                </h3>
                                            </div>
                                            <div className="text-right flex flex-col items-end gap-2">
                                                <div className="text-sm font-black text-slate-400 uppercase tracking-widest mb-1">Periodo Reportado</div>
                                                <select
                                                    data-pdf-exclude="true"
                                                    className="bg-emerald-500 text-white font-black text-2xl uppercase p-2 rounded-xl outline-none cursor-pointer border-none shadow-lg"
                                                    style={{ position: 'relative', zIndex: 50 }}
                                                    value={selectedMonth}
                                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                                >
                                                    {MONTHS.map(m => <option key={m} value={m} className="bg-slate-900 text-white font-black">{m} 2026</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-10">
                                            <h3 className="text-lg font-black uppercase text-slate-800 border-l-4 border-emerald-500 pl-4 mb-4">RESUMEN EJECUTIVO Y COMENTARIOS</h3>
                                            <textarea
                                                readOnly={!isEditingReport}
                                                className={cn(
                                                    "w-full p-6 rounded-2xl text-slate-700 text-sm font-medium outline-none transition-all resize-y min-h-[150px] print:border-none print:bg-transparent print:p-0",
                                                    isEditingReport ? "bg-white border-2 border-emerald-500 shadow-xl" : "bg-slate-50 border-2 border-slate-200"
                                                )}
                                                value={execSummary}
                                                onChange={(e) => setExecSummary(e.target.value)}
                                                placeholder="El responsable debe describir aquí los logros principales, retos y acciones a tomar del periodo..."
                                            ></textarea>
                                            <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase tracking-wider print:hidden">
                                                Estructura sugerida: LOGROS DESTACADOS, ÁREAS DE RIESGO, ACCIONES PRIORITARIAS
                                            </p>
                                        </div>

                                        <div className="mb-10">
                                            <h3 className="text-lg font-black uppercase text-slate-800 border-l-4 border-emerald-500 pl-4 mb-4">OBSERVACIONES DEL MES</h3>
                                            <textarea
                                                readOnly={!isEditingReport}
                                                className={cn(
                                                    "w-full p-6 rounded-2xl text-slate-700 text-sm font-medium outline-none transition-all resize-y min-h-[150px] print:border-none print:bg-transparent print:p-0",
                                                    isEditingReport ? "bg-white border-2 border-emerald-500 shadow-xl" : "bg-slate-50 border-2 border-slate-200"
                                                )}
                                                value={observations}
                                                onChange={(e) => setObservations(e.target.value)}
                                                placeholder="Observaciones adicionales sobre indicadores y metas..."
                                            ></textarea>
                                        </div>

                                        <div className="mb-10">
                                            <h3 className="text-lg font-black uppercase text-slate-800 border-l-4 border-emerald-500 pl-4 mb-6">DESEMPEÑO DE INDICADORES (KPIs)</h3>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left border-collapse">
                                                    <thead>
                                                        <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase font-black tracking-widest">
                                                            <th className="p-4 rounded-tl-xl">Indicador</th>
                                                            <th className="p-4 text-right">Meta {selectedMonth}</th>
                                                            <th className="p-4 text-right">Real {selectedMonth}</th>
                                                            <th className="p-4 text-right rounded-tr-xl">Cumplimiento</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {indicadores.map((ind) => {
                                                            const currentPeriod = selectedMonth;
                                                            const currObj = ind.history?.find((h: any) => h.period === currentPeriod) || { meta: 0, real: 0 };

                                                            // Cálculo acumulado (Año) para coincidir con el tablero
                                                            const monthIdx = MONTHS.indexOf(currentPeriod);
                                                            const metrics = getIndicatorMetrics(ind, getElapsedCompletedMonths(monthIdx), false);
                                                            const canonicalReal = metrics.real;
                                                            const canonicalMeta = metrics.meta;
                                                            let periodPct = metrics.pct;

                                                            return (
                                                                <tr key={ind.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                                                    <td className="p-4 font-bold text-slate-800 text-xs sm:text-sm">{ind.nombre}</td>
                                                                    <td className="p-4 text-right font-black text-slate-700 text-[11px]">
                                                                        {monthIdx > 0 && (
                                                                            <span className="text-[8px] text-slate-400 block uppercase tracking-tighter">Mes: {currObj.meta}</span>
                                                                        )}
                                                                        <span className="text-slate-900">{canonicalMeta}</span>
                                                                        {monthIdx > 0 && (
                                                                            <span className="text-[7px] text-slate-500 block uppercase tracking-[0.1em] mt-0.5 opacity-60 font-black">Acumulado</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="p-4 text-right font-black text-slate-900 text-[11px]">
                                                                        {monthIdx > 0 && (
                                                                            <span className="text-[8px] text-slate-400 block uppercase tracking-tighter">Mes: {currObj.real}</span>
                                                                        )}
                                                                        <span className="text-slate-900">{canonicalReal}</span>
                                                                        {monthIdx > 0 && (
                                                                            <span className="text-[7px] text-slate-500 block uppercase tracking-[0.1em] mt-0.5 opacity-60 font-black">Acumulado</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="p-4 text-right text-xs">
                                                                        <span className={cn(
                                                                            "px-3 py-1 font-black rounded-lg text-[9px] tracking-widest",
                                                                            !metrics.isActive ? "bg-slate-100 text-slate-500" :
                                                                            periodPct >= (settings?.threshold_green ?? 90) ? "bg-emerald-100 text-emerald-700" :
                                                                                periodPct >= (settings?.threshold_amber ?? 75) ? "bg-amber-100 text-amber-700" :
                                                                                    "bg-red-100 text-red-700"
                                                                        )}>
                                                                            {!metrics.isActive ? 'N/A' : `${periodPct}%`}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}


                                {activeTab === 'MINUTA' && (
                                    <div className="bg-slate-50 p-8 md:p-12 rounded-[2rem] text-slate-900 shadow-2xl mx-auto print:shadow-none print:m-0 print:p-0">
                                        <div className="flex justify-between items-center mb-8 pb-4 border-b-2 border-slate-200">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-white border-2 border-slate-200 rounded-xl flex items-center justify-center text-slate-400">
                                                    <Calendar size={24} />
                                                </div>
                                                <h2 className="text-2xl font-black text-slate-800 tracking-tighter uppercase">MINUTA DE SESIÓN MENSUAL</h2>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setIsEditingReport(!isEditingReport)}
                                                    className={cn(
                                                        "px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                                        isEditingReport ? "bg-amber-500 text-white" : "bg-white border text-slate-600 hover:bg-slate-50"
                                                    )}
                                                >
                                                    {isEditingReport ? 'Salir de Edicion' : 'Editar Minuta'}
                                                </button>
                                                {isEditingReport && (
                                                    <button
                                                        onClick={() => handleSaveReport({ extra_tables: extraTables, extra_columns: extraColumns })}
                                                        className="bg-emerald-500 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-emerald-600"
                                                    >
                                                        Guardar Agenda
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-8">
                                            <h3 className="text-xs font-black uppercase text-slate-500 mb-4 tracking-[0.2em]">AGENDA Y ACUERDOS DE SESIÓN</h3>
                                            <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 overflow-hidden shadow-sm">
                                                <GenericTableEditor
                                                    isAdmin={isEditingReport}
                                                    columns={extraColumns}
                                                    onColumnsChange={setExtraColumns}
                                                    data={extraTables || []}
                                                    onChange={setExtraTables}
                                                />
                                            </div>
                                            <p className="text-[9px] text-slate-400 font-bold uppercase mt-4 text-center tracking-widest">
                                                * Administrador puede modificar columnas. Usuarios con permiso agregan filas y acuerdos. Asegure definir un RESPONSABLE y una FECHA COMPROMISO para cada acuerdo.
                                            </p>
                                        </div>

                                        <div className="mt-8 bg-white p-8 rounded-3xl border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
                                            <div className="flex items-center gap-3 text-slate-700 font-bold text-sm">
                                                <Info size={18} className="text-slate-800" />
                                                {isEditingReport ? (
                                                    <span className="flex items-center gap-2">Sesión mínima recomendada: <input type="number" value={extraColumns.find(c => c.key === '_minSession')?.label || '60'} onChange={e => {
                                                        const exist = extraColumns.find(c => c.key === '_minSession');
                                                        if (exist) {
                                                            setExtraColumns(extraColumns.map(c => c.key === '_minSession' ? { ...c, label: e.target.value } : c));
                                                        } else {
                                                            setExtraColumns([...extraColumns, { key: '_minSession', label: e.target.value }]);
                                                        }
                                                    }} className="w-16 px-1 border rounded" /> minutos</span>
                                                ) : (
                                                    <span>Sesión mínima recomendada: {extraColumns.find(c => c.key === '_minSession')?.label || '60'} minutos</span>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                )}


                                {activeTab === 'TRIMESTRAL' && (
                                    <div className="bg-slate-50 p-8 md:p-12 rounded-[2rem] text-slate-900 shadow-2xl mx-auto">
                                        <div className="flex justify-between items-center bg-slate-200/50 p-2 rounded-2xl mb-8 border border-slate-200 w-fit mx-auto relative z-10">
                                            {['T1', 'T2', 'T3', 'T4'].map((q, i) => (
                                                <button key={q} className={cn("px-6 py-2 rounded-xl text-xs md:text-sm font-black transition-all", i === 0 ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-700')}>
                                                    {q}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-6 border-b border-slate-200 pb-8">
                                            <h3 className="flex items-center gap-3 text-slate-800 font-black uppercase text-xl md:text-2xl tracking-tighter">
                                                <Activity size={32} className="text-indigo-600" /> REPORTE TRIMESTRAL DE RESULTADOS
                                            </h3>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => setIsEditingReport(!isEditingReport)}
                                                    className={cn(
                                                        "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm transition-all",
                                                        isEditingReport ? "bg-amber-500 text-white" : "bg-white border text-slate-600 hover:bg-slate-50"
                                                    )}
                                                >
                                                    {isEditingReport ? 'Ver Reporte' : 'Ajustar Datos'}
                                                </button>
                                                {isEditingReport && (
                                                    <button
                                                        onClick={() => handleSaveReport({ observations, summary: execSummary })}
                                                        className="bg-emerald-500 text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg"
                                                    >
                                                        Consolidar Trimestre
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                                            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 text-center shadow-sm group hover:border-indigo-500 transition-colors">
                                                <div className="text-4xl md:text-5xl font-black text-slate-800 mb-2 tracking-tighter">
                                                    {Math.round(calculateProratedCompliance(indicadores, getElapsedCompletedMonths(MONTHS.indexOf(selectedMonth)), false, true).cumplimiento || 0)}%
                                                </div>
                                                <div className="text-[10px] md:text-xs text-slate-400 font-black uppercase tracking-[0.2em]">RESUMEN TRIMESTRAL: Cump. Global</div>
                                                <div className="mt-4 inline-flex items-center gap-1 text-emerald-500 text-[10px] font-black bg-emerald-50 px-3 py-1 rounded-full"><TrendingUp size={14} /> +3.2% vs T_Ant</div>
                                            </div>

                                            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 text-center shadow-sm group hover:border-indigo-500 transition-colors">
                                                <div className="text-4xl md:text-5xl font-black text-slate-800 mb-2 tracking-tighter">87%</div>
                                                <div className="text-[10px] md:text-xs text-slate-400 font-black uppercase tracking-[0.2em]">Part. Miembros</div>
                                                <div className="mt-4 inline-flex items-center gap-1 text-emerald-500 text-[10px] font-black bg-emerald-50 px-3 py-1 rounded-full"><TrendingUp size={14} /> OK</div>
                                            </div>

                                            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 text-center shadow-sm group hover:border-indigo-500 transition-colors">
                                                <div className="text-4xl md:text-5xl font-black text-slate-800 mb-2 tracking-tighter">15</div>
                                                <div className="text-[10px] md:text-xs text-slate-400 font-black uppercase tracking-[0.2em]">Acuerdos OK</div>
                                                <div className="mt-4 inline-flex items-center gap-1 text-indigo-500 text-[10px] font-black bg-indigo-50 px-3 py-1 rounded-full">TOTAL T1</div>
                                            </div>

                                            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 text-center shadow-sm group hover:border-indigo-500 transition-colors">
                                                <div className="text-4xl md:text-5xl font-black text-slate-800 mb-2 tracking-tighter text-emerald-600">A+</div>
                                                <div className="text-[10px] md:text-xs text-slate-400 font-black uppercase tracking-[0.2em]">Salud Inst.</div>
                                                <div className="mt-4 inline-flex items-center gap-1 text-emerald-500 text-[10px] font-black bg-emerald-50 px-3 py-1 rounded-full">EXCELENTE</div>
                                            </div>
                                        </div>


                                        <h3 className="flex items-center gap-2 text-slate-800 font-black uppercase text-base md:text-lg mb-4 mt-10">
                                            <TrendingUp size={20} className="text-indigo-600" /> Avance por Objetivo Estratégico
                                        </h3>
                                        <div className="space-y-6">
                                            <div>
                                                <div className="flex justify-between text-xs md:text-sm font-bold text-slate-700 mb-2">
                                                    <span>Objetivo 1: Alineamiento estratégico</span>
                                                    <span className="text-indigo-700">88%</span>
                                                </div>
                                                <div className="h-4 bg-slate-200 rounded-full w-full overflow-hidden">
                                                    <div className="h-full bg-slate-900 rounded-full" style={{ width: '88%' }}></div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex justify-between text-xs md:text-sm font-bold text-slate-700 mb-2">
                                                    <span>Objetivo 2: Participación activa</span>
                                                    <span className="text-indigo-700">94%</span>
                                                </div>
                                                <div className="h-4 bg-slate-200 rounded-full w-full overflow-hidden">
                                                    <div className="h-full bg-slate-900 rounded-full" style={{ width: '94%' }}></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-12 pt-8 border-t border-slate-200">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                                <div className="border-b border-slate-200 pb-2">
                                                    <div className="h-12"></div>
                                                    <p className="text-[8px] font-black uppercase text-slate-400 text-center">Validado por el Presidente de la Instancia</p>
                                                </div>
                                                <div className="border-b border-slate-200 pb-2">
                                                    <div className="h-12"></div>
                                                    <p className="text-[8px] font-black uppercase text-slate-400 text-center">Aprobado por el Vicepresidente Nacional CPX</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </main>

            {
                showReplicaModal && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-2xl">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 30 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            className="bg-slate-900 border border-white/5 p-6 md:p-10 rounded-[2.5rem] md:rounded-[4rem] w-full max-w-4xl shadow-[0_0_120px_rgba(0,0,0,0.9)] max-h-[96vh] overflow-hidden flex flex-col relative"
                        >
                            {/* Dynamic Glow Background */}
                            <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-emerald-500/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                            <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-indigo-500/5 blur-[100px] rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

                            <header className="mb-8 md:mb-12 flex justify-between items-start relative z-10 shrink-0">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="bg-emerald-500/10 text-emerald-500 px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border border-emerald-500/20 shadow-[0_0_20px_rgba(16,185,129,0.1)]">Sincronización segura</span>
                                        {isAdmin && <span className="bg-white/5 text-white/40 px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest italic border border-white/5">{APP_VERSION}</span>}
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tighter leading-none italic">
                                        Sincronizacion <span className="text-emerald-500 text-glow-emerald">Segura</span>
                                    </h2>
                                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-3">Origen de sincronización: <span className="text-emerald-500">{allInstances.find((c: any) => c.id === selectedInstancia)?.name || 'Instancia origen'}</span></div>
                                </div>
                                <button
                                    onClick={() => setShowReplicaModal(false)}
                                    className="bg-slate-950/80 p-4 rounded-2xl text-white/30 hover:text-white hover:bg-slate-900 transition-all border border-white/5 shadow-2xl active:scale-90"
                                >
                                    <X size={20} strokeWidth={3} />
                                </button>
                            </header>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8 relative z-10 shrink-0">
                                {[
                                    {
                                        mode: 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA',
                                        title: 'Indicadores + orden',
                                        desc: 'Recomendado. Conserva metas, avances, notas y checklists.'
                                    },
                                    {
                                        mode: 'CATALOG_ONLY_KEEP_DESTINATION_DATA',
                                        title: 'Solo catalogo',
                                        desc: 'Crea faltantes sin cambiar orden ni datos capturados.'
                                    },
                                    {
                                        mode: 'CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS',
                                        title: 'Catalogo + orden + metas',
                                        desc: 'Copia metas del origen, conserva avances y evidencias.'
                                    },
                                    {
                                        mode: 'FULL_STRUCTURE_REPLACE_EXPLICIT',
                                        title: 'Reemplazo completo',
                                        desc: 'No disponible. Requiere fase futura con backup y Cardex SYSTEM.',
                                        disabled: true
                                    }
                                ].map((option: any) => (
                                    <button
                                        key={option.mode}
                                        disabled={option.disabled}
                                        onClick={() => setIndicatorSyncMode(option.mode)}
                                        className={cn(
                                            "text-left p-4 rounded-2xl border transition-all",
                                            indicatorSyncMode === option.mode
                                                ? "bg-emerald-600/20 border-emerald-500 text-white"
                                                : "bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-600",
                                            option.disabled && "opacity-40 cursor-not-allowed hover:border-slate-800"
                                        )}
                                    >
                                        <div className="text-[10px] font-black uppercase tracking-widest">{option.title}</div>
                                        <div className="text-[9px] font-bold uppercase tracking-wider mt-1 opacity-60">{option.desc}</div>
                                    </button>
                                ))}
                            </div>

                            {/* Enhanced Fast Sync Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-14 relative z-10 shrink-0">
                                {[
                                    { type: 'COMISION', icon: Shield, color: 'indigo', ids: allInstances.filter(c => normalizeOwnerType(c.type) === 'COMISION').map(c => c.id) },
                                    { type: 'COMITE', icon: LayoutGrid, color: 'emerald', ids: allInstances.filter(c => normalizeOwnerType(c.type) === 'COMITE').map(c => c.id) },
                                    { type: 'CONSEJERIA', label: 'CONSEJERIA', icon: Users, color: 'amber', ids: allInstances.filter(c => normalizeOwnerType(c.type) === 'CONSEJERIA').map(c => c.id) }
                                ].map((group: any) => {
                                    const type = normalizeOwnerType(group.type || group.label);
                                    return {
                                        ...group,
                                        type,
                                        ids: allInstances.filter(c => normalizeOwnerType(c.type) === type).map(c => c.id)
                                    };
                                }).map(group => (
                                    <button
                                        key={group.type}
                                        onClick={() => {
                                            const alreadySelected = group.ids.length > 0 && group.ids.every(id => replicaTargets.includes(id));
                                            if (alreadySelected) {
                                                setReplicaTargets(prev => prev.filter(id => !group.ids.includes(id)));
                                            } else {
                                                setReplicaTargets(prev => [...new Set([...prev, ...group.ids])]);
                                            }
                                        }}
                                        className={cn(
                                            "flex flex-col items-center justify-center gap-3 p-4 md:p-8 rounded-[2rem] md:rounded-[3rem] border-2 transition-all group/btn relative overflow-hidden",
                                            group.color === 'indigo' ? (group.ids.length > 0 && group.ids.every(id => replicaTargets.includes(id)) ? "bg-indigo-600 border-indigo-500" : "bg-indigo-500/5 border-indigo-500/10 hover:border-indigo-500/40") :
                                                group.color === 'emerald' ? (group.ids.length > 0 && group.ids.every(id => replicaTargets.includes(id)) ? "bg-emerald-600 border-emerald-500" : "bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/40") :
                                                    (group.ids.length > 0 && group.ids.every(id => replicaTargets.includes(id)) ? "bg-amber-600 border-amber-500" : "bg-amber-500/5 border-amber-500/10 hover:border-amber-500/40")
                                        )}
                                    >
                                        <group.icon size={24} className={cn("transition-colors", (group.ids.length > 0 && group.ids.every(id => replicaTargets.includes(id))) ? "text-white" : "text-white/20 group-hover/btn:text-white/60")} />
                                        <span className={cn("text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-colors", (group.ids.length > 0 && group.ids.every(id => replicaTargets.includes(id))) ? "text-white" : "text-slate-500 group-hover/btn:text-white")}>{getOwnerTypeLabel(group.type)}</span>
                                    </button>
                                ))}
                                <button
                                    onClick={() => setReplicaTargets([])}
                                    className="flex flex-col items-center justify-center gap-3 p-4 md:p-8 rounded-[2rem] md:rounded-[3rem] bg-slate-950/20 border-2 border-slate-800/50 hover:bg-red-600/10 hover:border-red-500 transition-all group/btn"
                                >
                                    <RotateCcw size={24} className="text-slate-700 group-hover/btn:text-red-500 transition-colors" />
                                    <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-slate-700 group-hover/btn:text-red-500">Limpiar selección</span>
                                </button>
                            </div>

                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 relative z-10 shrink-0">Destinos de sincronización</div>
                            <div className="flex flex-col md:flex-row gap-5 items-center mb-6 md:mb-10 relative z-10 shrink-0">
                                <div className="relative flex-1 w-full group">
                                    <Search className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-emerald-500 transition-colors" size={20} />
                                    <input
                                        placeholder="Buscar destino..."
                                        className="w-full bg-slate-950 border-2 border-slate-800 rounded-[2rem] h-[60px] md:h-[68px] pl-18 p-6 text-[14px] font-bold text-white focus:border-emerald-500 outline-none transition-all placeholder:text-slate-800 shadow-2xl"
                                        value={replicaSearch}
                                        onChange={e => setReplicaSearch(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-1 p-1 bg-slate-950 rounded-[1.2rem] border border-slate-800 shadow-inner">
                                    {(['TODOS', 'COMISION', 'COMITE', 'CONSEJERIA'] as const).map(t => (
                                        <button
                                            key={t}
                                            onClick={() => setReplicaTypeFilter(normalizeOwnerType(t) as any)}
                                            className={cn(
                                                "px-4 md:px-8 py-2 md:py-3 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all",
                                                normalizeOwnerType(replicaTypeFilter) === normalizeOwnerType(t) ? "bg-emerald-600 text-white shadow-xl shadow-emerald-500/30" : "text-slate-600 hover:text-white hover:bg-slate-900"
                                            )}
                                        >
                                            {getOwnerTypeLabel(t)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Grid de Destinos Seleccionables */}
                            <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar mb-8 relative z-10">
                                {filteredReplicaComms.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-slate-700 py-20">
                                        <Search size={48} className="mb-4 opacity-20" />
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em]">No se encontraron destinos con ese criterio</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {filteredReplicaComms.map((inst: any) => (
                                            <div
                                                key={inst.id}
                                                onClick={() => toggleReplicaTarget(inst.id)}
                                                className={cn(
                                                    "p-6 rounded-3xl border-2 transition-all cursor-pointer flex items-center justify-between group",
                                                    replicaTargets.includes(inst.id)
                                                        ? "bg-indigo-600/20 border-indigo-500 shadow-lg shadow-indigo-500/10"
                                                        : "bg-slate-950 border-slate-800 hover:border-slate-700"
                                                )}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className={cn(
                                                        "w-10 h-10 rounded-xl flex items-center justify-center border transition-all",
                                                        replicaTargets.includes(inst.id) ? "bg-indigo-500 border-indigo-400" : "bg-slate-900 border-slate-800"
                                                    )}>
                                                        {normalizeOwnerType(inst.type) === 'COMISION' ? <Shield size={18} className="text-white" /> : normalizeOwnerType(inst.type) === 'COMITE' ? <LayoutGrid size={18} className="text-white" /> : <Users size={18} className="text-white" />}
                                                    </div>
                                                    <div>
                                                        <p className="text-[11px] font-black text-white uppercase tracking-tighter leading-tight group-hover:text-indigo-400 transition-colors">{inst.name}</p>
                                                        <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mt-1">{getOwnerTypeLabel(inst.type)}</p>
                                                    </div>
                                                </div>
                                                <div className={cn(
                                                    "w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all",
                                                    replicaTargets.includes(inst.id) ? "bg-indigo-500 border-indigo-400" : "border-slate-800"
                                                )}>
                                                    {replicaTargets.includes(inst.id) && <Check size={14} className="text-white" strokeWidth={4} />}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <footer className="mt-auto pt-6 md:pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10 bg-slate-900/95 backdrop-blur-xl -mx-6 md:-mx-10 -mb-6 md:-mb-10 p-6 md:p-10 sticky bottom-0">
                                <div className="text-[10px] font-black text-slate-600 uppercase">
                                    <span className="text-indigo-400">{replicaTargets.length}</span> Destinos Seleccionados
                                </div>
                                <div className="flex gap-4 w-full md:w-auto">
                                    <button onClick={() => setShowReplicaModal(false)} className="flex-1 md:flex-none px-6 py-4 rounded-2xl text-[10px] font-black uppercase text-white/70 hover:text-white transition-all bg-slate-950/50">Cancelar</button>
                                    <button
                                        onClick={handleReplicateIndicators}
                                        disabled={replicaTargets.length === 0 || loading}
                                        className={cn(
                                            "flex-[2] md:flex-none px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2",
                                            replicaTargets.length > 0 && !loading
                                                ? "bg-indigo-600 text-white hover:bg-indigo-500 shadow-xl shadow-indigo-500/20"
                                                : "bg-slate-800 text-slate-600 cursor-not-allowed"
                                        )}
                                    >
                                        {loading ? <RefreshCw size={14} className="animate-spin" /> : <Layers size={14} />}
                                        {loading ? 'PROCESANDO...' : 'Ejecutar sincronización'}
                                    </button>
                                </div>
                            </footer>
                        </motion.div>
                    </div>
                )
            }

            {/* GESTOR DE METAS: Modal global con overlay */}
            {isBulkCapture && activeIndicator && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-8 px-4">
                    <div className="w-full max-w-5xl">
                        <BulkCaptureView ind={activeIndicator} onClose={() => setIsBulkCapture(false)} />
                    </div>
                </div>
            )}

            {/* CHECKLIST MANAGER: Modal global por periodo */}
            {checklistOpen && (() => {
                const indChecklist = indicadores.find(i => i.id === checklistOpen.indId);
                return indChecklist ? (
                    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-8 px-4">
                        <div className="w-full max-w-5xl">
                            <ChecklistManagerView
                                key={checklistOpen.periodIdx}
                                ind={indChecklist}
                                periodIdx={checklistOpen.periodIdx}
                                onClose={() => setChecklistOpen(null)}
                                onNavigate={(newIdx) => setChecklistOpen({ ...checklistOpen, periodIdx: newIdx })}
                            />
                        </div>
                    </div>
                ) : null;
            })()}

                </>
            )}
        </div>
    );
};

const FORENSIC_MODE = false;

const VPDashboardPanel = ({ vpId, hierarchy, settings, selectedMonth, dashboardData, livePlanTotals, onSelectInstance, user }: any) => {
    const vp = hierarchy?.vicepresidencies?.find((v: any) => v.id === vpId);
    
    if (!vp) {
        return (
            <div className="flex flex-col items-center justify-center p-20 space-y-4 bg-slate-900/50 border border-slate-800 rounded-[2rem]">
                <Activity className="w-12 h-12 text-emerald-500 animate-spin" />
                <p className="text-white/40 text-xs font-black uppercase tracking-widest">Cargando datos de Vicepresidencia...</p>
            </div>
        );
    }
    
    const vpInstances = useMemo(() => {
        if (!vp) return [];
        return [
            ...(vp.comisiones || []).map((c: any) => ({ ...c, type: COMISION_TYPE })),
            ...(vp.comites || []).map((c: any) => ({ ...c, type: 'COMITE' })),
            ...(vp.consejerias || []).map((c: any) => ({ ...c, type: 'CONSEJERIA' }))
        ];
    }, [vp]);

    const isUserAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR';
    const [plans, setPlans] = useState<any[]>([]);
    const [panelLiveTotals, setPanelLiveTotals] = useState<any[]>([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [liveKpiResults, setLiveKpiResults] = useState<Record<string, { compliance: number | null, eligible: boolean, totalMeta: number, totalReal: number }>>({});
    const [planReadError, setPlanReadError] = useState<string | null>(null);
    const [kpiReadError, setKpiReadError] = useState<string | null>(null);

    const handleApprovePlan = async (commissionId: string) => {
        try {
            const plan = plans.find(p => p.commission_id === commissionId);
            if (!plan) return;
            await db.approveWorkPlan(plan.id, user.id);
            setPlans(prev => prev.map(p => p.id === plan.id? { ...p, status: 'APROBADO' } : p));
        } catch (err) {
            console.error("Error approving plan:", err);
        }
    };

    useEffect(() => {
        const fetchPlans = async () => {
            if (!vpId) return;
            setLoadingPlans(true);
            setPlanReadError(null);
            try {
                // Obtenemos los IDs de las instancias de esta VP
                const currentVP = hierarchy?.vicepresidencies?.find((v: any) => v.id === vpId);
                if (!currentVP) {
                    setLoadingPlans(false);
                    return;
                }
                
                const currentVPInstances = [
                    ...(currentVP.comisiones || []),
                    ...(currentVP.comites || []),
                    ...(currentVP.consejerias || [])
                ];
                const instanceIds = currentVPInstances.map((i: any) => i.id);

                const [scopedPlans, liveTotals] = await Promise.all([
                    db.getWorkPlans({ role: 'VICE_PRESIDENT', vicepresidency_id: vpId, org_scope: 'VP' } as any),
                    instanceIds.length > 0 ? db.getWorkPlanLiveTotalsByCommissionIds(instanceIds) : Promise.resolve([])
                ]);
                setPanelLiveTotals(liveTotals || []);

                const processedPlans = (scopedPlans || [])
                    .filter((plan: any) => instanceIds.includes(plan.commission_id) && Number(plan.year) === 2026)
                    .map(plan => {
                    return {
                        ...plan,
                        total_goal: plan.total_goal ?? 0,
                        total_progress: plan.total_progress ?? 0,
                        kpi_compliance: plan.kpi_compliance ?? 0
                    };
                });

                setPlans(processedPlans);
            } catch (err: unknown) {
                console.error("Error fetching VP plans:", err);
                setPlanReadError(db.getScopedReadErrorMessage(err));
            } finally {
                setLoadingPlans(false);
            }
        };
        fetchPlans();
    }, [vpId, hierarchy]);

    const monthIdx = MONTHS.indexOf(selectedMonth);

    useEffect(() => {
        const fetchLiveKpis = async () => {
            if (!vpId || !vpInstances.length) return;
            setKpiReadError(null);
            try {
                const ids = vpInstances.map((i: any) => i.id);
                
                let expandedIds = [...ids];
                const allInstances = getHierarchyInstances(hierarchy);

                if (hierarchy) {
                    const targetNames = vpInstances.map((i: any) => normalize(i.name)).filter(Boolean);
                    const additionalIds = allInstances
                        .filter((c: any) => targetNames.includes(normalize(c.name)))
                        .map((c: any) => c.id);
                    expandedIds = Array.from(new Set([...expandedIds, ...additionalIds]));
                }

                // resolveIndicatorsForInstance Logic (v18.13.4)
                const indicators = await db.getIndicators(expandedIds);
                const results: Record<string, { compliance: number | null, eligible: boolean, totalMeta: number, totalReal: number }> = {};
                const mIdx = MONTHS.indexOf(selectedMonth);

                ids.forEach(id => {
                    let instanceIdsToMatch = [id];
                    if (hierarchy) {
                        const current = allInstances.find((c: any) => c.id === id);
                        if (current && current.name) {
                            const cName = normalize(current.name);
                            instanceIdsToMatch = allInstances
                                .filter((c: any) => normalize(c.name) === cName)
                                .map((c: any) => c.id);
                        }
                    }

                    const instInds = indicators.filter((i: any) => {
                        const iid = i.instance_id || i.commission_id;
                        return instanceIdsToMatch.includes(iid);
                    });
                    const { cumplimiento, totalMeta, totalReal } = calculateProratedCompliance(instInds, getElapsedCompletedMonths(mIdx), false, true);
                    results[id] = {
                        compliance: cumplimiento,
                        eligible: cumplimiento !== null,
                        totalMeta: totalMeta || 0,
                        totalReal: totalReal || 0
                    };
                });

                if (FORENSIC_MODE) {
                    console.log(`[VP_LIVE_KPI_TRACE] VP: ${vpId} - Mes: ${selectedMonth}`);
                    console.table(Object.entries(results).map(([id, d]) => ({
                        Instancia: vpInstances.find((i: any) => i.id === id)?.name || id,
                        Cumplimiento: d.compliance,
                        Elegible: d.eligible
                    })));
                }
                setLiveKpiResults(results);
            } catch (err: unknown) {
                console.error("[FETCH_LIVE_KPIS_ERROR]", err);
                setKpiReadError(db.getScopedReadErrorMessage(err));
            }
        };
        fetchLiveKpis();
    }, [vpId, selectedMonth, vpInstances]);

    const instancesWithData = useMemo(() => {
        return vpInstances.map(inst => {
            const data = dashboardData?.find((d: any) => d.commission_id === inst.id);
            const liveKpi = liveKpiResults[inst.id];

            // RESOLUCIÓN DETERMINISTA DE TOTALES VIVOS DE PLAN (v18.27.73)
            const normalizeId = (idVal: any): string => (typeof idVal === 'string' ? idVal.trim().toLowerCase() : String(idVal || '').trim().toLowerCase());
            const targetKeys = new Set([
                normalizeId(inst?.id),
                normalizeId(inst?.commission_id),
                normalizeId(inst?.instance_id)
            ].filter(Boolean));

            const combinedLiveTotals = [
                ...(panelLiveTotals || []),
                ...(livePlanTotals || [])
            ];

            const livePlanTotalObj = combinedLiveTotals.find((p: any) => {
                const pComm = normalizeId(p?.commission_id);
                const pInst = normalizeId(p?.instance_id);
                const pId = normalizeId(p?.id);
                return (pComm && targetKeys.has(pComm)) || (pInst && targetKeys.has(pInst)) || (pId && targetKeys.has(pId));
            });
            const livePlanDbObj = plans?.find((p: any) => {
                const pComm = normalizeId(p?.commission_id);
                const pInst = normalizeId(p?.instance_id);
                const pId = normalizeId(p?.id);
                return (pComm && targetKeys.has(pComm)) || (pInst && targetKeys.has(pInst)) || (pId && targetKeys.has(pId));
            });

            const hasValidLiveTotal = livePlanTotalObj && typeof livePlanTotalObj.total_goal === 'number' && livePlanTotalObj.total_goal > 0;
            const planGoal = hasValidLiveTotal ? livePlanTotalObj.total_goal : (livePlanDbObj?.total_goal ?? 0);
            const planProgressRaw = hasValidLiveTotal ? (livePlanTotalObj.total_progress ?? 0) : (livePlanDbObj?.total_progress ?? 0);
            const planStatus = livePlanDbObj?.status || livePlanTotalObj?.status || inst?.status || 'SIN_PLAN';
            const vpApproved = livePlanDbObj?.vp_approved === true || livePlanTotalObj?.vp_approved === true;

            // Blindaje contra division by zero, NaN y valores nulos
            const planPct = (typeof planGoal === 'number' && planGoal > 0)
                ? Math.min(100, Math.round(((planProgressRaw || 0) / planGoal) * 100))
                : 0;

            const planEligible = isVpPlanEvaluable({ planGoal });

            const kpiMeta = liveKpi ? liveKpi.totalMeta : parseNumberSafe(data?.total_meta || data?.meta_anual, 0);
            const kpiReal = liveKpi ? liveKpi.totalReal : parseNumberSafe(data?.total_real || data?.real_anual, 0);
            const kpiEligible = liveKpi
                ? isVpKpiEvaluable({ kpiEligible: liveKpi.eligible, kpiMeta })
                : isVpKpiEvaluable({ hasActiveKpis: kpiMeta > 0, kpiMeta });
            const fallbackCompliance = parseNumberSafe(data?.total_compliance || data?.kpi_compliance, 0);
            const kpiCompliance = liveKpi ? liveKpi.compliance : (kpiEligible ? fallbackCompliance : null);

            return {
                ...inst,
                planGoal,
                planProgress: planPct, 
                planProgressValue: planProgressRaw,
                planEligible,
                kpiCompliance,
                kpiEligible,
                kpiMeta,
                kpiReal,
                hasActivePlan: planEligible, 
                hasActiveKpis: kpiEligible, 
                hasData: true,
                planStatus,
                vpApproved
            };
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [vpInstances, dashboardData, plans, liveKpiResults, livePlanTotals]);


    // v18.9.28: Uso de Lógica Centralizada de shared-logic para evitar discrepancias
    const vpCalculationSettings = useMemo(
        () => buildVpPerformanceSettings(instancesWithData, settings),
        [instancesWithData, settings]
    );
    const hasAnyPlanEligible = useMemo(() => instancesWithData.some(inst => inst.planEligible), [instancesWithData]);
    const hasAnyKpiEligible = useMemo(() => instancesWithData.some(inst => inst.kpiEligible), [instancesWithData]);

    const vpPerformance = useMemo(() => {
        const perf = calculateVPPerformance(instancesWithData, vpCalculationSettings);
        if (FORENSIC_MODE) {
        console.log('[VP_GOLDEN_CHECK] VP:', vpId);
        console.table(instancesWithData.map(i => ({
            instancias: i.name,
            type: i.type,
            planProgress: i.planProgress,
            kpiCompliance: i.kpiCompliance,
            hasActivePlan: i.hasActivePlan,
            hasActiveKpis: i.hasActiveKpis
        })));
        console.log(`vpPerf.plan (Gestión): ${perf.plan}, vpPerf.kpi (Impacto): ${perf.kpi}, vpPerf.global (Global): ${perf.global}`);
        }
        return perf;
    }, [instancesWithData, vpCalculationSettings, vpId]);

    const instancesWithPerformance = useMemo(() => instancesWithData.map((inst) => {
        const performance = calculateVPPerformance([inst], vpCalculationSettings);
        return {
            ...inst,
            weightedPerformance: performance.hasApplicableData && typeof performance.global === 'number'
                ? performance.global
                : null
        };
    }), [instancesWithData, vpCalculationSettings]);

    const finalPlanProgress = vpPerformance.plan ?? 0;
    const finalKpiCompliance = vpPerformance.kpi ?? 0;
    const totalScore = vpPerformance.global;
    const hasApplicableTotalScore = !!vpPerformance.hasApplicableData && totalScore !== null;
    const totalScoreVisualState = hasApplicableTotalScore ? getComplianceVisualState(totalScore, settings) : null;
    const vpPlanVisualState = hasAnyPlanEligible ? getComplianceVisualState(finalPlanProgress, settings) : null;
    const vpKpiVisualState = hasAnyKpiEligible ? getComplianceVisualState(finalKpiCompliance, settings) : null;
    const analysisMonthLabel = getVpAnalysisMonthLabel(selectedMonth);

    // TRACE: VP_PERFORMANCE_MODEL_V18.9.8_STABLE_IDENTIFIED


    const getSemaphoreColor = (val: number) => {
        return getComplianceVisualState(val, settings).textClass;
    };

    const getSemaphoreBg = (val: number) => {
        return getComplianceVisualState(val, settings).bgClass;
    };

    const getSemaphoreHoverBorder = (val: number) => {
        const state = getComplianceVisualState(val, settings).semanticState;
        if (state === 'green') return 'hover:border-emerald-500/30';
        if (state === 'amber') return 'hover:border-amber-500/30';
        return 'hover:border-red-500/30';
    };

    const getSemaphoreTextHover = (val: number) => {
        const state = getComplianceVisualState(val, settings).semanticState;
        if (state === 'green') return 'group-hover:text-emerald-400';
        if (state === 'amber') return 'group-hover:text-amber-400';
        return 'group-hover:text-red-400';
    };

    const getSemaphoreChevronHover = (val: number) => {
        const state = getComplianceVisualState(val, settings).semanticState;
        if (state === 'green') return 'group-hover:text-emerald-500';
        if (state === 'amber') return 'group-hover:text-amber-500';
        return 'group-hover:text-red-500';
    };

    const getVPHeaderBorder = (state: 'green' | 'amber' | 'red' | null) => {
        if (state === 'green') return 'border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.2)]';
        if (state === 'amber') return 'border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.2)]';
        if (state === 'red') return 'border-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.2)]';
        return 'border-slate-700/80 shadow-none';
    };

    if (planReadError || kpiReadError) return (
        <div className="p-8 rounded-3xl border border-red-500/30 bg-red-500/10 text-center space-y-4">
            <AlertTriangle className="mx-auto text-red-400" size={36} />
            <h2 className="text-red-200 font-black uppercase tracking-widest">Fallo de lectura del tablero VP</h2>
            <p className="text-white/70 text-sm">{planReadError || kpiReadError}</p>
            <p className="text-white/40 text-xs">Las metricas N/A no sustituyen este error remoto.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
            <div className={cn(
                "bg-slate-900 border-2 p-4 rounded-2xl relative overflow-hidden group transition-all duration-500",
                getVPHeaderBorder(totalScoreVisualState?.semanticState ?? null)
            )}>
                <div className={cn(
                    "absolute top-0 right-0 w-64 h-64 rounded-full -mr-32 -mt-32 blur-[80px] transition-colors duration-500",
                    totalScoreVisualState?.semanticState === 'green' ? "bg-emerald-500/5" :
                    totalScoreVisualState?.semanticState === 'amber' ? "bg-amber-500/5" :
                    totalScoreVisualState?.semanticState === 'red' ? "bg-red-500/5" : "bg-slate-500/5"
                )}></div>
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className={cn(
                            "w-10 h-10 rounded-xl p-0.5 shadow-lg bg-gradient-to-br transition-all duration-500",
                            totalScoreVisualState?.semanticState === 'green' ? "from-emerald-500 to-teal-700" :
                            totalScoreVisualState?.semanticState === 'amber' ? "from-amber-500 to-orange-700" :
                            totalScoreVisualState?.semanticState === 'red' ? "from-red-500 to-rose-700" : "from-slate-600 to-slate-800"
                        )}>
                            <div className={cn(
                                "w-full h-full bg-slate-950 rounded-[0.7rem] flex items-center justify-center transition-colors duration-500",
                                hasApplicableTotalScore ? getSemaphoreColor(totalScore) : 'text-slate-500'
                            )}>
                                <Crown size={18} strokeWidth={2} />
                            </div>
                        </div>
                        <div>
                            <p className={cn(
                                "text-[8px] font-black uppercase tracking-[0.2em] mb-0.5 leading-none transition-colors duration-500",
                                hasApplicableTotalScore ? getSemaphoreColor(totalScore) : 'text-slate-500'
                            )}>Vicepresidencia Estratégica</p>
                            <h1 className="text-xl font-black text-white tracking-tighter uppercase leading-none">{vp.name}</h1>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-900/40 p-2 px-5 rounded-xl border border-slate-800/50 backdrop-blur-md">
                        <div className="grid grid-cols-2 gap-4 text-right">
                            <div>
                                <p className="text-[8px] text-emerald-400 font-black uppercase tracking-widest">Periodo Analizado</p>
                                <p className="text-xs text-white font-black uppercase">{analysisMonthLabel} 2026</p>
                            </div>
                            <div>
                                <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest">Foco Actual</p>
                                <p className="text-xs text-white font-black uppercase">{selectedMonth} 2026</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <h3 className={`text-3xl font-black tracking-tighter leading-none ${hasApplicableTotalScore ? getSemaphoreColor(totalScore) : 'text-slate-500'}`}>
                                {hasApplicableTotalScore ? `${totalScore}%` : 'N/A'}
                            </h3>
                        </div>
                        <div className="flex gap-1.5">
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] transition-all duration-500 ${totalScoreVisualState?.semanticState === 'green'? 'bg-emerald-500 shadow-emerald-500/50' : 'bg-slate-800'}`}></div>
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] transition-all duration-500 ${totalScoreVisualState?.semanticState === 'amber'? 'bg-amber-500 shadow-amber-500/50' : 'bg-slate-800'}`}></div>
                            <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] transition-all duration-500 ${totalScoreVisualState?.semanticState === 'red'? 'bg-red-500 shadow-red-500/50' : 'bg-slate-800'}`}></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div title={hasAnyPlanEligible ? `${finalPlanProgress}%. ${vpPlanVisualState?.labelState}. Avance agregado de Plan.` : 'Plan no aplicable.'} aria-label={hasAnyPlanEligible ? `Plan de la Vicepresidencia: ${finalPlanProgress} por ciento, ${vpPlanVisualState?.labelState}.` : 'Plan de la Vicepresidencia: no aplicable.'} className={cn("bg-slate-900/40 border p-4 rounded-2xl group", vpPlanVisualState?.borderClass || 'border-slate-800')}>
                    <div className="flex justify-between items-center mb-2">
                        <div className={cn("p-1.5 rounded-lg border", vpPlanVisualState ? [vpPlanVisualState.bgSoftClass, vpPlanVisualState.textClass, vpPlanVisualState.borderClass] : 'bg-slate-900 text-slate-500 border-slate-700')}><Briefcase size={14} /></div>
                        <div className="text-right">
                            <span className={cn("text-lg font-black", vpPlanVisualState?.textSoftClass || "text-slate-500")}>{hasAnyPlanEligible ? `${finalPlanProgress}%` : 'N/A'}</span>
                            <p className="text-[7px] font-black uppercase text-white/30 tracking-widest">Gestión</p>
                        </div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden mb-2">
                        <div className={cn("h-full transition-all duration-1000", vpPlanVisualState?.bgClass || 'bg-slate-700')} style={{ width: `${hasAnyPlanEligible ? finalPlanProgress : 0}%` }}></div>
                    </div>
                </div>

                <div title={hasAnyKpiEligible ? `${finalKpiCompliance}%. ${vpKpiVisualState?.labelState}. Cumplimiento agregado de Indicadores.` : 'Indicadores no aplicables.'} aria-label={hasAnyKpiEligible ? `Indicadores de la Vicepresidencia: ${finalKpiCompliance} por ciento, ${vpKpiVisualState?.labelState}.` : 'Indicadores de la Vicepresidencia: no aplicables.'} className={cn("bg-slate-900/40 border p-4 rounded-2xl group", vpKpiVisualState?.borderClass || 'border-slate-800')}>
                    <div className="flex justify-between items-center mb-3">
                        <div className={cn("p-2 rounded-lg border", vpKpiVisualState ? [vpKpiVisualState.bgSoftClass, vpKpiVisualState.textClass, vpKpiVisualState.borderClass] : 'bg-slate-900 text-slate-500 border-slate-700')}><TrendingUp size={16} /></div>
                        <div className="text-right">
                            <span className={cn("text-xl font-black", vpKpiVisualState?.textSoftClass || "text-slate-500")}>{hasAnyKpiEligible ? `${finalKpiCompliance}%` : 'N/A'}</span>
                            <p className="text-[8px] font-black uppercase text-white/30 tracking-widest">Impacto</p>
                        </div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden mb-2">
                        <div className={cn("h-full transition-all duration-1000", vpKpiVisualState?.bgClass || 'bg-slate-700')} style={{ width: `${hasAnyKpiEligible ? finalKpiCompliance : 0}%` }}></div>
                    </div>
                </div>
            </div>

            {/* RESUMEN DE PONDERACIÓN (Solo visible para Admin) – BUG 4 FIX */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMINISTRADOR') && (
                <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-2xl">
                    <h3 className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-4 px-2">Resumen por Categoría</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                            { label: 'Comisiones', val: settings?.weight_comisiones || 50, icon: <Building2 />, color: 'text-slate-300', type: COMISION_TYPE },
                            { label: 'COMITÉS', val: settings?.weight_comites || 30, icon: <Users />, color: 'text-slate-300', type: 'COMITE' },
                            { label: 'CONSEJERÍAS', val: settings?.weight_consejerias || 20, icon: <Target />, color: 'text-slate-300', type: 'CONSEJERIA' }
                        ].filter(cat => instancesWithData.some(i => i.type === cat.type))
                            .map((cat, i) => (
                                <div key={i} className="bg-slate-950/50 p-6 rounded-2xl border border-white/5 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={cn("p-3 rounded-xl bg-white/5", cat.color)}>{cat.icon}</div>
                                        <div>
                                            <p className="text-[10px] font-black text-white uppercase tracking-widest">{cat.label}</p>
                                            <p className="text-[8px] text-white/30 uppercase font-bold tracking-widest">Peso Relativo</p>
                                        </div>
                                    </div>
                                    <span className="text-2xl font-black text-white">{cat.val}%</span>
                                </div>
                            ))}
                    </div>
                </div>
            )}



            <div className="space-y-4">
                <div className="flex justify-between items-end px-2">
                    <h3 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em]">Instancias Operativas</h3>
                    <p className="text-[8px] text-white/20 font-bold uppercase tracking-widest">{instancesWithData.length} Organismos</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {instancesWithPerformance.map(inst => {
                        const instPerformance = inst.weightedPerformance;

                        return (
                            <div key={inst.id}
                                onClick={() => onSelectInstance && onSelectInstance(inst.id)}
                                className={cn(
                                    "bg-slate-900/40 border border-slate-800/80 p-4 rounded-2xl transition-all group overflow-hidden relative cursor-pointer hover:scale-[1.02] active:scale-[0.98]",
                                    instPerformance === null ? 'hover:border-slate-700/80' : getSemaphoreHoverBorder(instPerformance)
                                )}>
                                <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:opacity-10 transition-opacity">
                                    {canonicalInstanceType(inst.type) === 'COMISION'? <Building2 size={40} /> : canonicalInstanceType(inst.type) === 'COMITE'? <Users size={40} /> : <Target size={40} />}
                                </div>

                                <div className="flex justify-between items-start mb-3 relative z-10">
                                    <div className="max-w-[70%]">
                                        <div className="flex items-center gap-2 mb-1 leading-none">
                                            <p className={cn(
                                                "text-[10px] font-black uppercase tracking-widest",
                                                canonicalInstanceType(inst.type) === 'COMISION'
                                                    ? 'text-emerald-500'
                                                    : canonicalInstanceType(inst.type) === 'COMITE'
                                                        ? 'text-blue-500'
                                                        : 'text-amber-500'
                                            )}>{INSTANCE_TYPE_LABELS[canonicalInstanceType(inst.type) === 'COMISION' ? COMISION_TYPE : canonicalInstanceType(inst.type)] || inst.type}</p>
                                            <span className="bg-slate-800 text-white/40 text-[8px] font-black px-1.5 py-0.5 rounded border border-white/5" title="Peso Relativo en VP">
                                                {getVpInstanceWeightLabel(instancesWithData, inst.type, settings)}
                                            </span>
                                        </div>
                                        <h4 className={cn(
                                            "text-base font-black text-white leading-tight uppercase tracking-tight transition-colors line-clamp-2 h-10",
                                            instPerformance === null ? 'group-hover:text-slate-300' : getSemaphoreTextHover(instPerformance)
                                        )}>{inst.name}</h4>
                                    </div>
                                    <div className="flex gap-1.5 relative z-10">
                                        <div className={`p-1.5 rounded-lg bg-slate-950/80 ${instPerformance === null ? 'text-slate-500' : getSemaphoreColor(instPerformance)} shadow-inner border border-white/5`}>
                                            <Activity size={12} />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 relative z-10">
                                    <div title={inst.planEligible ? `${inst.planProgress || 0}%. ${getComplianceVisualState(inst.planProgress || 0, settings).labelState}. Avance del Plan.` : 'Plan no aplicable.'} aria-label={inst.planEligible ? `Plan: ${inst.planProgress || 0} por ciento, ${getComplianceVisualState(inst.planProgress || 0, settings).labelState}.` : 'Plan: no aplicable.'} className="bg-slate-950/50 p-2 rounded-xl border border-white/5">
                                        <div className="flex justify-between items-center mb-0.5">
                                            <p className="text-[8px] font-black text-white/30 uppercase tracking-widest">Plan</p>
                                            <span className="text-[8px] font-bold text-slate-500">{inst.planProgressValue}/{inst.planGoal}</span>
                                        </div>
                                        <span className={cn("text-xs font-black", inst.planEligible ? getSemaphoreColor(inst.planProgress || 0) : 'text-slate-500')}>{inst.planEligible ? `${inst.planProgress || 0}%` : 'N/A'}</span>
                                    </div>
                                    <div title={inst.kpiEligible && inst.kpiCompliance !== null ? `${formatVpPercent(inst.kpiCompliance)}. ${getComplianceVisualState(inst.kpiCompliance, settings).labelState}. Cumplimiento de Indicadores.` : 'Indicadores no aplicables.'} aria-label={inst.kpiEligible && inst.kpiCompliance !== null ? `Indicadores: ${formatVpPercent(inst.kpiCompliance)}, ${getComplianceVisualState(inst.kpiCompliance, settings).labelState}.` : 'Indicadores: no aplicables.'} className="bg-slate-950/50 p-2 rounded-xl border border-white/5">
                                        <div className="flex justify-between items-center mb-0.5">
                                            <p className="text-[8px] font-black text-white/30 uppercase tracking-widest">Indicadores</p>
                                            <span className="text-[8px] font-bold text-slate-500">{(inst.kpiReal || 0).toFixed(1)}/{(inst.kpiMeta || 0).toFixed(1)}</span>
                                        </div>
                                        <span className={cn("text-xs font-black", inst.kpiEligible && inst.kpiCompliance !== null ? getSemaphoreColor(inst.kpiCompliance || 0) : 'text-slate-500')}>{inst.kpiEligible ? formatVpPercent(inst.kpiCompliance) : 'N/A'}</span>
                                    </div>
                                    <div title={instPerformance !== null ? `${instPerformance}%. ${getComplianceVisualState(instPerformance, settings).labelState}. Desempeño ponderado canónico.` : 'Desempeño no aplicable.'} aria-label={instPerformance !== null ? `Desempeño: ${instPerformance} por ciento, ${getComplianceVisualState(instPerformance, settings).labelState}.` : 'Desempeño: no aplicable.'} className="bg-slate-950/50 p-2 rounded-xl border border-white/5">
                                        <p className="mb-0.5 text-[8px] font-black uppercase tracking-widest text-white/30">Desempeño</p>
                                        <span className={cn("text-xs font-black", instPerformance !== null ? getSemaphoreColor(instPerformance) : 'text-slate-500')}>{instPerformance !== null ? `${instPerformance}%` : 'N/A'}</span>
                                    </div>
                                </div>

                                {/* REGLA #UX001: Visual indicator of performance */}
                                <div className="mt-4 h-1 w-full bg-slate-950 rounded-full overflow-hidden">
                                    <div 
                                        className={cn("h-full transition-all duration-1000", instPerformance === null ? 'bg-slate-700' : getSemaphoreBg(instPerformance))}
                                        style={{ width: `${instPerformance ?? 0}%` }}
                                    />
                                </div>

                                {/* BOTÓN APROBAR REPOSICIONADO PARA EVITAR RUIDO VISUAL */}
                                {canEditModule(user, 'Planeación') && inst.planStatus !== 'APROBADO' && inst.planStatus !== 'SIN PLAN' && (user.role === 'VICE_PRESIDENT' || isUserAdmin) && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleApprovePlan(inst.id); }}
                                        className="mt-4 w-full py-2 bg-emerald-600/20 hover:bg-emerald-600 text-emerald-500 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-xl border border-emerald-500/30 transition-all flex items-center justify-center gap-2 group/btn"
                                    >
                                        <Check size={14} className="group-hover/btn:scale-125 transition-transform" />
                                        APROBAR PLAN ESTRATÉGICO
                                    </button>
                                )}

                                <div className="mt-3 pt-3 border-t border-slate-800/50 flex justify-between items-center">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`w-1.5 h-1.5 rounded-full ${instPerformance === null ? 'bg-slate-700' : getSemaphoreBg(instPerformance)} animate-pulse shadow-[0_0_5px] shadow-current`}></div>
                                        <span className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Activo 2026</span>
                                    </div>
                                    <ChevronRight size={12} className={cn(
                                        "text-white/20 group-hover:translate-x-1 transition-all",
                                        instPerformance === null ? 'group-hover:text-slate-400' : getSemaphoreChevronHover(instPerformance)
                                    )} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Sección – Planes de Trabajo de la Vicepresidencia */}
            <div className="space-y-6">
                <div className="flex justify-between items-end px-4">
                    <h3 className="text-sm font-black text-white/40 uppercase tracking-[0.4em]">Planes de Trabajo de Instancias en {vp.name}</h3>
                    <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest">{plans.length} Planes Identificados</p>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-[3rem] overflow-hidden shadow-2xl">
                    {loadingPlans? (
                        <div className="p-20 text-center animate-pulse text-emerald-500 font-black uppercase tracking-[0.3em]">Cargando Planes de la Vicepresidencia...</div>
                    ) : plans.length > 0? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-950/50 text-white/30 text-[9px] uppercase font-black tracking-[0.2em] border-b border-white/5">
                                        <th className="px-8 py-6">Instancia</th>
                                        <th className="px-8 py-6 text-center">Estado</th>
                                        <th className="px-8 py-6 text-center">Meta Total</th>
                                        <th className="px-8 py-6 text-center">Avance</th>
                                        <th className="px-8 py-6 text-center">% Cumplimiento</th>
                                        <th className="px-8 py-6 text-right">Aval VP</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {plans.map((p: any) => {
                                        const inst = vpInstances.find(i => i.id === p.commission_id);
                                        return (
                                            <tr key={p.id} className="hover:bg-white/[0.02] transition-colors group">
                                                <td className="px-8 py-6">
                                                    <p className="text-white font-black uppercase text-base tracking-tight">{inst?.name || 'Desconocida'}</p>
                                                    <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest mt-1">{inst?.type || 'COMISION_TYPE'}</p>
                                                </td>
                                                <td className="px-8 py-6 text-center">
                                                    <span className={cn(
                                                        "px-3 py-1 rounded-full text-[8px] font-black border uppercase tracking-widest",
                                                        (p.status === 'APROBADO' || p.status === 'approved')? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.2)]" :
                                                            (p.status === 'REVISADO' || p.status === 'reviewed' || p.status === 'EN REVISION')? "bg-indigo-500/20 text-indigo-500 border-indigo-500/30" :
                                                                "bg-amber-500/20 text-amber-500 border-amber-500/30"
                                                    )}>
                                                        {STATUS_LABELS[p.status as keyof typeof STATUS_LABELS] || p.status}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-6 text-center font-mono text-xs text-white/60">{p.total_goal || 0}</td>
                                                <td className="px-8 py-6 text-center font-mono text-xs text-emerald-500/80 font-black">{p.total_progress || 0}</td>
                                                <td className="px-8 py-6 text-center">
                                                    <div className="flex flex-col items-center gap-1.5">
                                                        <span className={cn("text-xs font-black font-mono", getSemaphoreColor(p.global_percentage || 0))}>
                                                            {p.global_percentage || 0}%
                                                        </span>
                                                        <div className="w-20 h-1 bg-slate-950 rounded-full overflow-hidden">
                                                            <div className={cn("h-full transition-all duration-1000", getSemaphoreBg(p.global_percentage || 0))} style={{ width: `${p.global_percentage || 0}%` }}></div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    {p.status === 'APROBADO'? (
                                                        <div className="flex items-center justify-end gap-2 text-emerald-400">
                                                            <span className="text-[10px] font-black uppercase tracking-widest">Plan Aprobado</span>
                                                            <ShieldCheck size={16} />
                                                        </div>
                                                    ) : canEditModule(user, 'Planeación') && (user.role === 'VICE_PRESIDENT' || isUserAdmin)? (
                                                        <button
                                                            onClick={() => handleApprovePlan(inst?.id)}
                                                            className="px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600 text-emerald-500 hover:text-white rounded-xl border border-emerald-500/30 font-black text-[10px] uppercase tracking-widest transition-all shadow-lg flex items-center gap-2 ml-auto"
                                                        >
                                                            <Check size={14} />
                                                            Aprobar Plan
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center justify-end gap-2 text-white/10">
                                                            <span className="text-[10px] font-black uppercase tracking-widest italic">Pendiente de Aval</span>
                                                            <Shield size={16} />
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-20 text-center text-white/20 font-black uppercase tracking-[0.3em] italic">No hay planes registrados para esta Vicepresidencia en 2026</div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default TrackingModule;
